import React, {
	useState,
	useEffect,
	forwardRef,
	useImperativeHandle,
	useRef,
	useCallback,
	useMemo,
} from "react";
import {
	ChevronLeft,
	ChevronRight,
	Calendar,
	Edit2,
	Check,
	X,
	ChevronDown,
	ChevronUp,
	MessageSquare,
	Flag,
	Trash2,
} from "lucide-react";
import { apiClient } from "../../services/api";
import {
	CircularProgress,
	Box,
	Typography,
	Tooltip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Button,
	TextField,
	IconButton,
	Select as MuiSelect,
	MenuItem,
	Checkbox,
	ListItemText,
	FormControl,
	OutlinedInput,
	Chip,
} from "@mui/material";
import Select from "react-select";
import { getApiBaseUrl } from "../../utils/apiConfig";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import format from "date-fns/format";
import { enGB } from "date-fns/locale";
import VFFDataTableSidePanel from "../Sidebars/VFFDataTableSidePanel";
import { formatCommentForDisplay } from "../../utils/commentFormatter";
import { isUserAuthorizedForResponsibleFields } from "../../utils/authorizationUtils";
import { useUserAccess } from "../../hooks/useUserAccess";
import { useLanguage } from "../../hooks/useLanguage";
import { usePartTranslations } from "../../hooks/usePartTranslations";

// Add custom CSS for BTT status container scrollbar
const bttStatusStyles = `
  .btt-status-container::-webkit-scrollbar {
    width: 4px;
  }
  .btt-status-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  .btt-status-container::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
  .btt-status-container::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
	const style = document.createElement("style");
	style.textContent = bttStatusStyles;
	document.head.appendChild(style);
}

// Custom Tooltip Component for Comments
const CommentTooltip = ({ children, commentText, user, title }) => {
	if (!commentText) {
		return (
			<Tooltip title={title || "No comments yet"} arrow placement="top">
				{children}
			</Tooltip>
		);
	}

	const formattedComment = formatCommentForDisplay(commentText);

	const tooltipContent = (
		<Box sx={{ maxWidth: 400, p: 1 }}>
			<Typography
				variant="caption"
				sx={{
					color: "#fff",
					fontWeight: 600,
					display: "block",
					mb: 1,
					borderBottom: "1px solid rgba(255,255,255,0.3)",
					pb: 0.5,
				}}
			>
				{title || "Latest Comment"}
			</Typography>

			{formattedComment.isMultiLine &&
			formattedComment.parsedComments.length > 0 ? (
				<Box>
					{formattedComment.parsedComments.slice(0, 3).map((comment, index) => (
						<Box
							key={index}
							sx={{
								mb: 1,
								pb: 1,
								borderBottom:
									index <
									Math.min(2, formattedComment.parsedComments.length - 1)
										? "1px solid rgba(255,255,255,0.2)"
										: "none",
							}}
						>
							{comment.date && comment.user && (
								<Typography
									variant="caption"
									sx={{
										color: "rgba(255,255,255,0.8)",
										fontSize: "10px",
										fontWeight: 500,
										display: "block",
										mb: 0.3,
									}}
								>
									{comment.date} - {comment.user}:
								</Typography>
							)}
							<Typography
								variant="body2"
								sx={{
									color: "#fff",
									fontSize: "12px",
									lineHeight: 1.4,
									whiteSpace: "pre-wrap",
								}}
							>
								{comment.text.length > 100
									? `${comment.text.substring(0, 100)}...`
									: comment.text}
							</Typography>
						</Box>
					))}
					{formattedComment.parsedComments.length > 3 && (
						<Typography
							variant="caption"
							sx={{
								color: "rgba(255,255,255,0.7)",
								fontSize: "11px",
								fontStyle: "italic",
								display: "block",
								mt: 1,
							}}
						>
							+{formattedComment.parsedComments.length - 3} more comments...
						</Typography>
					)}
				</Box>
			) : (
				<Box>
					{user && (
						<Typography
							variant="caption"
							sx={{
								color: "rgba(255,255,255,0.8)",
								fontSize: "11px",
								display: "block",
								mb: 0.5,
							}}
						>
							By: {user}
						</Typography>
					)}
					<Typography
						variant="body2"
						sx={{
							color: "#fff",
							fontSize: "12px",
							lineHeight: 1.4,
							whiteSpace: "pre-wrap",
						}}
					>
						{formattedComment.cleanText.length > 150
							? `${formattedComment.cleanText.substring(0, 150)}...`
							: formattedComment.cleanText}
					</Typography>
				</Box>
			)}

			<Typography
				variant="caption"
				sx={{
					color: "rgba(255,255,255,0.6)",
					fontSize: "10px",
					fontStyle: "italic",
					display: "block",
					mt: 1,
					borderTop: "1px solid rgba(255,255,255,0.3)",
					pt: 0.5,
				}}
			>
				Click to view all comments
			</Typography>
		</Box>
	);

	return (
		<Tooltip
			title={tooltipContent}
			arrow
			placement="top"
			componentsProps={{
				tooltip: {
					sx: {
						bgcolor: "rgba(30, 40, 50, 0.95)",
						backdropFilter: "blur(10px)",
						border: "1px solid rgba(255,255,255,0.1)",
						borderRadius: "8px",
						boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
						maxWidth: 450,
						"& .MuiTooltip-arrow": {
							color: "rgba(30, 40, 50, 0.95)",
						},
					},
				},
			}}
		>
			{children}
		</Tooltip>
	);
};

// Shared utility function for formatting dates
const formatDate = (dateString) => {
	try {
		if (!dateString) return "";
		const date = new Date(dateString);
		return format(date, "MMM d, yyyy h:mm a");
	} catch (e) {
		return "";
	}
};

// Helper function to format date to YYYY-MM-DD format without timezone issues
const formatDateForBackend = (date) => {
	if (!date) return null;
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

// Helper function to format date to dd/mm/yyyy format
const formatDateToDDMMYYYY = (dateString) => {
	try {
		if (!dateString || dateString === "N/A" || dateString === "-")
			return dateString;
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return dateString; // Return original if not a valid date
		return format(date, "dd/MM/yyyy");
	} catch (e) {
		return dateString; // Return original string if formatting fails
	}
};

// Helper function to format MET dates to dd/MM/yyyy format
const formatMETDate = (dateString) => {
	try {
		if (
			!dateString ||
			dateString === "N/A" ||
			dateString === "-" ||
			dateString === 0
		)
			return dateString;

		let date;

		// Handle different input formats
		if (typeof dateString === "string") {
			// Check if it's in DD.MM.YYYY format (with dots)
			if (
				dateString.includes(".") &&
				dateString.match(/^\d{2}\.\d{2}\.\d{4}$/)
			) {
				const [day, month, year] = dateString.split(".");
				date = new Date(year, month - 1, day); // month is 0-indexed
			}
			// Check if it's in DD/MM/YYYY format (with slashes)
			else if (
				dateString.includes("/") &&
				dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)
			) {
				const [day, month, year] = dateString.split("/");
				date = new Date(year, month - 1, day); // month is 0-indexed
			}
			// Try to parse as a regular date string
			else {
				date = new Date(dateString);
			}
		} else {
			// If it's not a string, try to create a date directly
			date = new Date(dateString);
		}

		if (isNaN(date.getTime())) return dateString; // Return original if not a valid date
		return format(date, "dd/MM/yyyy");
	} catch (e) {
		return dateString; // Return original string if formatting fails
	}
};

// Helper function to format numbers to whole numbers (remove decimals)
const formatToWholeNumber = (value) => {
	if (value === null || value === undefined || value === "" || value === "-")
		return value;
	const number = parseFloat(value);
	if (isNaN(number)) return value;
	const result = Math.floor(number).toString();
	return result;
};

// Predefined Status BTT options
const STATUS_BTT_OPTIONS = [
	"AE",
	"BANF",
	"deleted",
	"Inhouse",
	"NR VSC",
	"Scania",
	"NR EV",
	"Lost/Missing Part",
	"In Stock",
	"E missing",
	"Exceptional reportable",
];

// Predefined Archive options (for display)
const ARCHIVE_OPTIONS = [
	"Unarchived",
	"To be deleted",
	"Duplicate Entry",
	"No Demand",
	"Hausteile",
	"On Hold",
	"PAT",
	"Others",
];

// Helper function to convert backend value to display value
const getArchiveDisplayValue = (value) => {
	return value === "N" || !value ? "Unarchived" : value;
};

// Helper function to convert display value to backend value
const getArchiveBackendValue = (displayValue) => {
	return displayValue === "Unarchived" ? "N" : displayValue;
};

// MultiSelect component for Status BTT
const StatusBTTMultiSelect = ({ value, onChange, disabled }) => {
	const [open, setOpen] = useState(false);

	// Parse the current value - it could be a string with comma-separated values or an array
	const currentValues = React.useMemo(() => {
		if (!value) return [];
		if (Array.isArray(value)) return value;
		if (typeof value === "string") {
			return value
				.split(",")
				.map((v) => v.trim())
				.filter((v) => v !== "");
		}
		return [];
	}, [value]);

	const handleChange = (event) => {
		const selectedValues = event.target.value;
		onChange(selectedValues);
	};

	const handleDelete = (valueToDelete) => {
		const newValues = currentValues.filter((v) => v !== valueToDelete);
		onChange(newValues);
	};

	return (
		<FormControl size="small" fullWidth disabled={disabled}>
			<MuiSelect
				multiple
				value={currentValues}
				onChange={handleChange}
				displayEmpty
				open={open}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				input={<OutlinedInput />}
				renderValue={(selected) => (
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 0.5,
							maxWidth: "100%",
						}}
					>
						{selected.length === 0 ? (
							<Typography
								variant="body2"
								sx={{ color: "#999", fontSize: "12px" }}
							>
								Select options
							</Typography>
						) : (
							selected.map((value) => (
								<Chip
									key={value}
									label={value}
									size="small"
									onDelete={disabled ? undefined : () => handleDelete(value)}
									sx={{
										height: "16px",
										fontSize: "10px",
										"& .MuiChip-label": {
											padding: "0 2px",
										},
										"& .MuiChip-deleteIcon": {
											fontSize: "12px",
											width: "12px",
											height: "12px",
										},
									}}
								/>
							))
						)}
					</Box>
				)}
				sx={{
					fontSize: "12px",
					height: "2px",
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: "#e5e7eb",
					},
					"& .MuiSelect-select": {
						padding: "2px 4px",
						fontSize: "12px",
						minHeight: "unset",
					},
				}}
				MenuProps={{
					PaperProps: {
						style: {
							maxHeight: 200,
							fontSize: "12px",
						},
					},
				}}
			>
				{STATUS_BTT_OPTIONS.map((option) => (
					<MenuItem key={option} value={option}>
						<Checkbox
							checked={currentValues.indexOf(option) > -1}
							size="small"
						/>
						<ListItemText
							primary={option}
							sx={{ "& .MuiTypography-root": { fontSize: "12px" } }}
						/>
					</MenuItem>
				))}
			</MuiSelect>
		</FormControl>
	);
};

// Toggle Switch Component - 3-state version
const ToggleSwitch = ({
	value,
	onChange,
	disabled,
	size = "small",
	"data-tour-target": dataTourTarget,
	...otherProps
}) => {
	const isSmall = size === "small";
	const switchWidth = isSmall ? 42 : 60; // Wider to accommodate 3 positions
	const switchHeight = isSmall ? 16 : 24;
	const circleSize = isSmall ? 12 : 20;
	const circleOffset = isSmall ? 2 : 2;

	// Map status values to positions and colors
	const getStatusInfo = (statusValue) => {
		switch (statusValue) {
			case null:
			case undefined:
			case 0:
				return {
					position: circleOffset,
					backgroundColor: "#e5e7eb",
					label: "None",
					textColor: "#6b7280",
				};
			case "in Arbeit":
			case 1:
				return {
					position: (switchWidth - circleSize) / 2,
					backgroundColor: "#f59e0b",
					label: "in Arbeit",
					textColor: "#d97706",
				};
			case "erledigt":
			case 2:
				return {
					position: switchWidth - circleSize - circleOffset,
					backgroundColor: "#10b981",
					label: "erledigt",
					textColor: "#059669",
				};
			default:
				return {
					position: circleOffset,
					backgroundColor: "#e5e7eb",
					label: "None",
					textColor: "#6b7280",
				};
		}
	};

	const statusInfo = getStatusInfo(value);
	return (
		<div
			onClick={!disabled ? onChange : undefined}
			style={{
				width: switchWidth,
				height: switchHeight,
				backgroundColor: statusInfo.backgroundColor,
				borderRadius: switchHeight / 2,
				position: "relative",
				cursor: disabled ? "not-allowed" : "pointer",
				transition: "all 0.2s ease",
				opacity: disabled ? 0.6 : 1,
				display: "inline-block",
				border: `1px solid ${statusInfo.textColor}`,
			}}
			title={`Status: ${statusInfo.label} (Click to cycle)`}
			data-tour-target={dataTourTarget}
			{...otherProps}
		>
			<div
				style={{
					width: circleSize,
					height: circleSize,
					backgroundColor: "white",
					borderRadius: "50%",
					position: "absolute",
					top: 1,
					left: statusInfo.position,
					transition: "all 0.2s ease",
					boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
					border: `1px solid ${statusInfo.textColor}`,
				}}
			/>
		</div>
	);
};

// Comment Modal Component for VFF
const CommentModal = ({
	open,
	onClose,
	kastKey,
	onAddComment,
	newComment,
	setNewComment,
	editingCommentId,
	setEditingCommentId,
	editText,
	setEditText,
	getUserDisplayName,
	getUserEmail,
	canWrite,
	preloadedCommentData = null,
}) => {
	const [comments, setComments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [backgroundLoading, setBackgroundLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [hasInitialized, setHasInitialized] = useState(false);
	// Local state for comment input to prevent lag from parent re-renders
	const [localComment, setLocalComment] = useState("");

	// Get current user name from prop function
	const currentUser = getUserDisplayName
		? getUserDisplayName()
		: "Current User";

	// Optimized onChange handler to prevent unnecessary re-renders
	const handleCommentChange = React.useCallback((e) => {
		setLocalComment(e.target.value);
	}, []);

	// Drag state
	const [position, setPosition] = useState({ x: null, y: null });
	const dragRef = useRef({
		dragging: false,
		startX: 0,
		startY: 0,
		originX: 0,
		originY: 0,
	});

	const handleDragStart = React.useCallback(
		(e) => {
			if (e.target.closest("button")) return;
			const paperEl =
				e.currentTarget
					.closest('[role="dialog"]')
					?.querySelector(".MuiDialog-paper") || e.currentTarget.parentElement;
			const rect = paperEl?.getBoundingClientRect() || { left: 0, top: 0 };
			dragRef.current = {
				dragging: true,
				startX: e.clientX,
				startY: e.clientY,
				originX: position.x !== null ? position.x : rect.left,
				originY: position.y !== null ? position.y : rect.top,
			};
			e.preventDefault();
		},
		[position],
	);

	useEffect(() => {
		const onMouseMove = (e) => {
			if (!dragRef.current.dragging) return;
			const dx = e.clientX - dragRef.current.startX;
			const dy = e.clientY - dragRef.current.startY;
			setPosition({
				x: dragRef.current.originX + dx,
				y: dragRef.current.originY + dy,
			});
		};
		const onMouseUp = () => {
			dragRef.current.dragging = false;
		};
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		return () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};
	}, []);
	useEffect(() => {
		if (open && kastKey && !hasInitialized) {
			setHasInitialized(true);
			// Initialize local comment with parent value
			setLocalComment(newComment || "");

			// If we have preloaded comment data, use it directly and skip API call for initial load
			if (preloadedCommentData?.latest_comment) {
				console.log("📄 Using preloaded comment data for kast_key:", kastKey);
				const preloadedComment = {
					id: "latest",
					text: preloadedCommentData.latest_comment,
					user: preloadedCommentData.latest_comment_user || "System",
					timestamp: preloadedCommentData.latest_comment_date || "",
					last_modified: preloadedCommentData.latest_comment_date || "",
				};
				setComments([preloadedComment]);
				setLoading(false);

				// Still fetch full comments in the background for completeness
				setBackgroundLoading(true);
				fetchComments(true); // true = background fetch
			} else {
				fetchComments();
			}
		} // Reset editing states and initialization flag when modal closes
		if (!open) {
			setHasInitialized(false);
			setLocalComment(""); // Clear local comment
			setPosition({ x: null, y: null });
			if (setEditingCommentId && setEditText) {
				setEditingCommentId(null);
				setEditText("");
			}
		}
	}, [open, kastKey]);

	const fetchComments = async (isBackgroundFetch = false) => {
		try {
			if (!isBackgroundFetch) {
				setLoading(true);
			}
			console.log("🔍 Fetching comments for kast_key:", kastKey);

			// URL encode the kast_key to handle special characters like # and spaces
			const encodedKastKey = encodeURIComponent(kastKey);
			console.log("🔗 Encoded kast_key:", encodedKastKey);

			const url = `/api/vff-comments?kast_key=${encodedKastKey}`;
			console.log("🌐 API URL:", url);

			const response = await apiClient.get(url);

			console.log(
				"✅ Comments fetched successfully:",
				response.data.comments?.length || 0,
				"comments",
			);
			setComments(response.data.comments || []);
		} catch (error) {
			console.error("❌ Error fetching comments:", error);
		} finally {
			if (isBackgroundFetch) {
				setBackgroundLoading(false);
			} else {
				setLoading(false);
			}
		}
	};
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!localComment.trim()) return;
		setSaving(true);

		// Create optimistic comment with temporary ID
		const optimisticComment = {
			id: `temp_${Date.now()}`,
			text: localComment,
			user: currentUser,
			timestamp: new Date().toISOString(),
			last_modified: new Date().toISOString(),
			kast_key: kastKey,
			is_optimistic: true, // Flag to identify optimistic updates
		};

		// Immediately add comment to local state (optimistic update)
		setComments((prevComments) => [optimisticComment, ...prevComments]);
		const commentTextToSave = localComment;
		setLocalComment(""); // Clear local input immediately
		if (setNewComment) setNewComment(""); // Also clear parent state

		try {
			console.log("📝 Adding comment for kast_key:", kastKey);

			const response = await apiClient.post("/api/vff-comments", {
				kast_key: kastKey,
				text: commentTextToSave,
				user: currentUser,
			});

			if (response.data && response.data.success) {
				console.log("✅ Comment added successfully");

				// Replace optimistic comment with real comment from server
				// Fetch fresh comments to get the real comment with proper ID and timestamp
				await fetchComments();

				// Update parent component data
				if (onAddComment) onAddComment();
			} else {
				throw new Error(response.data?.message || "Failed to save comment");
			}
		} catch (error) {
			console.error("❌ Error adding comment:", error);
			// Remove optimistic comment on error
			setComments((prevComments) =>
				prevComments.filter((comment) => comment.id !== optimisticComment.id),
			);

			// Restore comment text to input
			setLocalComment(commentTextToSave);
			if (setNewComment) setNewComment(commentTextToSave);

			// Show error message
			alert(
				`Error adding comment: ${error.response?.data?.detail || error.message || "Please try again."}`,
			);
		} finally {
			setSaving(false);
		}
	};

	// Handle Enter key submission for comments
	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};
	const handleUpdateComment = async (commentId) => {
		if (!editText.trim()) return;
		setSaving(true);

		try {
			console.log("📝 Updating comment:", commentId, "for kast_key:", kastKey);

			const response = await apiClient.put(`/api/vff-comments/${commentId}`, {
				kast_key: kastKey,
				text: editText,
				user: currentUser,
			});
			if (response.data) {
				console.log("✅ Comment updated successfully");
				if (setEditingCommentId) setEditingCommentId(null);
				if (setEditText) setEditText("");
				await fetchComments();
			} else {
				const errorText = await response.text();
				console.error("❌ Error updating comment:", response.status, errorText);
			}
		} catch (error) {
			console.error("❌ Error updating comment:", error);
		} finally {
			setSaving(false);
		}
	};
	const handleDeleteComment = async (commentId) => {
		if (
			!window.confirm(
				"Are you sure you want to delete this comment? This action cannot be undone.",
			)
		) {
			return;
		}

		setSaving(true);

		try {
			console.log(
				"🗑️ Starting delete process for comment:",
				commentId,
				"kast_key:",
				kastKey,
			);
			console.log("🌐 Current environment:", window.location.hostname);
			console.log(
				"🔗 API Base URL:",
				process.env.NODE_ENV === "development"
					? "http://localhost:8000"
					: "Deployed API",
			);

			let response;
			if (commentId === "latest") {
				const deleteUrl = `/api/vff-comments/latest`;
				const deleteData = {
					kast_key: kastKey,
					user: currentUser || "System",
				};
				console.log("🗑️ Deleting latest VFF comment using /latest endpoint");
				console.log("📝 DELETE URL:", deleteUrl);
				console.log("📦 Request payload:", JSON.stringify(deleteData, null, 2));

				response = await apiClient.delete(deleteUrl, {
					data: deleteData,
				});
			} else {
				const deleteUrl = `/api/vff-comments/${commentId}`;
				const deleteData = {
					kast_key: kastKey,
					user: currentUser || "System",
				};
				console.log(
					"🗑️ Deleting specific VFF comment using /{commentId} endpoint",
				);
				console.log("📝 DELETE URL:", deleteUrl);
				console.log("📦 Request payload:", JSON.stringify(deleteData, null, 2));

				response = await apiClient.delete(deleteUrl, {
					data: deleteData,
				});
			}
			console.log("📨 Response received:", {
				status: response.status,
				statusText: response.statusText,
				headers: response.headers,
				data: response.data,
			});

			if (response.data && response.data.success) {
				console.log("✅ Comment deleted successfully:", response.data.message);
				await fetchComments();
				if (onAddComment) onAddComment(); // Refresh parent component if needed
			} else {
				console.error("❌ Unexpected response format:", response);
				alert(
					"Error deleting comment: Unexpected response format. Please try again.",
				);
			}
		} catch (error) {
			console.error("❌ Error deleting comment - Full error details:");
			console.error("Error message:", error.message);
			console.error("Error code:", error.code);
			console.error("Error config:", error.config);
			console.error("Error response:", error.response);

			if (error.response) {
				console.error("Response status:", error.response.status);
				console.error("Response data:", error.response.data);
				console.error("Response headers:", error.response.headers);
				alert(
					`Error deleting comment: ${error.response.status} - ${error.response.data?.detail || error.message}`,
				);
			} else if (error.request) {
				console.error("No response received:", error.request);
				alert(
					"Error deleting comment: No response from server. Please check your connection.",
				);
			} else {
				console.error("Request setup error:", error.message);
				alert(`Error deleting comment: ${error.message}`);
			}
		} finally {
			setSaving(false);
		}
	};
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: "0px",
					boxShadow: "0 10px 40px rgba(30, 40, 50, 0.15)",
					overflow: "hidden",
					height: "70vh",
					maxHeight: "700px",
					...(position.x !== null
						? {
								position: "fixed",
								left: position.x,
								top: position.y,
								margin: 0,
								transform: "none",
							}
						: {}),
				},
			}}
		>
			{/* Header */}
			<Box
				onMouseDown={handleDragStart}
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					px: 3,
					py: 2,
					display: "flex",
					borderRadius: "0px",
					alignItems: "center",
					justifyContent: "space-between",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
					cursor: "grab",
					userSelect: "none",
					"&:active": { cursor: "grabbing" },
				}}
			>
				<Box display="flex" alignItems="center" gap={2}>
					<MessageSquare size={20} />
					<Typography
						variant="h6"
						sx={{
							fontWeight: 600,
							fontSize: "16px",
							borderRadius: "0px",
							letterSpacing: "0.5px",
							fontFamily: "MAN Europe_Bold",
						}}
					>
						Comments
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "rgba(255, 255, 255, 0.7)",
							fontSize: "14px",
						}}
					>
						Kast Key: {kastKey}
					</Typography>
				</Box>
				<button
					onClick={onClose}
					style={{
						background: "none",
						border: "none",
						color: "white",
						cursor: "pointer",
						padding: "8px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "18px",
						transition: "all 0.2s ease",
					}}
					onMouseOver={(e) => {
						e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
						e.target.style.transform = "scale(1.05)";
					}}
					onMouseOut={(e) => {
						e.target.style.backgroundColor = "transparent";
						e.target.style.transform = "scale(1)";
					}}
				>
					<X size={18} />
				</button>
			</Box>

			{/* Content */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100% - 70px)",
				}}
			>
				{/* Comments List */}
				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						backgroundColor: "#f8f9fa",
						"&::-webkit-scrollbar": {
							width: "6px",
						},
						"&::-webkit-scrollbar-track": {
							background: "#f1f1f1",
						},
						"&::-webkit-scrollbar-thumb": {
							background: "#c1c1c1",
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb:hover": {
							background: "#a8a8a8",
						},
					}}
				>
					{" "}
					{loading ? (
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							height="100%"
							py={4}
						>
							<CircularProgress sx={{ color: "#cc0000" }} size={32} />
							<Typography variant="body2" sx={{ ml: 2, color: "#666" }}>
								Loading comments...
							</Typography>
						</Box>
					) : comments.length > 0 ? (
						<Box sx={{ p: 2 }}>
							{comments.map((item, index) => (
								<Box
									key={item.id || `comment-${index}`}
									sx={{
										backgroundColor: item.is_optimistic ? "#f8f9ff" : "white",
										p: 2.5,
										mb: 1.5,
										border: item.is_optimistic
											? "1px solid #c7d2fe"
											: "1px solid #e1e5e9",
										boxShadow: item.is_optimistic
											? "0 1px 3px rgba(99, 102, 241, 0.1)"
											: "0 1px 3px rgba(0, 0, 0, 0.05)",
										transition: "all 0.2s ease",
										opacity: item.is_optimistic ? 0.8 : 1,
										position: "relative",
										"&:hover": {
											boxShadow: item.is_optimistic
												? "0 4px 12px rgba(99, 102, 241, 0.15)"
												: "0 4px 12px rgba(0, 0, 0, 0.1)",
											transform: "translateY(-1px)",
										},
										"&:last-child": {
											mb: 0,
										},
									}}
								>
									{editingCommentId === item.id ? (
										<Box>
											<TextField
												fullWidth
												multiline
												rows={2}
												value={editText}
												onChange={(e) => setEditText(e.target.value)}
												variant="outlined"
												size="small"
												autoFocus
												placeholder="Edit your comment..."
												sx={{
													"& .MuiOutlinedInput-root": {
														fontSize: "14px",
														"&:hover fieldset": { borderColor: "#1e2832" },
														"&.Mui-focused fieldset": {
															borderColor: "#1e2832",
														},
													},
												}}
											/>
											<Box
												display="flex"
												justifyContent="flex-end"
												gap={1}
												mt={1.5}
											>
												<Button
													size="small"
													onClick={() => {
														setEditingCommentId(null);
														setEditText("");
													}}
													disabled={saving}
													sx={{
														color: "#666",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
													}}
												>
													Cancel
												</Button>
												<Button
													variant="contained"
													size="small"
													onClick={() => handleUpdateComment(item.id)}
													disabled={saving || !editText.trim()}
													sx={{
														backgroundColor: "#cc0000",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
														"&:hover": { backgroundColor: "#b30000" },
													}}
												>
													{saving ? "Saving..." : "Save"}
												</Button>
											</Box>
										</Box>
									) : (
										<Box>
											<Box
												display="flex"
												justifyContent="space-between"
												alignItems="center"
												mb={1}
											>
												<Box display="flex" alignItems="center" gap={1}>
													<Box
														sx={{
															width: "32px",
															height: "32px",
															borderRadius: "50%",
															backgroundColor: "#cc0000",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															color: "white",
															fontSize: "12px",
															fontWeight: "bold",
														}}
													>
														{(item.user || "S").charAt(0).toUpperCase()}
													</Box>
													<Typography
														variant="subtitle2"
														sx={{
															fontWeight: 600,
															color: "#1e2832",
															fontSize: "13px",
														}}
													>
														{item.user || "System"}
													</Typography>
												</Box>
												<Box display="flex" alignItems="center" gap={1}>
													<Typography
														variant="caption"
														sx={{
															color: "#999",
															fontSize: "11px",
															fontStyle: "MAN Europe_Bold",
														}}
													>
														{formatDate(item.timestamp)}
													</Typography>
													{!item.is_legacy && canWrite && (
														<>
															<button
																onClick={() => {
																	setEditingCommentId(item.id);
																	setEditText(item.text);
																}}
																style={{
																	background: "none",
																	border: "none",
																	color: "#999",
																	cursor: "pointer",
																	padding: "4px",
																	borderRadius: "4px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	transition: "all 0.2s ease",
																}}
																onMouseOver={(e) => {
																	e.target.style.backgroundColor =
																		"rgba(204, 0, 0, 0.08)";
																	e.target.style.color = "#cc0000";
																}}
																onMouseOut={(e) => {
																	e.target.style.backgroundColor =
																		"transparent";
																	e.target.style.color = "#999";
																}}
																title="Edit comment"
															>
																<Edit2 size={14} />
															</button>
															<button
																onClick={() => handleDeleteComment(item.id)}
																disabled={saving}
																style={{
																	background: "none",
																	border: "none",
																	color: "#999",
																	cursor: saving ? "not-allowed" : "pointer",
																	padding: "4px",
																	borderRadius: "4px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	transition: "all 0.2s ease",
																	marginLeft: "4px",
																	opacity: saving ? 0.5 : 1,
																}}
																onMouseOver={(e) => {
																	if (!saving) {
																		e.target.style.backgroundColor =
																			"rgba(220, 38, 38, 0.08)";
																		e.target.style.color = "#dc2626";
																	}
																}}
																onMouseOut={(e) => {
																	if (!saving) {
																		e.target.style.backgroundColor =
																			"transparent";
																		e.target.style.color = "#999";
																	}
																}}
																title="Delete comment"
															>
																<Trash2 size={14} />
															</button>
														</>
													)}
												</Box>
											</Box>
											{(() => {
												const formattedComment = formatCommentForDisplay(
													item.text,
												);

												if (
													formattedComment.isMultiLine &&
													formattedComment.parsedComments.length > 0
												) {
													return (
														<Box sx={{ ml: 5 }}>
															{formattedComment.parsedComments.map(
																(comment, index) => (
																	<Box
																		key={index}
																		sx={{
																			mb: 1.5,
																			pb: 1.5,
																			borderBottom:
																				index <
																				formattedComment.parsedComments.length -
																					1
																					? "1px solid #f0f0f0"
																					: "none",
																		}}
																	>
																		{comment.date && comment.user && (
																			<Typography
																				variant="caption"
																				sx={{
																					color: "#666",
																					fontSize: "11px",
																					fontWeight: 600,
																					display: "block",
																					mb: 0.5,
																				}}
																			>
																				{comment.date} - {comment.user}:
																			</Typography>
																		)}
																		<Typography
																			variant="body2"
																			sx={{
																				color: "#333",
																				lineHeight: 1.6,
																				fontSize: "13px",
																				whiteSpace: "pre-wrap",
																			}}
																		>
																			{comment.text}
																		</Typography>
																	</Box>
																),
															)}
														</Box>
													);
												} else {
													return (
														<Typography
															variant="body2"
															sx={{
																whiteSpace: "pre-wrap",
																color: "#333",
																lineHeight: 1.6,
																fontSize: "13px",
																ml: 5, // Indent text to align with avatar
															}}
														>
															{formattedComment.cleanText}
														</Typography>
													);
												}
											})()}
										</Box>
									)}
								</Box>
							))}

							{/* Background Loading Indicator */}
							{backgroundLoading && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										py: 2,
										mt: 1,
									}}
								>
									<CircularProgress
										size={20}
										sx={{ color: "#cc0000", mr: 1 }}
									/>
									<Typography
										variant="caption"
										sx={{
											color: "#666",
											fontSize: "12px",
										}}
									>
										Loading more comments...
									</Typography>
								</Box>
							)}
						</Box>
					) : (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							height="100%"
							sx={{ color: "#666", py: 4 }}
						>
							<MessageSquare size={40} color="#d1d5db" />
							<Typography
								variant="body1"
								sx={{
									mt: 2,
									fontWeight: 500,
									color: "#4b5563",
								}}
							>
								No comments yet
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: "#9ca3af",
									fontSize: "13px",
									textAlign: "center",
									mt: 0.5,
								}}
							>
								Start the conversation by adding the first comment
							</Typography>
						</Box>
					)}
				</Box>{" "}
				{/* Add Comment Form */}
				{canWrite ? (
					<Box
						component="form"
						onSubmit={handleSubmit}
						sx={{
							p: 2.5,
							borderTop: "1px solid #e1e5e9",
							backgroundColor: "white",
							boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.05)",
						}}
					>
						{" "}
						<TextField
							fullWidth
							variant="outlined"
							placeholder="Write a comment..."
							value={localComment}
							onChange={handleCommentChange}
							onKeyDown={handleKeyDown}
							multiline
							rows={2}
							disabled={saving}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: "0px",
									fontSize: "14px",
									backgroundColor: "#fafafa",
									"&:hover": {
										backgroundColor: "#f5f5f5",
									},
									"&:hover fieldset": { borderColor: "#1e2832" },
									"&.Mui-focused": {
										backgroundColor: "white",
									},
									"&.Mui-focused fieldset": { borderColor: "#1e2832" },
								},
							}}
						/>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								mt: 2,
							}}
						>
							<Typography
								variant="caption"
								sx={{
									color: "#9ca3af",
									fontSize: "11px",
									fontStyle: "MAN EUROPE_Bold",
								}}
							>
								Press Enter to Submit, Shift + Enter for a new line
							</Typography>
							<Box sx={{ display: "flex", gap: 1.5 }}>
								<Button
									variant="outlined"
									onClick={onClose}
									disabled={saving}
									sx={{
										textTransform: "none",
										color: "#6b7280",
										borderColor: "#d1d5db",
										fontSize: "13px",
										minWidth: "70px",
										fontFamily: "MAN Europe_Bold",
										borderRadius: "0px",
										"&:hover": {
											borderColor: "#1e2832",
											backgroundColor: "rgba(30, 40, 50, 0.04)",
										},
									}}
								>
									Close
								</Button>
								<Button
									type="submit"
									variant="contained"
									disabled={!localComment.trim() || saving}
									sx={{
										textTransform: "none",
										backgroundColor: "#cc0000",
										fontSize: "13px",
										minWidth: "80px",
										fontWeight: 600,
										fontFamily: "MAN Europe_Bold",
										borderRadius: "0px",
										"&:hover": { backgroundColor: "#b30000" },
										"&.Mui-disabled": {
											backgroundColor: "#e5e7eb",
											color: "#9ca3af",
										},
									}}
								>
									{saving ? "Adding..." : "Add Comment"}
								</Button>
							</Box>
						</Box>
					</Box>
				) : (
					<Box
						sx={{
							p: 2.5,
							borderTop: "1px solid #e1e5e9",
							backgroundColor: "#f9fafb",
							textAlign: "center",
						}}
					>
						<Typography
							variant="body2"
							sx={{
								color: "#6b7280",
								fontSize: "13px",
								fontStyle: "italic",
							}}
						>
							🔒 You have read-only access. Write permission is required to add
							comments.
						</Typography>
						<Button
							variant="outlined"
							onClick={onClose}
							sx={{
								mt: 2,
								textTransform: "none",
								color: "#6b7280",
								borderColor: "#d1d5db",
								fontSize: "13px",
								minWidth: "70px",
								"&:hover": {
									borderColor: "#1e2832",
									backgroundColor: "rgba(30, 40, 50, 0.04)",
								},
							}}
						>
							Close
						</Button>
					</Box>
				)}
			</Box>
		</Dialog>
	);
};

// Vehicle Comment Modal Component
const VehicleCommentModal = ({
	open,
	onClose,
	kastKey,
	onAddComment,
	newComment,
	setNewComment,
	editingCommentId,
	setEditingCommentId,
	editText,
	setEditText,
	getUserDisplayName,
	preloadedCommentData = null,
}) => {
	const [comments, setComments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [backgroundLoading, setBackgroundLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [hasInitialized, setHasInitialized] = useState(false);
	const [localVehicleComment, setLocalVehicleComment] = useState("");

	// Optimized onChange handler to prevent unnecessary re-renders
	const handleVehicleCommentChange = React.useCallback((e) => {
		setLocalVehicleComment(e.target.value);
	}, []);

	// Get current user name from prop function
	const currentUser = getUserDisplayName
		? getUserDisplayName()
		: "Current User";

	// Drag state
	const [position, setPosition] = useState({ x: null, y: null });
	const dragRef = useRef({
		dragging: false,
		startX: 0,
		startY: 0,
		originX: 0,
		originY: 0,
	});

	const handleDragStart = React.useCallback(
		(e) => {
			if (e.target.closest("button")) return;
			const paperEl =
				e.currentTarget
					.closest('[role="dialog"]')
					?.querySelector(".MuiDialog-paper") || e.currentTarget.parentElement;
			const rect = paperEl?.getBoundingClientRect() || { left: 0, top: 0 };
			dragRef.current = {
				dragging: true,
				startX: e.clientX,
				startY: e.clientY,
				originX: position.x !== null ? position.x : rect.left,
				originY: position.y !== null ? position.y : rect.top,
			};
			e.preventDefault();
		},
		[position],
	);

	useEffect(() => {
		const onMouseMove = (e) => {
			if (!dragRef.current.dragging) return;
			const dx = e.clientX - dragRef.current.startX;
			const dy = e.clientY - dragRef.current.startY;
			setPosition({
				x: dragRef.current.originX + dx,
				y: dragRef.current.originY + dy,
			});
		};
		const onMouseUp = () => {
			dragRef.current.dragging = false;
		};
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		return () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
		};
	}, []);
	useEffect(() => {
		if (open && kastKey && !hasInitialized) {
			setHasInitialized(true);
			// Initialize local comment with parent value
			setLocalVehicleComment(newComment || "");

			// If we have preloaded comment data, use it directly and skip API call for initial load
			if (preloadedCommentData?.latest_comment) {
				console.log(
					"📄 Using preloaded vehicle comment data for kast_key:",
					kastKey,
				);
				const preloadedComment = {
					id: "latest",
					text: preloadedCommentData.latest_comment,
					user: preloadedCommentData.latest_comment_user || "System",
					timestamp: preloadedCommentData.latest_comment_date || "",
					last_modified: preloadedCommentData.latest_comment_date || "",
				};
				setComments([preloadedComment]);
				setLoading(false);

				// Still fetch full comments in the background for completeness
				setBackgroundLoading(true);
				fetchComments(true); // true = background fetch
			} else {
				fetchComments();
			}
		} // Reset editing states and initialization flag when modal closes
		if (!open) {
			setHasInitialized(false);
			setLocalVehicleComment(""); // Clear local comment
			setPosition({ x: null, y: null });
			if (setEditingCommentId && setEditText) {
				setEditingCommentId(null);
				setEditText("");
			}
		}
	}, [open, kastKey]);

	const fetchComments = async (isBackgroundFetch = false) => {
		try {
			if (!isBackgroundFetch) {
				setLoading(true);
			}
			console.log("🔍 Fetching vehicle comments for kast_key:", kastKey);

			// URL encode the kast_key to handle special characters like # and spaces
			const encodedKastKey = encodeURIComponent(kastKey);
			console.log("🔗 Encoded kast_key:", encodedKastKey);

			const url = `/api/vff-vehicle-comments?kast_key=${encodedKastKey}`;
			console.log("🌐 API URL:", url);

			const response = await apiClient.get(url);

			console.log(
				"✅ Vehicle Comments fetched successfully:",
				response.data.comments?.length || 0,
				"comments",
			);
			setComments(response.data.comments || []);
		} catch (error) {
			console.error("❌ Error fetching vehicle comments:", error);
		} finally {
			if (isBackgroundFetch) {
				setBackgroundLoading(false);
			} else {
				setLoading(false);
			}
		}
	};
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!localVehicleComment.trim()) return;
		setSaving(true); // Create optimistic comment with temporary ID
		const optimisticComment = {
			id: `temp_vehicle_${Date.now()}`,
			text: localVehicleComment,
			user: currentUser,
			timestamp: new Date().toISOString(),
			last_modified: new Date().toISOString(),
			kast_key: kastKey,
			is_optimistic: true, // Flag to identify optimistic updates
		};

		// Immediately add comment to local state (optimistic update)
		setComments((prevComments) => [optimisticComment, ...prevComments]);
		const commentTextToSave = localVehicleComment;
		setLocalVehicleComment(""); // Clear local input immediately
		if (setNewComment) setNewComment(""); // Also clear parent state

		try {
			console.log("📝 Adding vehicle comment for kast_key:", kastKey);

			const response = await apiClient.post("/api/vff-vehicle-comments", {
				kast_key: kastKey,
				text: commentTextToSave,
				user: currentUser,
			});

			if (response.data && response.data.success) {
				console.log("✅ Vehicle Comment added successfully");

				// Replace optimistic comment with real comment from server
				// Fetch fresh comments to get the real comment with proper ID and timestamp
				await fetchComments();

				// Update parent component data
				if (onAddComment) onAddComment();
			} else {
				throw new Error(
					response.data?.message || "Failed to save vehicle comment",
				);
			}
		} catch (error) {
			console.error("❌ Error adding vehicle comment:", error);
			// Remove optimistic comment on error
			setComments((prevComments) =>
				prevComments.filter((comment) => comment.id !== optimisticComment.id),
			);

			// Restore comment text to input
			setLocalVehicleComment(commentTextToSave);
			if (setNewComment) setNewComment(commentTextToSave);

			// Show error message
			alert(
				`Error adding vehicle comment: ${error.response?.data?.detail || error.message || "Please try again."}`,
			);
		} finally {
			setSaving(false);
		}
	};

	// Handle Enter key submission for vehicle comments
	const handleKeyDown = (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};
	const handleUpdateComment = async (commentId) => {
		if (!editText.trim()) return;
		setSaving(true);

		try {
			console.log(
				"📝 Updating vehicle comment:",
				commentId,
				"for kast_key:",
				kastKey,
			);

			const response = await apiClient.put(
				`/api/vff-vehicle-comments/${commentId}`,
				{
					kast_key: kastKey,
					text: editText,
					user: currentUser,
				},
			);

			if (response.data) {
				console.log("✅ Vehicle Comment updated successfully");
				setEditingCommentId(null);
				setEditText("");
				await fetchComments();
			} else {
				const errorText = await response.text();
				console.error(
					"❌ Error updating vehicle comment:",
					response.status,
					errorText,
				);
			}
		} catch (error) {
			console.error("❌ Error updating vehicle comment:", error);
		} finally {
			setSaving(false);
		}
	};
	const handleDeleteComment = async (commentId) => {
		if (
			!window.confirm(
				"Are you sure you want to delete this comment? This action cannot be undone.",
			)
		) {
			return;
		}

		setSaving(true);
		try {
			console.log(
				"🗑️ Starting vehicle comment delete process:",
				commentId,
				"kast_key:",
				kastKey,
			);
			console.log("🌐 Current environment:", window.location.hostname);

			let response;
			if (commentId === "latest") {
				const deleteUrl = `/api/vff-vehicle-comments/latest`;
				const deleteData = {
					kast_key: kastKey,
					user: currentUser || "System",
				};
				console.log(
					"🗑️ Deleting latest VFF vehicle comment using /latest endpoint",
				);
				console.log("📝 DELETE URL:", deleteUrl);
				console.log("📦 Request payload:", JSON.stringify(deleteData, null, 2));

				response = await apiClient.delete(deleteUrl, {
					data: deleteData,
				});
			} else {
				const deleteUrl = `/api/vff-vehicle-comments/${commentId}`;
				const deleteData = {
					kast_key: kastKey,
					user: currentUser || "System",
				};
				console.log(
					"🗑️ Deleting specific VFF vehicle comment using /{commentId} endpoint",
				);
				console.log("📝 DELETE URL:", deleteUrl);
				console.log("📦 Request payload:", JSON.stringify(deleteData, null, 2));

				response = await apiClient.delete(deleteUrl, {
					data: deleteData,
				});
			}
			console.log("📨 Vehicle comment response received:", {
				status: response.status,
				statusText: response.statusText,
				data: response.data,
			});

			if (response.data && response.data.success) {
				console.log(
					"✅ Vehicle Comment deleted successfully:",
					response.data.message,
				);
				await fetchComments();
				if (onAddComment) onAddComment(); // Refresh parent component if needed
			} else {
				console.error(
					"❌ Unexpected vehicle comment response format:",
					response,
				);
				alert(
					"Error deleting vehicle comment: Unexpected response format. Please try again.",
				);
			}
		} catch (error) {
			console.error("❌ Error deleting vehicle comment - Full error details:");
			console.error("Error message:", error.message);
			console.error("Error code:", error.code);
			console.error("Error response:", error.response);

			if (error.response) {
				console.error("Response status:", error.response.status);
				console.error("Response data:", error.response.data);
				alert(
					`Error deleting vehicle comment: ${error.response.status} - ${error.response.data?.detail || error.message}`,
				);
			} else if (error.request) {
				console.error(
					"No response received for vehicle comment delete:",
					error.request,
				);
				alert(
					"Error deleting vehicle comment: No response from server. Please check your connection.",
				);
			} else {
				alert(`Error deleting vehicle comment: ${error.message}`);
			}
		} finally {
			setSaving(false);
		}
	};
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: "12px",
					boxShadow: "0 10px 40px rgba(30, 40, 50, 0.15)",
					overflow: "hidden",
					height: "70vh",
					maxHeight: "700px",
					...(position.x !== null
						? {
								position: "fixed",
								left: position.x,
								top: position.y,
								margin: 0,
								transform: "none",
							}
						: {}),
				},
			}}
		>
			{/* Header */}
			<Box
				onMouseDown={handleDragStart}
				sx={{
					backgroundColor: "#1e2832",
					color: "white",
					px: 3,
					py: 2,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
					cursor: "grab",
					userSelect: "none",
					"&:active": { cursor: "grabbing" },
				}}
			>
				<Box display="flex" alignItems="center" gap={2}>
					<MessageSquare size={20} />
					<Typography
						variant="h6"
						sx={{
							fontWeight: 600,
							fontSize: "16px",
							letterSpacing: "0.5px",
						}}
					>
						Vehicle Comments
					</Typography>
					<Typography
						variant="body2"
						sx={{
							color: "rgba(255, 255, 255, 0.7)",
							fontSize: "14px",
						}}
					>
						Kast Key: {kastKey}
					</Typography>
				</Box>
				<button
					onClick={onClose}
					style={{
						background: "none",
						border: "none",
						color: "white",
						cursor: "pointer",
						padding: "8px",
						borderRadius: "50%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						fontSize: "18px",
						transition: "all 0.2s ease",
					}}
					onMouseOver={(e) => {
						e.target.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
						e.target.style.transform = "scale(1.05)";
					}}
					onMouseOut={(e) => {
						e.target.style.backgroundColor = "transparent";
						e.target.style.transform = "scale(1)";
					}}
				>
					<X size={18} />
				</button>
			</Box>

			{/* Content */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100% - 70px)",
				}}
			>
				{/* Comments List */}
				<Box
					sx={{
						flex: 1,
						overflowY: "auto",
						backgroundColor: "#f8f9fa",
						"&::-webkit-scrollbar": {
							width: "6px",
						},
						"&::-webkit-scrollbar-track": {
							background: "#f1f1f1",
						},
						"&::-webkit-scrollbar-thumb": {
							background: "#c1c1c1",
							borderRadius: "3px",
						},
						"&::-webkit-scrollbar-thumb:hover": {
							background: "#a8a8a8",
						},
					}}
				>
					{loading ? (
						<Box
							display="flex"
							justifyContent="center"
							alignItems="center"
							height="100%"
							py={4}
						>
							<CircularProgress sx={{ color: "#cc0000" }} size={32} />
							<Typography variant="body2" sx={{ ml: 2, color: "#666" }}>
								Loading vehicle comments...
							</Typography>
						</Box>
					) : comments.length > 0 ? (
						<Box sx={{ p: 2 }}>
							{comments.map((item, index) => (
								<Box
									key={item.id || `comment-${index}`}
									sx={{
										backgroundColor: item.is_optimistic ? "#f8f9ff" : "white",
										borderRadius: "10px",
										p: 2.5,
										mb: 1.5,
										border: item.is_optimistic
											? "1px solid #c7d2fe"
											: "1px solid #e1e5e9",
										boxShadow: item.is_optimistic
											? "0 1px 3px rgba(99, 102, 241, 0.1)"
											: "0 1px 3px rgba(0, 0, 0, 0.05)",
										transition: "all 0.2s ease",
										opacity: item.is_optimistic ? 0.8 : 1,
										position: "relative",
										"&:hover": {
											boxShadow: item.is_optimistic
												? "0 4px 12px rgba(99, 102, 241, 0.15)"
												: "0 4px 12px rgba(0, 0, 0, 0.1)",
											transform: "translateY(-1px)",
										},
										"&:last-child": {
											mb: 0,
										},
									}}
								>
									{editingCommentId === item.id ? (
										<Box>
											{" "}
											<TextField
												fullWidth
												multiline
												rows={2}
												value={editText}
												onChange={(e) => setEditText(e.target.value)}
												variant="outlined"
												size="small"
												autoFocus
												placeholder="Edit your comment..."
												sx={{
													"& .MuiOutlinedInput-root": {
														borderRadius: "8px",
														fontSize: "14px",
														"&:hover fieldset": { borderColor: "#1e2832" },
														"&.Mui-focused fieldset": {
															borderColor: "#1e2832",
														},
													},
												}}
											/>
											<Box
												display="flex"
												justifyContent="flex-end"
												gap={1}
												mt={1.5}
											>
												<Button
													size="small"
													onClick={() => {
														setEditingCommentId(null);
														setEditText("");
													}}
													disabled={saving}
													sx={{
														color: "#666",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
													}}
												>
													Cancel
												</Button>
												<Button
													variant="contained"
													size="small"
													onClick={() => handleUpdateComment(item.id)}
													disabled={saving || !editText.trim()}
													sx={{
														backgroundColor: "#cc0000",
														textTransform: "none",
														fontSize: "12px",
														minWidth: "60px",
														"&:hover": { backgroundColor: "#b30000" },
													}}
												>
													{saving ? "Saving..." : "Save"}
												</Button>
											</Box>
										</Box>
									) : (
										<Box>
											<Box
												display="flex"
												justifyContent="space-between"
												alignItems="center"
												mb={1}
											>
												<Box display="flex" alignItems="center" gap={1}>
													<Box
														sx={{
															width: "32px",
															height: "32px",
															borderRadius: "50%",
															backgroundColor: "#cc0000",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															color: "white",
															fontSize: "12px",
															fontWeight: "bold",
														}}
													>
														{(item.user || "S").charAt(0).toUpperCase()}
													</Box>
													<Typography
														variant="subtitle2"
														sx={{
															fontWeight: 600,
															color: "#1e2832",
															fontSize: "13px",
														}}
													>
														{item.user || "System"}
													</Typography>
												</Box>
												<Box display="flex" alignItems="center" gap={1}>
													<Typography
														variant="caption"
														sx={{
															color: "#999",
															fontSize: "11px",
															fontStyle: "MAN Europe_Bold",
														}}
													>
														{formatDate(item.timestamp)}
													</Typography>
													{!item.is_legacy && (
														<>
															<button
																onClick={() => {
																	setEditingCommentId(item.id);
																	setEditText(item.text);
																}}
																style={{
																	background: "none",
																	border: "none",
																	color: "#999",
																	cursor: "pointer",
																	padding: "4px",
																	borderRadius: "4px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	transition: "all 0.2s ease",
																}}
																onMouseOver={(e) => {
																	e.target.style.backgroundColor =
																		"rgba(204, 0, 0, 0.08)";
																	e.target.style.color = "#cc0000";
																}}
																onMouseOut={(e) => {
																	e.target.style.backgroundColor =
																		"transparent";
																	e.target.style.color = "#999";
																}}
																title="Edit comment"
															>
																<Edit2 size={14} />
															</button>
															<button
																onClick={() => handleDeleteComment(item.id)}
																disabled={saving}
																style={{
																	background: "none",
																	border: "none",
																	color: "#999",
																	cursor: saving ? "not-allowed" : "pointer",
																	padding: "4px",
																	borderRadius: "4px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	transition: "all 0.2s ease",
																	marginLeft: "4px",
																	opacity: saving ? 0.5 : 1,
																}}
																onMouseOver={(e) => {
																	if (!saving) {
																		e.target.style.backgroundColor =
																			"rgba(220, 38, 38, 0.08)";
																		e.target.style.color = "#dc2626";
																	}
																}}
																onMouseOut={(e) => {
																	if (!saving) {
																		e.target.style.backgroundColor =
																			"transparent";
																		e.target.style.color = "#999";
																	}
																}}
																title="Delete comment"
															>
																<Trash2 size={14} />
															</button>
														</>
													)}
												</Box>
											</Box>
											{(() => {
												const formattedComment = formatCommentForDisplay(
													item.text,
												);

												if (
													formattedComment.isMultiLine &&
													formattedComment.parsedComments.length > 0
												) {
													return (
														<Box sx={{ ml: 5 }}>
															{formattedComment.parsedComments.map(
																(comment, index) => (
																	<Box
																		key={index}
																		sx={{
																			mb: 1.5,
																			pb: 1.5,
																			borderBottom:
																				index <
																				formattedComment.parsedComments.length -
																					1
																					? "1px solid #f0f0f0"
																					: "none",
																		}}
																	>
																		{comment.date && comment.user && (
																			<Typography
																				variant="caption"
																				sx={{
																					color: "#666",
																					fontSize: "11px",
																					fontWeight: 600,
																					display: "block",
																					mb: 0.5,
																				}}
																			>
																				{comment.date} - {comment.user}:
																			</Typography>
																		)}
																		<Typography
																			variant="body2"
																			sx={{
																				color: "#333",
																				lineHeight: 1.6,
																				fontSize: "13px",
																				whiteSpace: "pre-wrap",
																			}}
																		>
																			{comment.text}
																		</Typography>
																	</Box>
																),
															)}
														</Box>
													);
												} else {
													return (
														<Typography
															variant="body2"
															sx={{
																whiteSpace: "pre-wrap",
																color: "#333",
																lineHeight: 1.6,
																fontSize: "13px",
																ml: 5, // Indent text to align with avatar
															}}
														>
															{formattedComment.cleanText}
														</Typography>
													);
												}
											})()}
										</Box>
									)}
								</Box>
							))}

							{/* Background Loading Indicator */}
							{backgroundLoading && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										py: 2,
										mt: 1,
									}}
								>
									<CircularProgress
										size={20}
										sx={{ color: "#cc0000", mr: 1 }}
									/>
									<Typography
										variant="caption"
										sx={{
											color: "#666",
											fontSize: "12px",
										}}
									>
										Loading more vehicle comments...
									</Typography>
								</Box>
							)}
						</Box>
					) : (
						<Box
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							height="100%"
							sx={{ color: "#666", py: 4 }}
						>
							<MessageSquare size={40} color="#d1d5db" />
							<Typography
								variant="body1"
								sx={{
									mt: 2,
									fontWeight: 500,
									color: "#4b5563",
								}}
							>
								No vehicle comments yet
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: "#9ca3af",
									fontSize: "13px",
									textAlign: "center",
									mt: 0.5,
								}}
							>
								Start the conversation by adding the first vehicle comment
							</Typography>
						</Box>
					)}
				</Box>

				{/* Add Comment Form */}
				<Box
					component="form"
					onSubmit={handleSubmit}
					sx={{
						p: 2.5,
						borderTop: "1px solid #e1e5e9",
						backgroundColor: "white",
						boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.05)",
					}}
				>
					{" "}
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Write a vehicle comment..."
						value={localVehicleComment}
						onChange={handleVehicleCommentChange}
						onKeyDown={handleKeyDown}
						multiline
						rows={2}
						disabled={saving}
						sx={{
							"& .MuiOutlinedInput-root": {
								borderRadius: "8px",
								fontSize: "14px",
								backgroundColor: "#fafafa",
								"&:hover": {
									backgroundColor: "#f5f5f5",
								},
								"&:hover fieldset": { borderColor: "#1e2832" },
								"&.Mui-focused": {
									backgroundColor: "white",
								},
								"&.Mui-focused fieldset": { borderColor: "#1e2832" },
							},
						}}
					/>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mt: 2,
						}}
					>
						<Typography
							variant="caption"
							sx={{
								color: "#9ca3af",
								fontSize: "11px",
								fontStyle: "MAN EUROPE_Bold",
							}}
						>
							Press Enter to Submit, Shift + Enter for a new line
						</Typography>
						<Box sx={{ display: "flex", gap: 1.5 }}>
							<Button
								variant="outlined"
								onClick={onClose}
								disabled={saving}
								sx={{
									textTransform: "none",
									color: "#6b7280",
									borderColor: "#d1d5db",
									fontSize: "13px",
									minWidth: "70px",
									"&:hover": {
										borderColor: "#1e2832",
										backgroundColor: "rgba(30, 40, 50, 0.04)",
									},
								}}
							>
								Close
							</Button>{" "}
							<Button
								type="submit"
								variant="contained"
								disabled={!localVehicleComment.trim() || saving}
								sx={{
									textTransform: "none",
									backgroundColor: "#cc0000",
									fontSize: "13px",
									minWidth: "80px",
									fontWeight: 600,
									"&:hover": { backgroundColor: "#b30000" },
									"&.Mui-disabled": {
										backgroundColor: "#e5e7eb",
										color: "#9ca3af",
									},
								}}
							>
								{saving ? "Adding..." : "Add Vehicle Comment"}
							</Button>
						</Box>
					</Box>
				</Box>
			</Box>
		</Dialog>
	);
};

const VFFDataTable = forwardRef(
	(
		{
			onSelectItems,
			selectedItems = [],
			onUpdateItem,
			getUserDisplayName,
			getUserEmail,
			isEditMode = false,
			editedItems = {},
			onEditedItemsChange,
			onEditModeToggle,
		},
		ref,
	) => {
		// Active UI language — used to translate German part descriptions to EN.
		const { language } = useLanguage();

		// Flag to prevent data fetching during updates
		const [isUpdating, setIsUpdating] = useState(false);
		const updateInProgress = useRef(false);

		const [searchQuery, setSearchQuery] = useState("");
		const [currentPage, setCurrentPage] = useState(1);
		const [itemsPerPage, setItemsPerPage] = useState(100);
		const [isLoading, setIsLoading] = useState(true);
		const [isLoadingMore, setIsLoadingMore] = useState(false);
		const [error, setError] = useState(null);
		const [tableData, setTableData] = useState([]);
		const [totalRecords, setTotalRecords] = useState(0);
		const [totalPages, setTotalPages] = useState(1);
		const [hasMoreData, setHasMoreData] = useState(true);
		const [allSelected, setAllSelected] = useState(false);
		const [rangePopoverOpen, setRangePopoverOpen] = useState(false);
		const [rangeFrom, setRangeFrom] = useState("");
		const [rangeTo, setRangeTo] = useState("");
		const [rangeError, setRangeError] = useState("");
		const rangePopoverRef = useRef(null);
		const checkboxRef = useRef(null);
		const [activeFilters, setActiveFilters] = useState({});
		const [sortBy, setSortBy] = useState(null);
		const [sortOrder, setSortOrder] = useState("asc");
		const [secondarySortBy, setSecondarySortBy] = useState(null);
		const [secondarySortOrder, setSecondarySortOrder] = useState("asc");
		const [ltbDates, setLtbDates] = useState({});
		const [openDatePicker, setOpenDatePicker] = useState(null);
		const [buildDateDates, setBuildDateDates] = useState({});
		const [openBuildDatePicker, setOpenBuildDatePicker] = useState(null);
		const [updatingBuildDateItems, setUpdatingBuildDateItems] = useState({});
		const [openPartDatePicker, setOpenPartDatePicker] = useState(null);
		const [openVehicleDatePicker, setOpenVehicleDatePicker] = useState(null);
		const [updateLoading, setUpdateLoading] = useState(false);
		const [updatingLtbItems, setUpdatingLtbItems] = useState({});
		const [updatingPartFollowupItems, setUpdatingPartFollowupItems] = useState(
			{},
		);
		const [updatingVehicleFollowupItems, setUpdatingVehicleFollowupItems] =
			useState({});
		const [partFollowupDates, setPartFollowupDates] = useState({});
		const [vehicleFollowupDates, setVehicleFollowupDates] = useState({});
		const [editingItems, setEditingItems] = useState({});
		const [responsibleValues, setResponsibleValues] = useState({});
		const [updatingResponsible, setUpdatingResponsible] = useState({});
		const [expandedRows, setExpandedRows] = useState({});
		const [sidePanelOpen, setSidePanelOpen] = useState(false);
		const [selectedRowForPanel, setSelectedRowForPanel] = useState(null);
		// Munich stock breakdown popover: null when closed, else { key, top, left }
		// positioned in viewport (fixed) coordinates so it isn't clipped by the
		// table's scroll container / sticky header.
		const [munichPopover, setMunichPopover] = useState(null);
		const [sidePanelPosition, setSidePanelPosition] = useState({
			top: 0,
			right: 0,
		});
		const [commentModalOpen, setCommentModalOpen] = useState(false);
		const [currentKastKey, setCurrentKastKey] = useState(null);
		const [newComment, setNewComment] = useState("");
		const [editingCommentId, setEditingCommentId] = useState(null);
		const [editText, setEditText] = useState("");
		const [saving, setSaving] = useState(false);

		// Pre-loaded comments data (from initial query)
		const [commentsData, setCommentsData] = useState({});

		// Translate the current page's German part descriptions to English
		// (AWS Translate via backend, cached). No-op unless language === "EN".
		const visibleDescriptions = useMemo(
			() => tableData.map((r) => r.part_description).filter(Boolean),
			[tableData],
		);
		const translateDescription = usePartTranslations(
			visibleDescriptions,
			language,
		);
		const [unsavedChangesDialog, setUnsavedChangesDialog] = useState({
			open: false,
			itemId: null,
			fieldType: null,
			originalValue: "",
			currentValue: "",
		});
		// State for flag update prevention modal
		const [flagUpdateModal, setFlagUpdateModal] = useState({
			open: false,
			type: "simultaneous", // 'simultaneous' or 'guide-to-edit'
			message: "",
		});
		// Track flag operation frequency to suggest edit mode
		const [flagOperationCount, setFlagOperationCount] = useState(0);
		const [flagOperationWindow, setFlagOperationWindow] = useState(Date.now());
		// User access control hook
		const userEmail = useMemo(
			() => (getUserEmail ? getUserEmail() : ""),
			[getUserEmail],
		);
		const {
			canWrite,
			canEditProtected,
			isLoading: accessLoading,
			refreshAccess,
		} = useUserAccess(userEmail);

		// Log access status for debugging
		useEffect(() => {
			if (!accessLoading) {
				console.log("🔐 VFF DataTable Access Control:", {
					userEmail,
					canWrite,
					canEditProtected,
					isEditMode,
				});
			}
		}, [accessLoading, canWrite, canEditProtected, userEmail, isEditMode]);

		// Global update tracking to prevent concurrent updates
		const [currentlyUpdating, setCurrentlyUpdating] = useState({
			field: null,
			itemId: null,
			timestamp: null,
		});
		// State for concurrent update warning dialog
		const [concurrentUpdateDialog, setConcurrentUpdateDialog] = useState({
			open: false,
			message: "",
			currentField: "",
			attemptedField: "",
		});
		// State for access denied modal (shown on 403 / read-only user write attempt)
		const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);

		// Track original values when user starts editing to detect unsaved changes
		const [originalResponsibleValues, setOriginalResponsibleValues] = useState(
			{},
		);

		// Last DB update state
		const [lastDbUpdate, setLastDbUpdate] = useState(null);

		// Helper function to check if any update is currently in progress
		const isAnyUpdateInProgress = () => {
			return (
				currentlyUpdating.field !== null ||
				Object.keys(updatingLtbItems).length > 0 ||
				Object.keys(updatingBuildDateItems).length > 0 ||
				Object.keys(updatingPartFollowupItems).length > 0 ||
				Object.keys(updatingVehicleFollowupItems).length > 0 ||
				Object.keys(updatingResponsible).length > 0 ||
				Object.keys(updatingPartStatus).length > 0 ||
				Object.keys(updatingVehicleStatus).length > 0 ||
				Object.keys(updatingFlagging).length > 0 ||
				Object.keys(updatingArchive).length > 0 ||
				Object.keys(updatingStatusBTT).length > 0
			);
		};
		// Helper function to get current update description
		const getCurrentUpdateDescription = () => {
			if (Object.keys(updatingLtbItems).length > 0) return "LTB date update";
			if (Object.keys(updatingBuildDateItems).length > 0)
				return "Build date update";
			if (Object.keys(updatingPartFollowupItems).length > 0)
				return "Part followup date update";
			if (Object.keys(updatingVehicleFollowupItems).length > 0)
				return "Vehicle followup date update";
			if (Object.keys(updatingResponsible).length > 0)
				return "Responsible person update";
			if (Object.keys(updatingPartStatus).length > 0)
				return "Part status update";
			if (Object.keys(updatingVehicleStatus).length > 0)
				return "Vehicle status update";
			if (Object.keys(updatingFlagging).length > 0)
				return "Flagging status update";
			if (Object.keys(updatingArchive).length > 0)
				return "Archive status update";
			if (Object.keys(updatingStatusBTT).length > 0) return "Status BTT update";
			if (currentlyUpdating.field) return `${currentlyUpdating.field} update`;
			return "unknown update";
		};

		// Helper function to show concurrent update warning
		const showConcurrentUpdateWarning = (attemptedField) => {
			const currentUpdate = getCurrentUpdateDescription();
			setConcurrentUpdateDialog({
				open: true,
				message: `Please wait for the current ${currentUpdate} to complete before making additional changes.`,
				currentField: currentUpdate,
				attemptedField: attemptedField,
			});
		};

		// Vehicle comment modal state management
		const [vehicleCommentModalOpen, setVehicleCommentModalOpen] =
			useState(false);
		const [currentVehicleKastKey, setCurrentVehicleKastKey] = useState(null);
		const [newVehicleComment, setNewVehicleComment] = useState("");
		const [editingVehicleCommentId, setEditingVehicleCommentId] =
			useState(null);
		const [editVehicleText, setEditVehicleText] = useState("");
		const [updatingPartStatus, setUpdatingPartStatus] = useState({});
		const [updatingVehicleStatus, setUpdatingVehicleStatus] = useState({});
		const [updatingFlagging, setUpdatingFlagging] = useState({});
		const [updatingArchive, setUpdatingArchive] = useState({});
		const [updatingStatusBTT, setUpdatingStatusBTT] = useState({});
		const [tempStatusBTTSelections, setTempStatusBTTSelections] = useState({});
		const [openArchiveDropdowns, setOpenArchiveDropdowns] = useState({});
		const statusUpdateQueue = useRef([]);
		const isProcessingStatusQueue = useRef(false);
		const [queueLength, setQueueLength] = useState(0);
		const isStateTriggeredFetch = useRef(false);
		const isDashboardFilterActive = useRef(false);

		// Immediately check for dashboard parameters on component initialization
		// This prevents the initial useEffect from running when coming from dashboard
		const checkForDashboardParameters = () => {
			const urlParams = new URLSearchParams(window.location.search);
			const fromDashboard = urlParams.get("from_dashboard");
			const vehicleFilter = urlParams.get("vehicle_filter");
			const statusColorFilter = urlParams.get("status_color_filter");

			if (fromDashboard === "true" && (vehicleFilter || statusColorFilter)) {
				console.log(
					"🛑 Dashboard parameters detected - blocking initial fetch",
				);
				isDashboardFilterActive.current = true;
				return true;
			}
			return false;
		};

		// Check for dashboard parameters immediately when component initializes
		const hasDashboardParams = checkForDashboardParameters();

		// Process status updates from queue one by one
		const processStatusUpdateQueue = async () => {
			if (
				isProcessingStatusQueue.current ||
				statusUpdateQueue.current.length === 0
			) {
				return;
			}
			isProcessingStatusQueue.current = true;
			console.log(
				`🚀 Processing status update queue with ${statusUpdateQueue.current.length} items`,
			);
			setQueueLength(statusUpdateQueue.current.length);

			while (statusUpdateQueue.current.length > 0) {
				const updateItem = statusUpdateQueue.current.shift();
				setQueueLength(statusUpdateQueue.current.length);
				const { id, field, currentValue, newValue } = updateItem;

				try {
					console.log(
						`🔄 Processing queued status update for ${id}: ${field} ${currentValue} -> ${newValue}`,
					);

					// Set loading state for visual feedback
					if (field === "snr_ma_nahme_status") {
						setUpdatingPartStatus((prev) => ({ ...prev, [id]: true }));
					} else if (field === "fzg_ma_nahme_status") {
						setUpdatingVehicleStatus((prev) => ({ ...prev, [id]: true }));
					} // Update local state immediately for UI feedback
					updateLocalState(id, field, newValue);

					// Get user display name for tracking
					const userName = getUserEmail();
					const headers = {
						"X-User-Name": userName,
					};

					// Make the API call
					let response;
					if (
						field === "snr_ma_nahme_status" ||
						field === "fzg_ma_nahme_status"
					) {
						response = await apiClient.post(
							"/api/vff-status-update",
							{
								kast_key: id,
								field: field,
								value: newValue,
							},
							{ headers },
						);
					} else {
						response = await apiClient.post(
							"/api/update-item",
							{
								id: id,
								field,
								value: newValue,
							},
							{ headers },
						);
					}

					if (!response.data) {
						throw new Error("Failed to update item");
					}
					console.log(`✅ Status update completed for ${id}`);

					// Small delay between updates to prevent overwhelming the server
					await new Promise((resolve) => setTimeout(resolve, 150));
				} catch (error) {
					console.error(
						`❌ Error updating queued status ${field} for ${id}:`,
						error,
					);

					// Revert local state on error
					const revertValue = currentValue; // Revert to the original value
					updateLocalState(id, field, revertValue); // 403 = access denied
					if (error.response?.status === 403) {
						refreshAccess();
						setShowAccessDeniedModal(true);
						return;
					}

					// Show user-friendly error message
					let userMessage = "Error updating status. Please try again.";
					if (error.message && error.message.includes("500")) {
						userMessage =
							"🔧 Server error occurred. Please try again in a moment.";
					} else if (error.message && error.message.includes("Network")) {
						userMessage =
							"🌐 Network connection issue. Please check your internet connection.";
					}

					console.error(
						`⚠️ Status update failed for ${id}. Showing user message: ${userMessage}`,
					);
					// Only show alert for critical errors, not for every queue item
					if (statusUpdateQueue.current.length <= 1) {
						alert(userMessage);
					}
				} finally {
					// Clear loading state
					if (field === "snr_ma_nahme_status") {
						setUpdatingPartStatus((prev) => {
							const newState = { ...prev };
							delete newState[id];
							return newState;
						});
					} else if (field === "fzg_ma_nahme_status") {
						setUpdatingVehicleStatus((prev) => {
							const newState = { ...prev };
							delete newState[id];
							return newState;
						});
					}
				}
			}
			isProcessingStatusQueue.current = false;
			setQueueLength(0);
			console.log(`🏁 Status update queue processing completed`);
		}; // Add click-outside handler to close Status BTT dropdowns
		useEffect(() => {
			const handleClickOutside = (event) => {
				// Close all Status BTT dropdowns when clicking outside
				const dropdowns = document.querySelectorAll(
					'[id^="status-btt-dropdown-"]',
				);
				dropdowns.forEach((dropdown) => {
					if (
						!dropdown.contains(event.target) &&
						!dropdown.previousElementSibling?.contains(event.target)
					) {
						dropdown.style.display = "none";
						// Clear temp selections for this dropdown
						const dropdownId = dropdown.id;
						const itemId = dropdownId.replace("status-btt-dropdown-", "");
						setTempStatusBTTSelections((prev) => {
							const newState = { ...prev };
							delete newState[itemId];
							return newState;
						});
					}
				});

				// Close all Archive dropdowns when clicking outside
				const archiveDropdowns = document.querySelectorAll(
					'[id^="archive-dropdown-"]',
				);
				archiveDropdowns.forEach((dropdown) => {
					if (
						!dropdown.contains(event.target) &&
						!dropdown.previousElementSibling?.contains(event.target)
					) {
						dropdown.style.display = "none";
					}
				});
			};

			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}, []);
		// Reset flag operation tracking when entering edit mode or component unmounts
		useEffect(() => {
			if (isEditMode) {
				// Reset tracking when entering edit mode
				setFlagOperationCount(0);
				setFlagOperationWindow(Date.now());
			}
		}, [isEditMode]);
		// Cleanup flag operation tracking and status queue on component unmount
		useEffect(() => {
			return () => {
				setFlagOperationCount(0);
				// Clear any pending status updates
				statusUpdateQueue.current = [];
				isProcessingStatusQueue.current = false;
				setQueueLength(0);
			};
		}, []);

		// Auto-dismiss simultaneous operations modal when all operations complete
		useEffect(() => {
			if (flagUpdateModal.open && flagUpdateModal.type === "simultaneous") {
				const ongoingUpdates = Object.keys(updatingFlagging).length > 0;
				if (!ongoingUpdates) {
					// All operations completed, auto-dismiss the modal after a short delay
					const timer = setTimeout(() => {
						setFlagUpdateModal((prev) => ({ ...prev, open: false }));
					}, 1000);
					return () => clearTimeout(timer);
				}
			}
		}, [updatingFlagging, flagUpdateModal.open, flagUpdateModal.type]);
		// Ref to prevent duplicate DB update fetches
		const dbUpdateFetched = useRef(false);

		// Ref to prevent duplicate initial data fetches
		const initialDataFetched = useRef(false);

		// Fetch last DB update on component mount
		const fetchLastDbUpdate = async () => {
			// Prevent duplicate calls
			if (dbUpdateFetched.current) {
				console.log("📅 DB Update already fetched, skipping...");
				return;
			}

			dbUpdateFetched.current = true;

			try {
				const response = await apiClient.get("/api/last-db-update");
				if (response.data && response.data.last_update) {
					setLastDbUpdate(response.data.last_update);
					console.log("📅 Last DB Update:", response.data.last_update);
				}
			} catch (error) {
				console.error("❌ Error fetching last DB update:", error);
				dbUpdateFetched.current = false; // Reset on error to allow retry
				// Don't set error state, just log it - this is not critical
			}
		};

		useEffect(() => {
			fetchLastDbUpdate();
		}, []); // Run once on mount

		// Handle sorting with compound sorting support
		const handleSort = (field) => {
			console.log("🔄 Sort clicked:", {
				field,
				currentSortBy: sortBy,
				currentSortOrder: sortOrder,
				currentSecondarySortBy: secondarySortBy,
				currentSecondarySortOrder: secondarySortOrder,
			});

			let newSortBy, newSortOrder, newSecondarySortBy, newSecondarySortOrder;

			if (sortBy === field) {
				// If already sorting by this field, toggle the order
				newSortBy = field;
				newSortOrder = sortOrder === "asc" ? "desc" : "asc";
				newSecondarySortBy = secondarySortBy;
				newSecondarySortOrder = secondarySortOrder;
				console.log("🔄 Toggling sort order:", newSortOrder);
			} else {
				// If sorting by a new field, implement compound sorting logic
				const isDateField = field === "required_date";
				const isPartField = field === "part_no";
				const currentIsDateField = sortBy === "required_date";
				const currentIsPartField = sortBy === "part_no";

				// Check if we're creating a Date + Part No compound sort
				if (
					(isDateField && currentIsPartField) ||
					(isPartField && currentIsDateField)
				) {
					// Compound sorting: always group by part_no first, then sort dates within groups
					newSortBy = "part_no";
					newSortOrder = "asc"; // Always group by part_no ascending

					// Set secondary sort for dates
					newSecondarySortBy = "required_date";
					// If user clicked on dates field, use ascending as default
					// If user was already sorting dates, preserve that order
					if (isDateField) {
						newSecondarySortOrder = "asc";
					} else if (currentIsDateField) {
						newSecondarySortOrder = sortOrder; // Preserve current date sort order
					} else {
						newSecondarySortOrder = "asc";
					}

					console.log(
						"🔄 Creating compound sort: Part No (asc) + Dates (" +
							newSecondarySortOrder +
							")",
					);
				} else {
					// Regular single-field sorting
					newSortBy = field;
					newSortOrder = "asc";
					newSecondarySortBy = null;
					newSecondarySortOrder = "asc";
					console.log("🔄 Setting new sort field:", field);
				}
			}

			// Update state
			setSortBy(newSortBy);
			setSortOrder(newSortOrder);
			setSecondarySortBy(newSecondarySortBy);
			setSecondarySortOrder(newSecondarySortOrder);

			// Reset to first page when sorting changes
			setCurrentPage(1);

			// Fetch data with new sorting - pass the new values directly
			console.log("🔄 Fetching data with compound sort:", {
				primary: { field: newSortBy, order: newSortOrder },
				secondary: { field: newSecondarySortBy, order: newSecondarySortOrder },
			});

			fetchData(
				activeFilters,
				1,
				itemsPerPage,
				searchQuery,
				false,
				newSortBy,
				newSortOrder,
				newSecondarySortBy,
				newSecondarySortOrder,
			);
		};

		// Handle Deselect All button
		const handleDeselectAll = () => {
			// Deselecting all items
			onSelectItems?.([]);
			setAllSelected(false);
		};

		// Handle select-all checkbox click — open range popover instead of immediately selecting
		const handleSelectAllClick = (e) => {
			e.preventDefault();
			setRangeFrom("");
			setRangeTo("");
			setRangeError("");
			setRangePopoverOpen((prev) => !prev);
		};

		// Apply range selection
		const handleApplyRangeSelection = () => {
			const from = parseInt(rangeFrom, 10);
			const to = parseInt(rangeTo, 10);
			const total = tableData.length;

			if (!rangeFrom && !rangeTo) {
				// No range entered — select all loaded items
				const pageData = tableData.map((item) => ({
					...item,
					id: item.kast_key,
				}));
				onSelectItems?.(pageData);
				setAllSelected(true);
				setRangePopoverOpen(false);
				return;
			}

			if (isNaN(from) || isNaN(to)) {
				setRangeError("Please enter valid numbers for both fields.");
				return;
			}
			if (from < 1 || to < 1) {
				setRangeError("Row numbers must be 1 or greater.");
				return;
			}
			if (from > to) {
				setRangeError('"From" must be less than or equal to "To".');
				return;
			}
			if (from > total) {
				setRangeError(`"From" (${from}) exceeds total loaded rows (${total}).`);
				return;
			}

			const clampedTo = Math.min(to, total);
			// tableData rows are 1-indexed in the UI (rowIndex + 1)
			const rangeItems = tableData.slice(from - 1, clampedTo).map((item) => ({
				...item,
				id: item.kast_key,
			}));

			// Merge with existing selections (avoid duplicates)
			const existingIds = new Set(selectedItems.map((s) => s.id));
			const newItems = rangeItems.filter((item) => !existingIds.has(item.id));
			const merged = [...selectedItems, ...newItems];

			onSelectItems?.(merged);
			setAllSelected(merged.length === total && total > 0);
			setRangeError("");
			setRangePopoverOpen(false);
		};

		// Close popover when clicking outside
		useEffect(() => {
			if (!rangePopoverOpen) return;
			const handleOutsideClick = (e) => {
				if (
					rangePopoverRef.current &&
					!rangePopoverRef.current.contains(e.target) &&
					checkboxRef.current &&
					!checkboxRef.current.contains(e.target)
				) {
					setRangePopoverOpen(false);
				}
			};
			document.addEventListener("mousedown", handleOutsideClick);
			return () =>
				document.removeEventListener("mousedown", handleOutsideClick);
		}, [rangePopoverOpen]);

		// Handle individual row checkbox
		const handleSelectItem = (item) => {
			const itemId = item.kast_key; // Use kast_key as unique identifier
			const isSelected = selectedItems.some(
				(selected) => selected.id === itemId,
			);
			let newSelectedItems;
			if (isSelected) {
				newSelectedItems = selectedItems.filter(
					(selected) => selected.id !== itemId,
				);
			} else {
				newSelectedItems = [...selectedItems, { ...item, id: itemId }];
			}
			onSelectItems?.(newSelectedItems);
			setAllSelected(
				newSelectedItems.length === tableData.length && tableData.length > 0,
			);
		};

		// Handle double-click on checkbox to select all items with same part number
		const handleDoubleClickSelectBySNR = (item) => {
			const partNumber = item.part_no;
			if (!partNumber || partNumber === "-") return;

			// Find all items on current page with the same part number
			const itemsWithSamePartNo = tableData.filter(
				(row) => row.part_no === partNumber,
			);

			if (itemsWithSamePartNo.length === 0) return;

			// Convert to the format expected by selectedItems (with id property)
			const itemsToSelect = itemsWithSamePartNo.map((row) => ({
				...row,
				id: row.kast_key,
			}));

			// Merge with existing selections (avoid duplicates)
			const existingIds = new Set(selectedItems.map((item) => item.id));
			const newItems = itemsToSelect.filter(
				(item) => !existingIds.has(item.id),
			);
			const newSelectedItems = [...selectedItems, ...newItems];

			onSelectItems?.(newSelectedItems);
			setAllSelected(
				newSelectedItems.length === tableData.length && tableData.length > 0,
			);

			// Show a brief notification to the user
			console.log(
				`Selected ${itemsToSelect.length} part(s) with part number: ${partNumber}`,
			);
		};

		// Handle opening side panel for more info
		const handleOpenSidePanel = (row, event) => {
			// Check if panel is already open for the same row
			if (
				sidePanelOpen &&
				selectedRowForPanel &&
				selectedRowForPanel.kast_key === row.kast_key
			) {
				// Close the panel if clicking on the same row's "More info" button
				setSidePanelOpen(false);
				setSelectedRowForPanel(null);
			} else {
				// Debug logging for row data
				console.log(
					"VFF DataTable - Row data being passed to side panel:",
					row,
				);
				console.log("VFF DataTable - All keys in row:", Object.keys(row));
				console.log("VFF DataTable - old_snr in row:", row.old_snr);
				console.log("VFF DataTable - new_snr in row:", row.new_snr);
				console.log("VFF DataTable - change_no in row:", row.change_no);
				console.log(
					"VFF DataTable - kast_key_anlage in row:",
					row.kast_key_anlage,
				);
				console.log("VFF DataTable - mb_key in row:", row.mb_key);
				console.log("VFF DataTable - baukasten_no in row:", row.baukasten_no);

				// Open the panel with the new row data
				setSelectedRowForPanel(row);
				setSidePanelOpen(true);
			}
		};

		// Handle closing side panel
		const handleCloseSidePanel = () => {
			setSidePanelOpen(false);
			setSelectedRowForPanel(null);
		};

		// Handle editing responsible fields
		const handleEditResponsible = (itemId) => {
			console.log("🔧 Edit button clicked for itemId:", itemId);
			// Find the item to get initial values using kast_key
			const item = tableData.find((item) => item.kast_key === itemId);

			if (item) {
				// Initialize the edit values
				setResponsibleValues((prev) => ({
					...prev,
					[itemId]: {
						part_responsible:
							item.part_responsible !== "-" ? item.part_responsible : "",
						vehicle_responsible:
							item.vehicle_responsible !== "-" ? item.vehicle_responsible : "",
					},
				}));

				// Set the item as editing
				setEditingItems((prev) => ({
					...prev,
					[itemId]: true,
				}));
			}
		};

		// Handle saving individual responsible field (for Enter key functionality)
		const handleSaveResponsibleField = async (itemId, fieldType) => {
			try {
				const values = responsibleValues[itemId];
				if (!values) return;

				// Find the item using kast_key
				const itemToUpdate = tableData.find((item) => item.kast_key === itemId);
				if (!itemToUpdate) return;

				const kastKey = itemToUpdate.kast_key;
				const fieldName =
					fieldType === "part_responsible"
						? "part_responsible"
						: "vehicle_responsible";
				const newValue = values[fieldName];

				// Only update if the value has changed
				if (
					newValue === itemToUpdate[fieldName] ||
					(newValue === "" && itemToUpdate[fieldName] === "-")
				) {
					return; // No change needed
				}

				console.log(`Updating ${fieldName} for kast_key:`, kastKey);

				// Set loading state for this specific field
				setUpdatingResponsible((prev) => ({
					...prev,
					[itemId]: true,
				}));

				try {
					const response = await apiClient.post("/api/update-item", {
						id: kastKey,
						field: fieldName,
						value: newValue || "-",
					});
					console.log(`✅ ${fieldName} updated successfully:`, response.data);
					// Update local table data to reflect the changes immediately
					setTableData((prev) =>
						prev.map((item) => {
							if (item.kast_key === itemId) {
								return {
									...item,
									[fieldName]: newValue || "-",
								};
							}
							return item;
						}),
					);

					// Update original values to match the saved value to prevent false positives
					setOriginalResponsibleValues((prev) => ({
						...prev,
						[itemId]: {
							...prev[itemId],
							[fieldName]: newValue || "",
						},
					}));
				} catch (error) {
					console.error(`❌ Error updating ${fieldName}:`, error);
					throw error;
				}
			} catch (error) {
				console.error("Error saving responsible field:", error);

				if (error.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
					return;
				}

				// Show user-friendly error message
				let userMessage = `Failed to update responsible field. Please try again.`;
				if (error.response?.data?.detail) {
					userMessage = error.response.data.detail;
				} else if (error.message && error.message !== "Network Error") {
					userMessage = error.message;
				}

				alert(userMessage);
			} finally {
				// Clear loading state
				setUpdatingResponsible((prev) => {
					const newState = { ...prev };
					delete newState[itemId];
					return newState;
				});
			}
		};

		// Handle saving responsible fields
		const handleSaveResponsible = async (itemId) => {
			try {
				const values = responsibleValues[itemId];
				if (!values) return;

				// Show loading state
				setUpdatingResponsible((prev) => ({
					...prev,
					[itemId]: true,
				}));

				// Find the item using kast_key
				const itemToUpdate = tableData.find((item) => item.kast_key === itemId);

				if (!itemToUpdate) return;
				const kastKey = itemToUpdate.kast_key;
				console.log("Updating responsible fields for kast_key:", kastKey);

				// Get user display name for tracking
				const userName = getUserEmail();
				const headers = {
					"X-User-Name": userName,
				};

				// Update part_responsible if changed
				if (values.part_responsible !== itemToUpdate.part_responsible) {
					try {
						const response = await apiClient.post(
							"/api/update-item",
							{
								id: kastKey,
								field: "part_responsible",
								value: values.part_responsible || "-",
							},
							{ headers },
						);
						console.log(
							"✅ Part responsible updated successfully:",
							response.data,
						);
					} catch (error) {
						console.error("❌ Error updating part responsible:", error);
						throw error;
					}
				}

				// Update vehicle_responsible if changed
				if (values.vehicle_responsible !== itemToUpdate.vehicle_responsible) {
					try {
						const response = await apiClient.post(
							"/api/update-item",
							{
								id: kastKey,
								field: "vehicle_responsible",
								value: values.vehicle_responsible || "-",
							},
							{ headers },
						);
						console.log(
							"✅ Vehicle responsible updated successfully:",
							response.data,
						);
					} catch (error) {
						console.error("❌ Error updating vehicle responsible:", error);
						throw error;
					}
				}

				// Update local table data to reflect the changes immediately
				setTableData((prev) =>
					prev.map((item) => {
						if (item.kast_key === itemId) {
							return {
								...item,
								part_responsible: values.part_responsible || "-",
								vehicle_responsible: values.vehicle_responsible || "-",
							};
						}
						return item;
					}),
				);

				// Exit editing mode
				setEditingItems((prev) => {
					const newState = { ...prev };
					delete newState[itemId];
					return newState;
				});
			} catch (error) {
				console.error("Error saving responsible fields:", error);

				if (error.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
					return;
				}

				// Show user-friendly error message
				let userMessage =
					"Failed to update responsible fields. Please try again.";

				if (error.message || (error.response && error.response.data)) {
					const errorText =
						error.message || JSON.stringify(error.response.data);

					if (errorText.includes("ICEBERG_COMMIT_ERROR")) {
						userMessage =
							"⚠️ Unable to save changes - the database is currently being updated by another process. Please wait a moment and try again.";
					} else if (errorText.includes("Failed to commit")) {
						userMessage =
							"⚠️ Save operation failed - there may be conflicting changes. Please refresh the page and try again.";
					} else if (errorText.includes("timeout")) {
						userMessage =
							"⏱️ The update is taking longer than expected. Please wait and check if the changes were saved.";
					} else if (errorText.includes("500")) {
						userMessage =
							"🔧 Server error occurred. Please try again in a moment.";
					} else if (errorText.includes("Network")) {
						userMessage =
							"🌐 Network connection issue. Please check your internet connection and try again.";
					}
				}

				alert(userMessage);
			} finally {
				// Clear loading state
				setUpdatingResponsible((prev) => {
					const newState = { ...prev };
					delete newState[itemId];
					return newState;
				});
			}
		};

		// Handle canceling edit
		const handleCancelEdit = (itemId) => {
			// Remove the item from editing state
			setEditingItems((prev) => {
				const newState = { ...prev };
				delete newState[itemId];
				return newState;
			});

			// Clear the values
			setResponsibleValues((prev) => {
				const newState = { ...prev };
				delete newState[itemId];
				return newState;
			});
		};

		// Handle comment modal functions
		const handleOpenCommentModal = (item) => {
			// Cancel any ongoing edit operations to prevent state conflicts
			if (editingItems[item.kast_key]) {
				handleCancelEdit(item.kast_key);
			}

			console.log("🔍 Opening comment modal for item:", item);
			console.log("🗝️ Full kast_key:", item.kast_key);
			console.log("📏 kast_key length:", item.kast_key?.length);

			setCurrentKastKey(item.kast_key);
			setNewComment("");
			setEditingCommentId(null);
			setEditText("");
			setCommentModalOpen(true);
		};

		const handleCloseCommentModal = () => {
			setCommentModalOpen(false);
			setCurrentKastKey(null);
			// Reset all comment input states when closing modal
			setNewComment("");
			setEditingCommentId(null);
			setEditText("");
		};
		const handleCommentAdded = async (kastKey) => {
			// Refetch the latest comment for this specific kast_key to update local state
			if (kastKey) {
				try {
					const encodedKastKey = encodeURIComponent(kastKey);
					const response = await apiClient.get(
						`/api/vff-comments?kast_key=${encodedKastKey}`,
					);
					const comments = response.data.comments || [];

					if (comments.length > 0) {
						// Update local comments data with the latest part comment
						setCommentsData((prev) => ({
							...prev,
							[kastKey]: {
								...prev[kastKey],
								latest_part_comment: comments[0].text || "",
								latest_part_comment_date: comments[0].timestamp || "",
								latest_part_comment_user: comments[0].user || "",
							},
						}));
					}
				} catch (error) {
					console.error("Error refreshing comment data:", error);
				}
			}
		};

		// Vehicle Comment Modal Handlers
		const handleOpenVehicleCommentModal = (row) => {
			console.log("🎯 Opening vehicle comment modal for row:", row);
			setCurrentVehicleKastKey(row.kast_key);
			setVehicleCommentModalOpen(true);
		};

		const handleCloseVehicleCommentModal = () => {
			setVehicleCommentModalOpen(false);
			setCurrentVehicleKastKey(null);
			// Reset all vehicle comment input states when closing modal
			setNewVehicleComment("");
			setEditingVehicleCommentId(null);
			setEditVehicleText("");
		};
		const handleVehicleCommentAdded = async (kastKey) => {
			// Refetch the latest vehicle comment for this specific kast_key to update local state
			if (kastKey) {
				try {
					const encodedKastKey = encodeURIComponent(kastKey);
					const response = await apiClient.get(
						`/api/vff-vehicle-comments?kast_key=${encodedKastKey}`,
					);
					const comments = response.data.comments || [];

					if (comments.length > 0) {
						// Update local comments data with the latest vehicle comment
						setCommentsData((prev) => ({
							...prev,
							[kastKey]: {
								...prev[kastKey],
								latest_vehicle_comment: comments[0].text || "",
								latest_vehicle_comment_date: comments[0].timestamp || "",
								latest_vehicle_comment_user: comments[0].user || "",
							},
						}));
					}
				} catch (error) {
					console.error("Error refreshing vehicle comment data:", error);
				}
			}
		};
		// Handle Status BTT updates
		const handleStatusBTTUpdate = async (id, selectedValues) => {
			// Check write access first
			if (!canWrite) {
				setShowAccessDeniedModal(true);
				return;
			}
			// Check for concurrent updates before proceeding
			if (isAnyUpdateInProgress()) {
				showConcurrentUpdateWarning("Status BTT update");
				return;
			}

			try {
				setUpdatingStatusBTT((prev) => ({ ...prev, [id]: true }));
				// Join selected values with comma
				const newValue = Array.isArray(selectedValues)
					? selectedValues.join(",")
					: selectedValues || "";
				console.log(`Updating status_btt for ${id} to: ${newValue}`);

				// Get user email for tracking
				const userName = getUserEmail();
				const headers = {
					"X-User-Name": userName,
				};

				const response = await apiClient.post(
					"/api/update-item",
					{
						id: id,
						field: "status_btt",
						value: newValue,
					},
					{ headers },
				);

				if (response.data) {
					// Update local state
					setTableData((prev) =>
						prev.map((item) => {
							if (item.kast_key === id) {
								return { ...item, status_btt: newValue };
							}
							return item;
						}),
					);
					console.log(`✅ Status BTT updated successfully for ${id}`);
				} else {
					console.error(`❌ Error updating status BTT:`, response.status);
					alert("Error updating status BTT. Please try again.");
				}
			} catch (error) {
				console.error(`❌ Error updating status BTT:`, error);
				if (error.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
				} else {
					alert("Error updating status BTT. Please try again.");
				}
			} finally {
				setUpdatingStatusBTT((prev) => {
					const newState = { ...prev };
					delete newState[id];
					return newState;
				});
			}
		};
		// Status toggle handler - 3-step cycle with queue management
		const handleStatusToggle = async (id, field, currentValue) => {
			// Check write access first
			if (!canWrite) {
				setShowAccessDeniedModal(true);
				console.warn(
					"🚫 User attempted to toggle status without write access:",
					userEmail,
					field,
				);
				return;
			}

			// Check authorization for protected fields
			if (!isUserAuthorizedForResponsibleFields(getUserEmail)) {
				setShowAccessDeniedModal(true);
				return;
			}

			// Check for concurrent updates before proceeding
			if (isAnyUpdateInProgress()) {
				showConcurrentUpdateWarning(
					`${field === "snr_ma_nahme_status" ? "Part status" : "Vehicle status"} toggle`,
				);
				return;
			}

			// Define the 3-step cycle: null/blank → "in Arbeit" → "erledigt" → null/blank
			let newValue;
			switch (currentValue) {
				case null:
				case undefined:
				case 0:
				case "":
					newValue = "in Arbeit";
					break;
				case "in Arbeit":
				case 1:
					newValue = "erledigt";
					break;
				case "erledigt":
				case 2:
					newValue = null; // Reset to blank/null
					break;
				default:
					newValue = "in Arbeit"; // Default fallback
					break;
			}

			console.log(
				`🔄 Queueing status toggle ${field} for ${id}: ${currentValue} -> ${newValue}`,
			);

			// Check if this item is already in the queue
			const existingIndex = statusUpdateQueue.current.findIndex(
				(item) => item.id === id && item.field === field,
			);
			if (existingIndex !== -1) {
				// Update existing queue item with latest value
				statusUpdateQueue.current[existingIndex] = {
					id,
					field,
					currentValue,
					newValue,
				};
				console.log(`📝 Updated existing queue item for ${id}`);
			} else {
				// Add new item to queue
				statusUpdateQueue.current.push({ id, field, currentValue, newValue });
				console.log(`➕ Added new item to status update queue for ${id}`);
			}

			// Update queue length for UI reactivity
			setQueueLength(statusUpdateQueue.current.length);

			// Immediately update the local UI to show the new state optimistically
			updateLocalState(id, field, newValue);

			// Set visual loading indicator to show this item is being processed
			if (field === "snr_ma_nahme_status") {
				setUpdatingPartStatus((prev) => ({ ...prev, [id]: true }));
			} else if (field === "fzg_ma_nahme_status") {
				setUpdatingVehicleStatus((prev) => ({ ...prev, [id]: true }));
			} // Process the queue (this function handles concurrency internally)
			processStatusUpdateQueue();
		};
		// Handle dropdown updates
		const handleUpdateItem = (id, field, value) => {
			// Check write access first
			if (!canWrite) {
				setShowAccessDeniedModal(true);
				console.warn(
					"🚫 User attempted to update without write access:",
					userEmail,
					field,
					value,
				);
				return;
			}

			// Check protected field access
			const protectedFields = [
				"part_responsible",
				"vehicle_responsible",
				"part_followup_date",
				"vehicle_followup_date",
				"flagging",
				"archive",
				"ltb",
				"ltb_date",
				"build_date",
				"status_btt",
				"snr_ma_nahme_status",
				"fzg_ma_nahme_status",
			];
			if (protectedFields.includes(field) && !canEditProtected) {
				setShowAccessDeniedModal(true);
				console.warn(
					"🚫 User attempted to update protected field without authorization:",
					userEmail,
					field,
					value,
				);
				return;
			}

			// Check for concurrent updates before proceeding
			if (isAnyUpdateInProgress()) {
				showConcurrentUpdateWarning(field);
				return;
			}

			// Close any open date pickers
			setOpenDatePicker(null);
			setOpenBuildDatePicker(null);
			setOpenPartDatePicker(null);
			setOpenVehicleDatePicker(null);

			// Update immediately without confirmation for all fields including dates
			executeUpdate(id, field, value);
		};

		// Helper function to update local state immediately for UI feedback
		const updateLocalState = (id, field, value) => {
			setTableData((prevData) =>
				prevData.map((item) =>
					item.kast_key === id ? { ...item, [field]: value } : item,
				),
			);
			// Also update the specific date state variables for immediate UI updates
			if (field === "part_followup_date") {
				setPartFollowupDates((prev) => ({
					...prev,
					[id]: value,
				}));
			} else if (field === "vehicle_followup_date") {
				setVehicleFollowupDates((prev) => ({
					...prev,
					[id]: value,
				}));
			} else if (field === "build_date") {
				setBuildDateDates((prev) => ({
					...prev,
					[id]: value,
				}));
			}
		};
		// Helper function to revert local state changes on error
		const revertLocalState = (id, field) => {
			// Find the original value from the server data
			setTableData((prevData) => {
				const originalItem = prevData.find((item) => item.kast_key === id);
				if (originalItem) {
					// In practice, we'd need to store original values somewhere
					// For now, we'll trigger a refresh of the data
					console.log(`Reverting changes for ${field} on item ${id}`);
				}
				return prevData; // Keep current state for now
			});

			// Also revert the specific date state variables if needed
			if (field === "part_followup_date") {
				// Revert to original value from tableData
				const originalItem = tableData.find((item) => item.kast_key === id);
				if (originalItem) {
					setPartFollowupDates((prev) => ({
						...prev,
						[id]: originalItem.part_followup_date,
					}));
				}
			} else if (field === "vehicle_followup_date") {
				// Revert to original value from tableData
				const originalItem = tableData.find((item) => item.kast_key === id);
				if (originalItem) {
					setVehicleFollowupDates((prev) => ({
						...prev,
						[id]: originalItem.vehicle_followup_date,
					}));
				}
			}
		};
		// Execute the actual update
		const executeUpdate = async (id, field, value) => {
			try {
				// Set global update state to prevent concurrent updates
				setCurrentlyUpdating({
					field: field,
					itemId: id,
					timestamp: Date.now(),
				});

				// Set update flags to prevent data refetching during updates
				console.log("🔄 Setting update flags to prevent refresh:", {
					field,
					id,
					value,
				});
				setIsUpdating(true);
				updateInProgress.current = true;
				setUpdateLoading(true);
				// Set specific loading states for different fields
				if (field === "ltb") {
					console.log(`Setting LTB updating state for ${id}`);
					setUpdatingLtbItems((prev) => ({
						...prev,
						[id]: true,
					}));
				} else if (field === "build_date") {
					console.log(`Setting Build Date updating state for ${id}`);
					setUpdatingBuildDateItems((prev) => ({
						...prev,
						[id]: true,
					}));
				} else if (field === "part_followup_date") {
					console.log(`Setting Part followup updating state for ${id}`);
					setUpdatingPartFollowupItems((prev) => ({
						...prev,
						[id]: true,
					}));
				} else if (field === "vehicle_followup_date") {
					console.log(`Setting Vehicle followup updating state for ${id}`);
					setUpdatingVehicleFollowupItems((prev) => ({
						...prev,
						[id]: true,
					}));
				}
				// Update local state BEFORE calling the API for immediate UI feedback
				updateLocalState(id, field, value);

				// Find the item using kast_key (which is now the id)
				const itemToUpdate = tableData.find((item) => item.kast_key === id);

				// Get the kast_key for this item (which is the same as id now)
				const kastKey = itemToUpdate?.kast_key;
				const idToUse = kastKey || id; // Make the API call directly to update the backend
				// Use different endpoints based on field type
				let response;
				// Get user display name for tracking
				const userName = getUserEmail();
				const headers = {
					"X-User-Name": userName,
				};

				if (
					field === "snr_ma_nahme_status" ||
					field === "fzg_ma_nahme_status"
				) {
					// Status fields use a specific endpoint
					response = await apiClient.post(
						"/api/vff-status-update",
						{
							kast_key: idToUse,
							field: field,
							value: value,
						},
						{ headers },
					);
				} else {
					// Other fields use the general update endpoint
					response = await apiClient.post(
						"/api/update-item",
						{
							id: idToUse,
							field,
							value,
						},
						{ headers },
					);
				}

				if (!response.data) {
					throw new Error("Failed to update item");
				}

				// Call parent's update handler for any additional logic (no API call there now)
				if (onUpdateItem) {
					await onUpdateItem(id, field, value, kastKey);
				}

				console.log(
					`✅ Successfully updated ${field} for item ${id} without page refresh`,
				);
			} catch (error) {
				console.error("Error updating item:", error);

				// Revert local state changes on error
				revertLocalState(id, field); // 403 = access denied — show the modal instead of a raw alert
				if (error.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
					return;
				}

				// Show user-friendly error message
				let userMessage = "Failed to update item. Please try again.";

				if (error.message) {
					if (error.message.includes("ICEBERG_COMMIT_ERROR")) {
						userMessage =
							"⚠️ Unable to save changes - the database is currently being updated by another process. Please wait a moment and try again.";
					} else if (error.message.includes("Failed to commit")) {
						userMessage =
							"⚠️ Save operation failed - there may be conflicting changes. Please refresh the page and try again.";
					} else if (error.message.includes("timeout")) {
						userMessage =
							"⏱️ The update is taking longer than expected. Please wait and check if the changes were saved.";
					} else if (error.message.includes("500")) {
						userMessage =
							"🔧 Server error occurred. Please try again in a moment.";
					} else if (error.message.includes("Network")) {
						userMessage =
							"🌐 Network connection issue. Please check your internet connection and try again.";
					}
				}

				alert(userMessage);
			} finally {
				// Clear update flags to allow normal data fetching again
				console.log("🔄 Clearing update flags after update completion:", {
					field,
					id,
				});
				setIsUpdating(false);
				updateInProgress.current = false;
				setUpdateLoading(false);

				// Clear global update state
				setCurrentlyUpdating({
					field: null,
					itemId: null,
					timestamp: null,
				});

				// Clear specific loading states based on field
				if (field === "ltb") {
					console.log(`Clearing LTB updating state for ${id}`);
					setUpdatingLtbItems((prev) => {
						const newState = { ...prev };
						delete newState[id];
						return newState;
					});
				} else if (field === "build_date") {
					console.log(`Clearing Build Date updating state for ${id}`);
					setUpdatingBuildDateItems((prev) => {
						const newState = { ...prev };
						delete newState[id];
						return newState;
					});
				} else if (field === "part_followup_date") {
					console.log(`Clearing Part followup updating state for ${id}`);
					setUpdatingPartFollowupItems((prev) => {
						const newState = { ...prev };
						delete newState[id];
						return newState;
					});
				} else if (field === "vehicle_followup_date") {
					console.log(`Clearing Vehicle followup updating state for ${id}`);
					setUpdatingVehicleFollowupItems((prev) => {
						const newState = { ...prev };
						delete newState[id];
						return newState;
					});
				}
			}
		};

		// Handle flagging toggle
		const handleFlaggingToggle = async (id, currentValue) => {
			// Check authorization first
			if (!isUserAuthorizedForResponsibleFields(getUserEmail)) {
				setShowAccessDeniedModal(true);
				return;
			}

			// Check for concurrent updates from other fields before proceeding
			if (isAnyUpdateInProgress()) {
				showConcurrentUpdateWarning("Flagging toggle");
				return;
			}

			// Check if there are ongoing flag update operations
			const ongoingUpdates = Object.keys(updatingFlagging).length > 0;

			// If not in edit mode and there are multiple items being updated simultaneously
			if (!isEditMode && ongoingUpdates) {
				console.log(
					"🚫 Preventing simultaneous flag updates. Current operations:",
					Object.keys(updatingFlagging),
				);
				setFlagUpdateModal({
					open: true,
					type: "simultaneous",
					message:
						"Multiple flag updates are currently in progress. Please wait for them to complete before making additional changes, or switch to Edit Mode for bulk operations.",
				});
				return;
			}

			// Track frequency of flag operations to suggest edit mode (only in normal mode)
			if (!isEditMode) {
				const now = Date.now();
				const windowDuration = 30000; // 30 seconds

				// Reset counter if window has expired
				if (now - flagOperationWindow > windowDuration) {
					setFlagOperationCount(1);
					setFlagOperationWindow(now);
				} else {
					const newCount = flagOperationCount + 1;
					setFlagOperationCount(newCount);

					// Suggest edit mode after 5 flag operations within 30 seconds
					if (newCount >= 5) {
						setFlagUpdateModal({
							open: true,
							type: "guide-to-edit",
							message:
								"You're making multiple flag changes. Consider switching to Edit Mode to make bulk changes more efficiently and reduce server load.",
						});
						// Reset counter after showing modal
						setFlagOperationCount(0);
						setFlagOperationWindow(now);
					}
				}
			}

			// If in edit mode, track the change for bulk update instead of immediate update
			if (isEditMode) {
				const newValue = currentValue === "Y" ? "N" : "Y";
				console.log(
					`Tracking flagging change for bulk edit ${id}: ${currentValue} -> ${newValue}`,
				);

				// Find the original flagging value from tableData
				const originalItem = tableData.find((item) => item.kast_key === id);
				const originalValue = originalItem?.flagging || "N";

				// Track changes in bulk edit mode
				if (newValue !== originalValue) {
					onEditedItemsChange?.((prev) => ({
						...prev,
						[id]: {
							...prev[id],
							flagging: newValue,
						},
					}));
				} else {
					// Remove from edited items if value reverted to original
					onEditedItemsChange?.((prev) => {
						const newState = { ...prev };
						if (newState[id]) {
							delete newState[id].flagging;
							if (Object.keys(newState[id]).length === 0) {
								delete newState[id];
							}
						}
						return newState;
					});
				}

				// Update local display state immediately for visual feedback
				setTableData((prev) =>
					prev.map((item) => {
						if (item.kast_key === id) {
							return { ...item, flagging: newValue };
						}
						return item;
					}),
				);

				return; // Don't proceed to API call in edit mode
			} // Normal mode: immediate update
			const newValue = currentValue === "Y" ? "N" : "Y";

			try {
				// Set update flags to prevent page refresh
				setIsUpdating(true);
				updateInProgress.current = true;
				setUpdatingFlagging((prev) => ({ ...prev, [id]: true }));

				console.log(
					`Toggling flagging for ${id}: ${currentValue} -> ${newValue}`,
				);

				// Update local state immediately for UI feedback
				setTableData((prev) =>
					prev.map((item) => {
						if (item.kast_key === id) {
							return { ...item, flagging: newValue };
						}
						return item;
					}),
				);
				const response = await apiClient.post(
					"/api/update-flagging",
					{
						kast_key: id,
						value: newValue,
					},
					{
						headers: {
							"X-User-Name": getUserEmail(),
						},
					},
				);
				if (response.data) {
					console.log(`✅ Flagging updated successfully for ${id}`);
				} else {
					console.error(`❌ Error updating flagging:`, response.status);
					alert("Error updating flag status. Please try again.");
				}
			} catch (error) {
				console.error(`❌ Error updating flagging:`, error);
				if (error.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
				} else {
					alert("Error updating flag status. Please try again.");
				}

				// Revert local state on error
				const revertValue = newValue === "Y" ? "N" : "Y";
				setTableData((prev) =>
					prev.map((item) => {
						if (item.kast_key === id) {
							return { ...item, flagging: revertValue };
						}
						return item;
					}),
				);
			} finally {
				// Clear update flags to allow normal data fetching
				setIsUpdating(false);
				updateInProgress.current = false;
				setUpdatingFlagging((prev) => {
					const newState = { ...prev };
					delete newState[id];
					return newState;
				});
			}
		}; // Handle archive dropdown change
		const handleArchiveChange = async (id, newValue) => {
			// Check authorization first
			if (!isUserAuthorizedForResponsibleFields(getUserEmail)) {
				setShowAccessDeniedModal(true);
				return;
			}

			// Check for concurrent updates before proceeding
			if (isAnyUpdateInProgress()) {
				showConcurrentUpdateWarning("Archive status");
				return;
			}

			try {
				// Set update flags to prevent page refresh
				setIsUpdating(true);
				updateInProgress.current = true;
				setUpdatingArchive((prev) => ({ ...prev, [id]: true }));

				console.log(`Updating archive for ${id} to: ${newValue}`);

				// Update local state immediately for UI feedback
				setTableData((prev) =>
					prev.map((item) => {
						if (item.kast_key === id) {
							return { ...item, archive: newValue };
						}
						return item;
					}),
				);
				const response = await apiClient.post(
					"/api/update-archive",
					{
						kast_key: id,
						value: newValue,
					},
					{
						headers: {
							"X-User-Name": getUserEmail(),
						},
					},
				);

				if (response.data) {
					console.log(`✅ Archive updated successfully for ${id}`);
				} else {
					console.error(`❌ Error updating archive:`, response.status);
					alert("Error updating archive status. Please try again.");
				}
			} catch (error) {
				console.error(`❌ Error updating archive:`, error);
				if (error.response?.status === 403) {
					refreshAccess();
					setShowAccessDeniedModal(true);
				} else {
					alert("Error updating archive status. Please try again.");
				}
				// Revert local state on error - need to know original value
				// For now, just show the error without reverting
			} finally {
				// Clear update flags to allow normal data fetching
				setIsUpdating(false);
				updateInProgress.current = false;
				setUpdatingArchive((prev) => {
					const newState = { ...prev };
					delete newState[id];
					return newState;
				});
			}
		};

		// Bulk update functions for flagging and archive
		const handleBulkFlaggingUpdate = async (flagValue) => {
			if (selectedItems.length === 0) {
				alert("Please select items to update.");
				return;
			}

			try {
				const kastKeys = selectedItems.map((item) => item.id);
				console.log(`Bulk updating flagging to ${flagValue} for:`, kastKeys); // Add user information to headers for tracking
				const headers = {
					"X-User-Name": getUserEmail() || "Unknown User",
				};

				const response = await apiClient.post(
					"/api/bulk-update-flagging",
					{
						updates: kastKeys.map((kastKey) => ({
							kast_key: kastKey,
							value: flagValue,
						})),
					},
					{ headers },
				);

				if (response.data) {
					// Update local state for all selected items
					setTableData((prev) =>
						prev.map((item) => {
							if (kastKeys.includes(item.kast_key)) {
								return { ...item, flagging: flagValue };
							}
							return item;
						}),
					);
					console.log(
						`✅ Bulk flagging update successful for ${kastKeys.length} items`,
					);

					// Clear selection after successful update
					onSelectItems?.([]);
					setAllSelected(false);
				} else {
					console.error(`❌ Error bulk updating flagging:`, response.status);
					alert("Error updating flag status. Please try again.");
				}
			} catch (error) {
				console.error(`❌ Error bulk updating flagging:`, error);
				alert("Error updating flag status. Please try again.");
			}
		};

		const handleBulkArchiveUpdate = async (archiveValue) => {
			if (selectedItems.length === 0) {
				alert("Please select items to update.");
				return;
			}

			try {
				const kastKeys = selectedItems.map((item) => item.id);
				console.log(`Bulk updating archive to ${archiveValue} for:`, kastKeys);
				const headers = {
					"X-User-Name": getUserEmail() || "Unknown User",
				};

				const response = await apiClient.post(
					"/api/bulk-update-archive",
					{
						updates: kastKeys.map((kastKey) => ({
							kast_key: kastKey,
							value: archiveValue,
						})),
					},
					{ headers },
				);

				if (response.data) {
					// Update local state for all selected items
					setTableData((prev) =>
						prev.map((item) => {
							if (kastKeys.includes(item.kast_key)) {
								return { ...item, archive: archiveValue };
							}
							return item;
						}),
					);
					console.log(
						`✅ Bulk archive update successful for ${kastKeys.length} items`,
					);

					// Clear selection after successful update
					onSelectItems?.([]);
					setAllSelected(false);
				} else {
					console.error(`❌ Error bulk updating archive:`, response.status);
					alert("Error updating archive status. Please try again.");
				}
			} catch (error) {
				console.error(`❌ Error bulk updating archive:`, error);
				alert("Error updating archive status. Please try again.");
			}
		};
		// Expose methods to parent
		useImperativeHandle(ref, () => ({
			getCurrentPageData: () => [...tableData],
			fetchData: (filters = {}, page = 1, perPage = 100, search = "") => {
				// Reset data for new search/filter and load first page with initial load size
				setTableData([]);
				setCurrentPage(1);
				setHasMoreData(true);
				// Set flag to prevent useEffect from triggering duplicate fetch
				isStateTriggeredFetch.current = true;
				return fetchData(filters, 1, itemsPerPage, search, false);
			},
			getCurrentItemsPerPage: () => itemsPerPage, // Allow parent to get the current value
			setParentItemsPerPage: () => {}, // This will be overridden by the parent component
			setStateTriggerFlag: (value) => {
				isStateTriggeredFetch.current = value;
			},
			resetDashboardFilterFlag: () => {
				isDashboardFilterActive.current = false;
				console.log(
					"🔓 Dashboard filter flag reset - normal operations enabled",
				);
			},
			// Expose bulk update functions
			handleBulkFlaggingUpdate,
			handleBulkArchiveUpdate,
			// Patch specific rows in-place after a bulk edit — no scroll reset, no full reload.
			// changes keys mirror what VFFBulkEditModal.handleSave produces:
			//   status_btt, ltb_date, build_date, part_followup_date, part_responsible,
			//   snr_ma_nahme_status, archive, flagging, part_comment, user_name
			updateLocalData: (selectedItems, changes) => {
				const selectedKeySet = new Set(selectedItems.map((i) => i.kast_key));

				// 1. Patch tableData rows in-place
				setTableData((prevData) =>
					prevData.map((row) => {
						if (!selectedKeySet.has(row.kast_key)) return row;
						const updatedRow = { ...row };
						if ("status_btt" in changes)
							updatedRow.status_btt = changes.status_btt;
						if ("ltb_date" in changes) {
							updatedRow.ltb = changes.ltb_date;
							updatedRow.ltb_date = changes.ltb_date;
						}
						if ("build_date" in changes)
							updatedRow.build_date = changes.build_date;
						if ("part_followup_date" in changes)
							updatedRow.part_followup_date = changes.part_followup_date;
						if ("part_responsible" in changes)
							updatedRow.part_responsible = changes.part_responsible;
						if ("snr_ma_nahme_status" in changes)
							updatedRow.snr_ma_nahme_status = changes.snr_ma_nahme_status;
						if ("archive" in changes) updatedRow.archive = changes.archive;
						if ("flagging" in changes) updatedRow.flagging = changes.flagging;
						if ("tags" in changes) updatedRow.tags = changes.tags || "";
						return updatedRow;
					}),
				);

				// 2. Sync auxiliary date state maps for immediate picker UI feedback
				if ("part_followup_date" in changes) {
					setPartFollowupDates((prev) => {
						const next = { ...prev };
						selectedItems.forEach((item) => {
							next[item.kast_key] = changes.part_followup_date;
						});
						return next;
					});
				}
				if ("build_date" in changes) {
					setBuildDateDates((prev) => {
						const next = { ...prev };
						selectedItems.forEach((item) => {
							next[item.kast_key] = changes.build_date;
						});
						return next;
					});
				}

				// 3. Update commentsData directly — no API round-trip needed
				if (changes.part_comment) {
					const now = new Date().toISOString();
					const commentUser =
						changes.user_name ||
						(getUserDisplayName ? getUserDisplayName() : "");
					setCommentsData((prev) => {
						const next = { ...prev };
						selectedItems.forEach((item) => {
							next[item.kast_key] = {
								...prev[item.kast_key],
								latest_part_comment: changes.part_comment,
								latest_part_comment_date: now,
								latest_part_comment_user: commentUser,
							};
						});
						return next;
					});
				}

				console.log(
					`✅ VFFDataTable: Patched ${selectedItems.length} rows in-place (no scroll reset)`,
				);
			}, // Clear all selections
			clearAllSelections: () => {
				setAllSelected(false);
				onSelectItems?.([]);
				console.log("🔄 VFFDataTable: All selections cleared");
			},
			// Reset sorting to default state
			resetSorting: () => {
				setSortBy(null);
				setSortOrder("asc");
				setSecondarySortBy(null);
				setSecondarySortOrder("asc");
				console.log("🔄 Compound sorting reset to default state");
			},

			// Update local data after edit mode save to ensure immediate UI updates
			updateLocalDataFromEditMode: (editedItems) => {
				console.log("🔄 Updating local data from edit mode:", editedItems);

				// Update the main table data
				setTableData((prevData) => {
					return prevData.map((row) => {
						const editedItem = editedItems[row.kast_key];
						if (editedItem) {
							// Apply all the edited changes to this row
							const updatedRow = { ...row };

							Object.keys(editedItem).forEach((field) => {
								updatedRow[field] = editedItem[field];
							});

							return updatedRow;
						}
						return row;
					});
				});

				// Update the specific date state variables for immediate UI updates
				Object.entries(editedItems).forEach(([kastKey, changes]) => {
					if (changes.part_followup_date) {
						setPartFollowupDates((prev) => ({
							...prev,
							[kastKey]: changes.part_followup_date,
						}));
					}

					if (changes.vehicle_followup_date) {
						setVehicleFollowupDates((prev) => ({
							...prev,
							[kastKey]: changes.vehicle_followup_date,
						}));
					}
				});

				console.log(
					"✅ Local data updated from edit mode - dates should show immediately",
				);
			},
		})); // Infinite scroll handler
		const handleScroll = React.useCallback(
			(e) => {
				// Close any open Status BTT dropdowns when scrolling
				const allDropdowns = document.querySelectorAll(
					'[id^="status-btt-dropdown-"]',
				);
				allDropdowns.forEach((dropdown) => {
					if (dropdown.style.display !== "none") {
						dropdown.style.display = "none";
						// Clear temporary selections when closing dropdown due to scroll
						const dropdownId = dropdown.id;
						const itemId = dropdownId.replace("status-btt-dropdown-", "");
						setTempStatusBTTSelections((prev) => {
							const newState = { ...prev };
							delete newState[itemId];
							return newState;
						});
					}
				});

				// Close any open Archive dropdowns when scrolling
				const allArchiveDropdowns = document.querySelectorAll(
					'[id^="archive-dropdown-"]',
				);
				allArchiveDropdowns.forEach((dropdown) => {
					if (dropdown.style.display !== "none") {
						dropdown.style.display = "none";
					}
				});
				if (isLoadingMore || !hasMoreData || isLoading) return;

				const container = e.target;
				const scrollTop = container.scrollTop;
				const scrollHeight = container.scrollHeight;
				const clientHeight = container.clientHeight;

				// Only proceed if there's actually scrollable content
				if (scrollHeight <= clientHeight) return;

				// Trigger load more when user scrolls to within 200px of bottom (reduced threshold for better UX)
				const threshold = 200;
				const remainingScroll = scrollHeight - scrollTop - clientHeight;
				// Debug logging when close to bottom
				if (remainingScroll < 500) {
					// Scroll position tracking (debug info available if needed)
				}

				if (remainingScroll < threshold) {
					console.log("🔄 Infinite scroll triggered - Loading more data...", {
						scrollTop,
						scrollHeight,
						clientHeight,
						remaining: remainingScroll,
						threshold,
						currentPage,
						hasMoreData,
					});

					const nextPage = currentPage + 1;
					setCurrentPage(nextPage);
					// Keep consistent page size for proper pagination
					fetchData(activeFilters, nextPage, itemsPerPage, searchQuery, true);
				}
			},
			[
				isLoadingMore,
				hasMoreData,
				isLoading,
				currentPage,
				activeFilters,
				searchQuery,
				itemsPerPage,
				totalPages,
				tableData,
				setTempStatusBTTSelections,
			],
		);
		// Load more data function for manual trigger (optional)
		const loadMoreData = React.useCallback(() => {
			if (isLoadingMore || !hasMoreData || isLoading) return;
			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			// Keep consistent page size for proper pagination
			fetchData(activeFilters, nextPage, itemsPerPage, searchQuery, true);
		}, [
			isLoadingMore,
			hasMoreData,
			isLoading,
			currentPage,
			activeFilters,
			searchQuery,
			itemsPerPage,
		]);
		const fetchData = async (
			filters = {},
			page = 1,
			perPage = 100,
			search = "",
			isLoadMore = false,
			overrideSortBy = null,
			overrideSortOrder = null,
			overrideSecondarySortBy = null,
			overrideSecondarySortOrder = null,
		) => {
			try {
				if (!isLoadMore) {
					setIsLoading(true);
					setError(null);
				} else {
					setIsLoadingMore(true);
				}

				// Use override sort values if provided, otherwise use state values
				const currentSortBy = overrideSortBy !== null ? overrideSortBy : sortBy;
				const currentSortOrder =
					overrideSortOrder !== null ? overrideSortOrder : sortOrder;
				const currentSecondarySortBy =
					overrideSecondarySortBy !== null
						? overrideSecondarySortBy
						: secondarySortBy;
				const currentSecondarySortOrder =
					overrideSecondarySortOrder !== null
						? overrideSecondarySortOrder
						: secondarySortOrder;

				console.log("📡 fetchData called with compound sort:", {
					primary: { currentSortBy, currentSortOrder },
					secondary: { currentSecondarySortBy, currentSecondarySortOrder },
					overrides: {
						overrideSortBy,
						overrideSortOrder,
						overrideSecondarySortBy,
						overrideSecondarySortOrder,
					},
				});

				// Update activeFilters with the new filters if provided
				// If filters is empty, use the existing activeFilters
				const currentFilters =
					Object.keys(filters).length > 0 ? filters : activeFilters;
				if (!isLoadMore) {
					setActiveFilters(currentFilters);
				}
				// Update itemsPerPage if a different value is passed
				if (perPage !== itemsPerPage && !isLoadMore) {
					console.log(
						`Updating items per page from ${itemsPerPage} to ${perPage}`,
					);
					setItemsPerPage(perPage);
				}

				// Detect if this is a dashboard filter request (has vehicle + status color filters)
				const isDashboardFilter =
					currentFilters.fzgNr?.length > 0 &&
					currentFilters.statusColor?.length > 0;
				if (isDashboardFilter) {
					console.log(
						"🎯 Dashboard filters detected, setting dashboard filter flag",
					);
					isDashboardFilterActive.current = true;
					// Reset the flag after a delay to allow for the data to be loaded
					setTimeout(() => {
						isDashboardFilterActive.current = false;
						console.log("🎯 Dashboard filter flag reset");
					}, 5000); // 5 second delay
				}

				// Create payload for POST request similar to PTSachNrDataTable
				const payload = {
					programm: currentFilters.programm || [],
					projectNumber: currentFilters.projectNumber || [],
					fzgNumber: currentFilters.fzgNr || [],
					bza: currentFilters.bza || [],
					partNo: currentFilters.partNo || [],
					partNoPrefix: currentFilters.partNoPrefix || [],
					supplier: currentFilters.supplier || [],
					baukastenNo: currentFilters.baukastenNo || [],
					purchasingStatus: currentFilters.purchasing || [],
					logistics1Status: currentFilters.logistics1 || [],
					logistics2Status: currentFilters.logistics2 || [],
					responsibleParts: currentFilters.responsibleParts || [],
					responsibleVehicle: currentFilters.responsibleVehicle || [],
					partsStatus: currentFilters.partsStatus || [],
					vehicleStatus: currentFilters.vehicleStatus || [],
					seTeam: currentFilters.seTeam || [],
					mbKey: currentFilters.mbKey || [],
					tags: currentFilters.tags || [],
					statusColor: currentFilters.statusColor || [],
					statusBtt: currentFilters.statusBtt || [],
					kategorieTeilenanflug: currentFilters.kategorieTeilenanflug || [],
					ruckmeldeStat: currentFilters.ruckmeldeStat || [],
					alg: currentFilters.alg || [],
					petFrom: currentFilters.pet?.from || null,
					petTo: currentFilters.pet?.to || null,
					changeNo: currentFilters.changeNo || [],
					kastKeyAnlage: currentFilters.kastKeyAnlage || [],
					anlo: currentFilters.anlo || [],
					vkgrArt: currentFilters.vkgrArt || [],
					benenn: currentFilters.benenn || [],
					vkgrBend: currentFilters.vkgrBend || [],
					flagging: currentFilters.flagging || [],
					archive: currentFilters.archive || [],
					trafficLight: currentFilters.trafficLight || [],
					kastKeys: currentFilters.kastKeys || [],
					requirementDateFrom: currentFilters.requirementDateFrom || null,
					requirementDateTo: currentFilters.requirementDateTo || null,
					partsFollowupDateFrom: currentFilters.partsFollowupDateFrom || null,
					partsFollowupDateTo: currentFilters.partsFollowupDateTo || null,
					vehicleFollowupDateFrom:
						currentFilters.vehicleFollowupDateFrom || null,
					vehicleFollowupDateTo: currentFilters.vehicleFollowupDateTo || null,
					ltbDateFrom: currentFilters.ltbDateFrom || null,
					ltbDateTo: currentFilters.ltbDateTo || null,
					search: currentFilters.globalSearch || "",
					sort_by: currentSortBy,
					sort_order: currentSortOrder,
					secondary_sort_by: currentSecondarySortBy,
					secondary_sort_order: currentSecondarySortOrder,
					page: Number(page) || 1,
					items_per_page: Number(perPage) || 100,
				};

				console.log("Final search term being sent to API:", payload.search);
				console.log("🔄 Compound sorting parameters in payload:", {
					primary: { sort_by: payload.sort_by, sort_order: payload.sort_order },
					secondary: {
						secondary_sort_by: payload.secondary_sort_by,
						secondary_sort_order: payload.secondary_sort_order,
					},
				});

				console.log("Fetching VFF data with payload:", payload);
				// Generate a request ID for tracking
				const requestId = Math.random().toString(36).substr(2, 9);
				console.log("🔍 VFF REQUEST:", {
					requestId,
					page,
					items_per_page: perPage,
					isLoadMore,
					filters: {
						project: payload.project,
						fzgNumber: payload.fzgNumber,
						bza: payload.bza,
						purchasingStatus: payload.purchasingStatus,
						logistics1Status: payload.logistics1Status,
						logistics2Status: payload.logistics2Status,
						statusColor: payload.statusColor,
						kategorieTeilenanflug: payload.kategorieTeilenanflug,
						ruckmeldeStat: payload.ruckmeldeStat,
						alg: payload.alg,
						petFrom: payload.petFrom,
						petTo: payload.petTo,
						flagging: payload.flagging,
						archive: payload.archive,
						requirementDateFrom: payload.requirementDateFrom,
						requirementDateTo: payload.requirementDateTo,
						partsFollowupDateFrom: payload.partsFollowupDateFrom,
						partsFollowupDateTo: payload.partsFollowupDateTo,
						vehicleFollowupDateFrom: payload.vehicleFollowupDateFrom,
						vehicleFollowupDateTo: payload.vehicleFollowupDateTo,
						ltbDateFrom: payload.ltbDateFrom,
						ltbDateTo: payload.ltbDateTo,
					},
					timestamp: new Date().toISOString(),
				});

				const response = await apiClient.post("/api/vff-data", payload);
				const pagination = response.data.pagination || {
					total_items: 0,
					total_pages: 1,
				};
				const data = response.data.data || [];
				console.log("VFF DataTable - Full API response:", response.data);
				console.log("VFF DataTable - API Response data sample:", data[0]);
				if (data[0]) {
					console.log("VFF DataTable - mb_key in API data:", data[0].mb_key);
					console.log(
						"VFF DataTable - baukasten_no in API data:",
						data[0].baukasten_no,
					);
					console.log("VFF DataTable - old_snr in API data:", data[0].old_snr);
					console.log("VFF DataTable - new_snr in API data:", data[0].new_snr);
					console.log(
						"VFF DataTable - change_no in API data:",
						data[0].change_no,
					);
					console.log(
						"VFF DataTable - kast_key_anlage in API data:",
						data[0].kast_key_anlage,
					);
					console.log(
						"VFF DataTable - All keys in first record:",
						Object.keys(data[0]),
					);
				}
				const newCommentsData = {};
				data.forEach((item) => {
					if (item.kast_key) {
						newCommentsData[item.kast_key] = {
							// Part comments
							latest_part_comment: item.latest_part_comment || "",
							latest_part_comment_date: item.latest_part_comment_date || "",
							latest_part_comment_user: item.latest_part_comment_user || "",
							// Vehicle comments
							latest_vehicle_comment: item.latest_vehicle_comment || "",
							latest_vehicle_comment_date:
								item.latest_vehicle_comment_date || "",
							latest_vehicle_comment_user:
								item.latest_vehicle_comment_user || "",
						};
					}
				});

				if (isLoadMore) {
					// Append new data to existing data for infinite scroll
					console.log(
						`📄 Appending ${data.length} new records to existing ${tableData.length} records`,
					);
					setTableData((prevData) => [...prevData, ...data]);
					setHasMoreData(page < pagination.total_pages);
					console.log(
						`🔍 Infinite scroll status: page ${page}/${pagination.total_pages}, hasMore: ${page < pagination.total_pages}`,
					);

					// Merge new comments data with existing
					setCommentsData((prev) => ({ ...prev, ...newCommentsData }));
				} else {
					// Replace data for new search/filter
					console.log(
						`🔄 Loading fresh data: ${data.length} records, total pages: ${pagination.total_pages}`,
					);
					setTableData(data);
					setHasMoreData(pagination.total_pages > 1);
					setAllSelected(false);
					onSelectItems?.([]);

					// Replace comments data for fresh load
					setCommentsData(newCommentsData);
				}

				setTotalRecords(pagination.total_items || data.length);
				setTotalPages(Math.max(1, pagination.total_pages || 1));
				setCurrentPage(page);
			} catch (err) {
				console.error("Error fetching VFF data:", err);
				setError("Failed to load data. Please try again later.");
				if (!isLoadMore) {
					setTableData([]);
					setTotalRecords(0);
					setTotalPages(1);
					setHasMoreData(false);
					setAllSelected(false);
					onSelectItems?.([]);
				}
			} finally {
				if (!isLoadMore) {
					setIsLoading(false);
				} else {
					setIsLoadingMore(false);
				}
			}
		};

		useEffect(() => {
			// Only fetch if not triggered by direct function call and not during updates
			// Prevent fetching during active updates to maintain scroll position
			console.log("🔍 useEffect triggered:", {
				isStateTriggeredFetch: isStateTriggeredFetch.current,
				isUpdating,
				updateInProgress: updateInProgress.current,
				searchQuery,
				isDashboardFilterActive: isDashboardFilterActive.current,
				initialDataFetched: initialDataFetched.current,
			});

			// Prevent duplicate initial data fetch in React.StrictMode
			if (searchQuery === "" && initialDataFetched.current) {
				console.log("✅ Initial fetch already done, skipping duplicate...");
				return;
			}

			// Don't auto-fetch if dashboard filters are active or if prevented by other flags
			if (
				!isStateTriggeredFetch.current &&
				!isUpdating &&
				!updateInProgress.current &&
				!isDashboardFilterActive.current
			) {
				// Mark as fetched for initial load (empty search query)
				if (searchQuery === "") {
					initialDataFetched.current = true;
				}

				// Reset data and fetch first page only
				setTableData([]);
				setCurrentPage(1);
				setHasMoreData(true);
				// Initial load: using itemsPerPage for consistent loading
				fetchData(activeFilters, 1, itemsPerPage, searchQuery, false);
			} else {
				console.log(
					"✅ Fetch prevented by update flags or dashboard filter active",
				);
			}

			// Reset the flag after the effect runs
			isStateTriggeredFetch.current = false;
		}, [searchQuery]); // Removed activeFilters and itemsPerPage from dependencies to prevent duplicate calls

		// Initialize dates when tableData changes
		useEffect(() => {
			// Create objects with item IDs as keys and date values
			const initialLtbDates = {};
			const initialPartFollowupDates = {};
			const initialVehicleFollowupDates = {};

			tableData.forEach((item) => {
				const itemId = item.kast_key; // Use kast_key as the ID

				// Process LTB dates
				if (item.ltb && item.ltb !== "null" && item.ltb !== "") {
					try {
						const dateObj = new Date(item.ltb);
						if (!isNaN(dateObj.getTime())) {
							initialLtbDates[itemId] = item.ltb;
						}
					} catch (error) {
						console.error(`Error parsing LTB date for ${itemId}:`, error);
					}
				}
				// Process Part Followup dates
				if (
					item.part_followup_date &&
					item.part_followup_date !== "-" &&
					item.part_followup_date !== "N/A"
				) {
					try {
						const dateObj = new Date(item.part_followup_date);
						if (!isNaN(dateObj.getTime())) {
							initialPartFollowupDates[itemId] = item.part_followup_date;
						}
					} catch (error) {
						console.error(
							`Error parsing Part followup date for ${itemId}:`,
							error,
						);
					}
				}
				// Process Vehicle Followup dates
				if (
					item.vehicle_followup_date &&
					item.vehicle_followup_date !== "-" &&
					item.vehicle_followup_date !== "N/A"
				) {
					try {
						// Check if it's a numeric timestamp
						if (
							!isNaN(item.vehicle_followup_date) &&
							typeof item.vehicle_followup_date === "number"
						) {
							const timestamp = parseFloat(item.vehicle_followup_date);
							const dateObj = new Date(timestamp * 1000); // Convert seconds to milliseconds
							if (!isNaN(dateObj.getTime())) {
								initialVehicleFollowupDates[itemId] = dateObj.toISOString();
							}
						} else {
							// Handle as regular date string
							const dateObj = new Date(item.vehicle_followup_date);
							if (!isNaN(dateObj.getTime())) {
								initialVehicleFollowupDates[itemId] =
									item.vehicle_followup_date;
							}
						}
					} catch (error) {
						console.error(
							`Error parsing Vehicle followup date for ${itemId}:`,
							error,
						);
					}
				}
			});
			setLtbDates(initialLtbDates);
			setPartFollowupDates(initialPartFollowupDates);
			setVehicleFollowupDates(initialVehicleFollowupDates);
		}, [tableData]);

		// Sync allSelected statesss
		useEffect(() => {
			const allCurrentPageItemsSelected = tableData.every((item) => {
				const itemId = item.kast_key; // Use kast_key as unique identifier
				return selectedItems.some((selected) => selected.id === itemId);
			});
			setAllSelected(allCurrentPageItemsSelected && tableData.length > 0);
		}, [selectedItems, tableData]);
		const handleSearch = (e) => {
			e.preventDefault();
			// Set the flag to prevent the useEffect from triggering a duplicate fetch
			isStateTriggeredFetch.current = true;
			setCurrentPage(1);
			setTableData([]);
			setHasMoreData(true);
			fetchData(activeFilters, 1, itemsPerPage, searchQuery, false);
		};
		const handleItemsPerPageChange = (newItemsPerPage) => {
			// This function is kept for compatibility but not used by dropdown anymore
			// Fixed at 100 records per load
			// Set the flag to prevent the useEffect from triggering a duplicate fetch
			isStateTriggeredFetch.current = true;
			setItemsPerPage(100);
			setCurrentPage(1);
			setTableData([]);
			setHasMoreData(true);
			fetchData(activeFilters, 1, 100, searchQuery, false);
		};

		const nextPage = () => {
			if (currentPage < totalPages) {
				// Set the flag to prevent the useEffect from triggering a duplicate fetch
				isStateTriggeredFetch.current = true;
				setCurrentPage(currentPage + 1);
				fetchData(activeFilters, currentPage + 1, itemsPerPage, searchQuery);
			}
		};

		const prevPage = () => {
			if (currentPage > 1) {
				// Set the flag to prevent the useEffect from triggering a duplicate fetch
				isStateTriggeredFetch.current = true;
				setCurrentPage(currentPage - 1);
				fetchData(activeFilters, currentPage - 1, itemsPerPage, searchQuery);
			}
		};
		const formatData = (data) => {
			// Debug logging for first few items
			if (data.length > 0) {
				console.log("Raw data sample (first item):", {
					required: data[0].required,
					total_menge: data[0].total_menge,
					kast_key: data[0].kast_key,
				});
			}
			return data.map((item, index) => ({
				id: item.vehicle_no + item.part_no,
				snr: index + 1,
				vehicle_no: item.vehicle_no || "-",
				part_no: item.part_no || "-",
				part_description: item.part_description || "-",
				project: item.project || "-",
				programm: item.programm || "-",
				supplier: item.supplier || "-",
				purchase_type: item.purchase_type || "-",
				met: formatMETDate(item.met) || 0,
				ltb: item.ltb || null,
				ltb_date: item.ltb || null,
				build_date: item.build_date || null,
				required: formatToWholeNumber(item.required) || 0,
				total_menge: formatToWholeNumber(item.total_menge) || 0,
				munich: formatToWholeNumber(item.munich) || 0,
				// Munich stock breakdown (keep null for dypp so we can detect "has breakdown")
				munich_dypp: formatToWholeNumber(item.munich_dypp),
				munich_lager: formatToWholeNumber(item.munich_lager) || 0,
				cracow: formatToWholeNumber(item.cracow) || 0,
				total_stock_mtb: formatToWholeNumber(item.total_stock_mtb) || 0,
				required_date: item.required_date || "N/A",
				anlo: item.anlo || "-",
				vkgr_art: item.vkgr_art || "-",
				benenn: item.benenn || "-",
				vkgr_bend: item.vkgr_bend || "-",
				kast_key: item.kast_key || "-",
				kast_key_anlage: item.kast_key_anlage || "-",
				mb_key: item.mb_key || "-",
				baukasten_no: item.baukasten_no || "-",
				projektnummer: item.projektnummer || "",
				old_snr: item.old_snr || "",
				new_snr: item.new_snr || "",
				tags: item.tags || "",
				sales_group: item.sales_group || "-",
				alg: item.alg || "-",
				pet: item.pet || "-",
				change_no: item.change_no || "-",
				kast_key_anlage: item.kast_key_anlage || "-",
				supplier_status: item.supplier_status || "-",
				dates_status: item.dates_status || "-",
				stock_status: item.stock_status || "-",
				part_responsible: item.part_responsible || "-",
				part_followup_date: item.part_followup_date
					? item.part_followup_date.split(" ")[0]
					: "-",
				vehicle_responsible: item.vehicle_responsible || "-",
				status_color: item.status_color || "-",
				kategorie_teileanflug: item.kategorie_teileanflug || "-",
				ruckmelde_stat: item.ruckmelde_stat || "-",
				se_team: item.se_team || "",
				snr_ma_nahme_status: item.snr_ma_nahme_status || null,
				fzg_ma_nahme_status: item.fzg_ma_nahme_status || null,
				vehicle_followup_date: (() => {
					// Handle vehicle_followup_date which could be a numeric timestamp
					const dateValue = item.vehicle_followup_date;
					if (!dateValue || dateValue === "-") return "-";

					try {
						// Check if it's a numeric timestamp (stored as double in the database)
						if (!isNaN(dateValue) && typeof dateValue === "number") {
							// Convert timestamp (seconds since epoch) to date
							const date = new Date(dateValue * 1000); // Multiply by 1000 to convert seconds to milliseconds
							if (!isNaN(date.getTime())) {
								return date.toISOString();
							}
						}
						// If it's already a string date format
						else if (typeof dateValue === "string") {
							return dateValue.split(" ")[0];
						}
					} catch (error) {
						console.error("Error formatting vehicle_followup_date:", error);
					}
					return "-";
				})(),
				flagging: item.flagging || "N", // Add flagging field with default 'N'
				archive: item.archive || "N", // Add archive field with default 'N'
				status_btt: item.status_btt || "", // Add status_btt field
				// Add worklog tracking fields
				comment_dt_track: item.comment_dt_track || null,
				user_name_track: item.user_name_track || null,
			}));
		};
		const displayData = formatData(tableData);

		// Helper function to determine color based on status value
		const getStatusColor = (status) => {
			if (!status || status === "-") return "transparent";
			switch (status.trim().toUpperCase()) {
				case "OK":
					return "#10b981"; // green
				case "NOK":
					return "#9ca3af"; // grey
				case "NRV":
					return "#ef4444"; // red
				default:
					return "transparent";
			}
		}; // Helper function to map status_color values to colors
		const getColorForStatusColor = (statusColor) => {
			if (!statusColor || statusColor === "-") return "transparent";

			// Convert to lowercase for case-insensitive comparison
			const statusValue = statusColor.toString().toLowerCase().trim();
			switch (statusValue) {
				case "available":
					return "#137c13";
				case "red":
					return "#e40045";
				case "yellow":
					return "#ffff00";
				case "blue":
					return "#4b96d2";
				case "orange":
					return "#ffcd00";
				case "light green":
					return "#91b900";
				default:
					return "transparent";
			}
		};

		// Helper function to get kategorie_teileanflug styling
		const getKategorieStyle = (kategorie) => {
			if (!kategorie || kategorie === "-") {
				return {
					backgroundColor: "white",
					color: "black",
				};
			}

			// Convert to uppercase for case-insensitive comparison and get first 3 characters
			const kategorieValue = kategorie
				.toString()
				.toUpperCase()
				.trim()
				.substring(0, 3);

			switch (kategorieValue) {
				case "ANL":
					return {
						backgroundColor: "#FF819C",
						color: "black",
					};
				case "VER":
					return {
						backgroundColor: "#1F947D",
						color: "white",
					};
				case "JIS":
					return {
						backgroundColor: "#763DA8",
						color: "white",
					};
				case "FUN":
					return {
						backgroundColor: "transparent",
						color: "black",
					};
				case "NRV":
					return {
						backgroundColor: "transparent",
						color: "grey",
					};
				case "NUL":
					return {
						backgroundColor: "#CBCECC",
						color: "white",
					};
				default:
					return {
						backgroundColor: "white",
						color: "black",
					};
			}
		};
		return (
			<div
				className="table-container"
				style={{
					backgroundColor: "white",
					display: "flex",
					flexDirection: "column",
					height: "100%",
					position: "relative",
					overflow: "hidden",
					fontFamily: "MAN EUROPE_Regular",
					fontFeatureSettings: '"tnum" 1',
				}}
			>
				<div
					style={{
						overflowY: "auto",
						height: "100%",
						borderBottom: "1px solid #eee",
						overflowX: "auto",
					}}
					onScroll={handleScroll}
				>
					<table
						style={{
							width: "100%",
							fontSize: "14px",
							borderCollapse: "separate",
							borderSpacing: "0 0px",
							tableLayout: "auto", // Change from 'fixed' to 'auto' to allow proper resizing
							minWidth: "1520px", // Increased to accommodate serial number column and build date field
						}}
					>
						<thead
							style={{
								backgroundColor: "#f8f9fa",
								position: "sticky",
								top: 0,
								zIndex: 2,
								borderBottom: "1px solid #e5e7eb",
							}}
						>
							<tr>
								{/* Serial Number Column */}
								<th
									style={{
										padding: "5px",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "20px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									Sr No.
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "center",
										fontWeight: "500",
										color: "#374151",
										width: "110px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
										position: "relative",
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "4px",
											flexWrap: "nowrap",
										}}
									>
										{/* Select-all checkbox — click opens range popover */}
										<div
											style={{
												position: "relative",
												display: "inline-flex",
												alignItems: "center",
											}}
										>
											<input
												ref={checkboxRef}
												type="checkbox"
												checked={allSelected}
												onChange={() => {}}
												onClick={handleSelectAllClick}
												style={{ cursor: "pointer" }}
												title="Click to open selection options (select all or pick a range)"
											/>
											{/* Range Selection Popover */}
											{rangePopoverOpen && (
												<div
													ref={rangePopoverRef}
													style={{
														position: "absolute",
														top: "24px",
														left: "0",
														zIndex: 999,
														backgroundColor: "#fff",
														border: "1px solid #d1d5db",
														borderRadius: "8px",
														boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
														padding: "12px",
														width: "230px",
														fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
													}}
												>
													{/* Header */}
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
															marginBottom: "10px",
														}}
													>
														<span
															style={{
																fontSize: "12px",
																fontWeight: "600",
																color: "#111827",
																fontFamily: "MAN EUROPE_Bold",
															}}
														>
															Select Rows
														</span>
														<button
															onClick={() => setRangePopoverOpen(false)}
															style={{
																background: "none",
																border: "none",
																cursor: "pointer",
																padding: "0",
																color: "#6b7280",
																fontSize: "14px",
																lineHeight: 1,
															}}
															title="Close"
														>
															✕
														</button>
													</div>

													{/* Select All button */}
													<button
														onClick={() => {
															const pageData = tableData.map((item) => ({
																...item,
																id: item.kast_key,
															}));
															onSelectItems?.(pageData);
															setAllSelected(true);
															setRangePopoverOpen(false);
														}}
														style={{
															width: "100%",
															fontSize: "12px",
															padding: "6px 8px",
															backgroundColor: "#1d4ed8",
															color: "white",
															border: "none",
															borderRadius: "5px",
															cursor: "pointer",
															fontFamily: "MAN EUROPE_Bold",
															marginBottom: "10px",
														}}
													>
														{" "}
														Select All Loaded ({tableData.length})
													</button>

													{/* Deselect All button */}
													<button
														onClick={() => {
															onSelectItems?.([]);
															setAllSelected(false);
															setRangePopoverOpen(false);
														}}
														disabled={selectedItems.length === 0}
														style={{
															width: "100%",
															fontSize: "12px",
															padding: "6px 8px",
															backgroundColor:
																selectedItems.length === 0
																	? "#e5e7eb"
																	: "#dc3545",
															color:
																selectedItems.length === 0
																	? "#9ca3af"
																	: "white",
															border: "none",
															borderRadius: "5px",
															cursor:
																selectedItems.length === 0
																	? "not-allowed"
																	: "pointer",
															fontFamily: "MAN EUROPE_Bold",
															marginBottom: "10px",
														}}
													>
														Deselect All ({selectedItems.length})
													</button>

													{/* Divider */}
													<div
														style={{
															borderTop: "1px solid #e5e7eb",
															marginBottom: "10px",
														}}
													/>

													{/* Range label */}
													<div
														style={{
															fontSize: "11px",
															color: "#6b7280",
															marginBottom: "8px",
														}}
													>
														Or select by row number range:
													</div>

													{/* From / To inputs */}
													<div
														style={{
															display: "flex",
															gap: "8px",
															alignItems: "center",
															marginBottom: "8px",
														}}
													>
														<div style={{ flex: 1 }}>
															<label
																style={{
																	fontSize: "10px",
																	color: "#6b7280",
																	display: "block",
																	marginBottom: "2px",
																}}
															>
																From
															</label>
															<input
																type="number"
																min="1"
																max={tableData.length}
																value={rangeFrom}
																onChange={(e) => {
																	setRangeFrom(e.target.value);
																	setRangeError("");
																}}
																onKeyDown={(e) =>
																	e.key === "Enter" &&
																	handleApplyRangeSelection()
																}
																placeholder="e.g. 150"
																style={{
																	width: "100%",
																	padding: "5px 6px",
																	border: "1px solid #d1d5db",
																	borderRadius: "4px",
																	fontSize: "12px",
																	boxSizing: "border-box",
																	outline: "none",
																}}
															/>
														</div>
														<div style={{ flex: 1 }}>
															<label
																style={{
																	fontSize: "10px",
																	color: "#6b7280",
																	display: "block",
																	marginBottom: "2px",
																}}
															>
																To
															</label>
															<input
																type="number"
																min="1"
																max={tableData.length}
																value={rangeTo}
																onChange={(e) => {
																	setRangeTo(e.target.value);
																	setRangeError("");
																}}
																onKeyDown={(e) =>
																	e.key === "Enter" &&
																	handleApplyRangeSelection()
																}
																placeholder="e.g. 175"
																style={{
																	width: "100%",
																	padding: "5px 6px",
																	border: "1px solid #d1d5db",
																	borderRadius: "4px",
																	fontSize: "12px",
																	boxSizing: "border-box",
																	outline: "none",
																}}
															/>
														</div>
													</div>

													{/* Error message */}
													{rangeError && (
														<div
															style={{
																fontSize: "11px",
																color: "#dc2626",
																marginBottom: "8px",
																lineHeight: 1.4,
															}}
														>
															⚠ {rangeError}
														</div>
													)}

													{/* Apply button */}
													<button
														onClick={handleApplyRangeSelection}
														style={{
															width: "100%",
															fontSize: "12px",
															padding: "6px 8px",
															backgroundColor: "#cc0000",
															color: "white",
															border: "none",
															borderRadius: "5px",
															cursor: "pointer",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														Apply Range
													</button>
												</div>
											)}
										</div>

										{selectedItems.length > 0 && (
											<button
												onClick={handleDeselectAll}
												style={{
													fontSize: "12px",
													padding: "3px 7px",
													backgroundColor: "#dc3545",
													color: "white",
													border: "none",
													borderRadius: "3px",
													cursor: "pointer",
													fontFamily: "MAN EUROPE_Bold",
													transition: "background-color 0.2s ease",
													whiteSpace: "nowrap",
												}}
												onMouseOver={(e) =>
													(e.target.style.backgroundColor = "#c82333")
												}
												onMouseOut={(e) =>
													(e.target.style.backgroundColor = "#dc3545")
												}
												title={`Deselect all ${selectedItems.length} selected item${selectedItems.length !== 1 ? "s" : ""}`}
											>
												Clear ({selectedItems.length})
											</button>
										)}
									</div>
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "left",
										fontWeight: "500",
										color: "black",
										width: "60px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
									}}
								></th>
								<th
									style={{
										padding: "3px",
										textAlign: "left",
										fontWeight: "500",
										color: "black",
										width: "50px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
										cursor: "pointer",
										userSelect: "none",
										position: "relative",
									}}
									onClick={() => handleSort("vehicle_no")}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "flex-start",
											gap: "4px",
										}}
									>
										Vehicle
										<div style={{ marginLeft: "2px" }}>
											{sortBy === "vehicle_no" ? (
												// Active sorting - show directional chevron in red
												sortOrder === "asc" ? (
													<ChevronUp size={14} style={{ color: "#cc0000" }} />
												) : (
													<ChevronDown size={14} style={{ color: "#cc0000" }} />
												)
											) : (
												// Default state - show up chevron in black to indicate sorting is available
												<ChevronUp size={14} style={{ color: "#666" }} />
											)}
										</div>
									</div>
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "220px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
										cursor: "pointer",
										userSelect: "none",
										position: "relative",
									}}
									onClick={() => handleSort("part_no")}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "4px",
										}}
									>
										Part Details
										<div style={{ marginLeft: "2px" }}>
											{sortBy === "part_no" ? (
												// Active sorting - show directional chevron in red
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "2px",
													}}
												>
													{sortOrder === "asc" ? (
														<ChevronUp size={14} style={{ color: "#cc0000" }} />
													) : (
														<ChevronDown
															size={14}
															style={{ color: "#cc0000" }}
														/>
													)}
													{secondarySortBy === "required_date" && (
														<span
															style={{
																fontSize: "10px",
																color: "#cc0000",
																fontWeight: "bold",
															}}
														>
															1
														</span>
													)}
												</div>
											) : (
												// Default state - show up chevron in gray to indicate sorting is available
												<ChevronUp size={14} style={{ color: "#666" }} />
											)}
										</div>
									</div>
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "50px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									Supplier
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "80px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
										cursor: "pointer",
										userSelect: "none",
										position: "relative",
									}}
									onClick={() => handleSort("required_date")}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "4px",
										}}
									>
										<Edit2
											size={12}
											style={{ color: "#cc0000", opacity: 0.7 }}
										/>
										Dates
										<div style={{ marginLeft: "2px" }}>
											{secondarySortBy === "required_date" ? (
												// Secondary sorting (compound) - show directional chevron in orange
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "2px",
													}}
												>
													{secondarySortOrder === "asc" ? (
														<ChevronUp size={14} style={{ color: "#ff8800" }} />
													) : (
														<ChevronDown
															size={14}
															style={{ color: "#ff8800" }}
														/>
													)}
													<span
														style={{
															fontSize: "10px",
															color: "#ff8800",
															fontWeight: "bold",
														}}
													>
														2
													</span>
												</div>
											) : sortBy === "required_date" ? (
												// Primary sorting - show directional chevron in red
												sortOrder === "asc" ? (
													<ChevronUp size={14} style={{ color: "#cc0000" }} />
												) : (
													<ChevronDown size={14} style={{ color: "#cc0000" }} />
												)
											) : (
												// Default state - show up chevron in gray to indicate sorting is available
												<ChevronUp size={14} style={{ color: "#666" }} />
											)}
										</div>
									</div>
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "120px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									<Edit2
										size={12}
										style={{
											color: "#cc0000",
											opacity: 0.7,
											position: "relative",
											right: "5px",
										}}
									/>
									Requirements
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "110px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									Stock
								</th>
								<th
									style={{
										padding: "3px",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "140px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
									}}
								>
									<Edit2
										size={12}
										style={{
											color: "#cc0000",
											opacity: 0.7,
											position: "relative",
											right: "5px",
										}}
									/>
									BTT status
								</th>
								<th
									style={{
										padding: "0",
										textAlign: "center",
										fontWeight: "500",
										color: "black",
										width: "80px",
										borderBottom: "1px solid #e5e7eb",
										fontFamily: "MAN EUROPE_Bold",
										fontSize: "14px",
										textTransform: "uppercase",
									}}
								>
									{" "}
									<div
										style={{
											padding: "6px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											gap: "6px",
											textTransform: "none",
										}}
									>
										<Edit2
											size={12}
											style={{ color: "#cc0000", opacity: 0.7 }}
										/>
										Comment
									</div>{" "}
									<div
										style={{
											display: "flex",
											borderTop: "1px solid #e0e0e0",
											fontSize: "12px",
											fontWeight: "500",
											textTransform: "none",
										}}
									>
										<div
											style={{
												flex: 1,
												padding: "2px",
												borderRight: "1px solid #e0e0e0",
												backgroundColor: "#f5f5f5",
												borderLeft: "1px solid #e0e0e0",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: "4px",
											}}
										>
											Parts
										</div>
										{" "}
									</div>{" "}
								</th>
							</tr>
						</thead>
						<tbody style={{ marginTop: "12px" }}>
							{isLoading && (
								<tr>
									<td
										colSpan="10"
										style={{ textAlign: "center", padding: "40px" }}
									>
										<Box
											display="flex"
											flexDirection="column"
											alignItems="center"
											justifyContent="center"
											height="100%"
											minHeight="300px"
										>
											<CircularProgress
												size={60}
												thickness={4}
												sx={{ color: "#cc0000" }}
											/>
											<Typography
												variant="body1"
												style={{ marginTop: "20px", color: "#666" }}
											>
												We're preparing your content... Please hold
											</Typography>
										</Box>
									</td>
								</tr>
							)}
							{!isLoading && error && (
								<tr>
									<td
										colSpan="10"
										style={{
											textAlign: "center",
											padding: "40px",
											color: "red",
										}}
									>
										{error}
									</td>
								</tr>
							)}
							{!isLoading && !error && displayData.length === 0 && (
								<tr>
									<td
										colSpan="10"
										style={{ textAlign: "center", padding: "40px" }}
									>
										No data found. Try adjusting your search query.
									</td>
								</tr>
							)}
							{!isLoading &&
								!error &&
								displayData.length > 0 &&
								displayData.map((row, rowIndex) => {
									const itemId = row.kast_key; // Use kast_key as unique identifier
									const isSelected = selectedItems.some(
										(selected) => selected.id === itemId,
									);
									return (
										<tr
											key={rowIndex}
											style={{
												borderRadius: "8px",
												boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
												marginBottom: "8px",
												minHeight: "40px",
												maxHeight: "80px",
												overflow: "visible",
											}}
										>
											{/* Serial Number Cell */}
											<td
												style={{
													padding: "3px",
													textAlign: "center",
													fontWeight: "500",
													color: "#666",
													fontSize: "13px",
													fontFamily: "MAN EUROPE_Bold",
													borderLeft: "3px solid transparent",
												}}
											>
												{rowIndex + 1}
											</td>
											<td
												style={{
													padding: "3px",
													textAlign: "center",
													borderTopLeftRadius: "8px",
													borderBottomLeftRadius: "8px",
												}}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														alignItems: "center",
														justifyContent: "center",
														gap: "4px",
													}}
												>
													{/* Checkbox and Flag Icon Row */}{" "}
													<div
														style={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															gap: "4px",
														}}
													>
														<input
															type="checkbox"
															checked={isSelected}
															onChange={() => handleSelectItem(row)}
															onDoubleClick={() =>
																handleDoubleClickSelectBySNR(row)
															}
															style={{
																cursor: "pointer",
																width: "14px",
																height: "14px",
															}}
															title="Double-click to select all parts with same part number on this page"
														/>
														{updatingFlagging[itemId] ? (
															<CircularProgress
																size={12}
																thickness={5}
																sx={{ color: "#cc0000" }}
															/>
														) : (
															<button
																onClick={() =>
																	handleFlaggingToggle(
																		itemId,
																		(() => {
																			// Show edited value if available, otherwise original value
																			const editedValue =
																				editedItems[itemId]?.flagging;
																			return editedValue !== undefined
																				? editedValue
																				: row.flagging;
																		})(),
																	)
																}
																disabled={
																	!isUserAuthorizedForResponsibleFields(
																		getUserEmail,
																	)
																}
																style={{
																	background: isEditMode
																		? "rgba(204, 0, 0, 0.1)"
																		: "none",
																	border: isEditMode
																		? "1px solid rgba(204, 0, 0, 0.3)"
																		: "none",
																	borderRadius: isEditMode ? "2px" : "0",
																	cursor: !isUserAuthorizedForResponsibleFields(
																		getUserEmail,
																	)
																		? "not-allowed"
																		: "pointer",
																	padding: "2px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	opacity:
																		!isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		)
																			? 0.6
																			: 1,
																	transition: "all 0.2s ease",
																	position: "relative",
																}}
																title={
																	!isUserAuthorizedForResponsibleFields(
																		getUserEmail,
																	)
																		? "Access restricted - Only authorized users can modify flagging status"
																		: (() => {
																				const editedValue =
																					editedItems[itemId]?.flagging;
																				const currentValue =
																					editedValue !== undefined
																						? editedValue
																						: row.flagging;
																				const modeText = isEditMode
																					? " (Edit mode - click to toggle, changes saved on bulk save)"
																					: " (Click to toggle immediately)";
																				return `Flag status: ${currentValue === "Y" ? "FLAGGED" : "NOT FLAGGED"}${modeText}`;
																			})()
																}
																onMouseEnter={(e) => {
																	if (isEditMode) {
																		e.target.style.background =
																			"rgba(204, 0, 0, 0.2)";
																		e.target.style.borderColor =
																			"rgba(204, 0, 0, 0.5)";
																	}
																}}
																onMouseLeave={(e) => {
																	if (isEditMode) {
																		e.target.style.background =
																			"rgba(204, 0, 0, 0.1)";
																		e.target.style.borderColor =
																			"rgba(204, 0, 0, 0.3)";
																	}
																}}
															>
																<Flag
																	size={14}
																	color={(() => {
																		// Show edited value if available, otherwise original value
																		const editedValue =
																			editedItems[itemId]?.flagging;
																		const currentValue =
																			editedValue !== undefined
																				? editedValue
																				: row.flagging;
																		return currentValue === "Y"
																			? "#ff6b35"
																			: "grey";
																	})()}
																	fill={(() => {
																		// Show edited value if available, otherwise original value
																		const editedValue =
																			editedItems[itemId]?.flagging;
																		const currentValue =
																			editedValue !== undefined
																				? editedValue
																				: row.flagging;
																		return currentValue === "Y"
																			? "#ff6b35"
																			: "none";
																	})()}
																/>
																{/* Show indicator if value was edited */}
																{isEditMode &&
																	editedItems[itemId]?.flagging !==
																		undefined && (
																		<div
																			style={{
																				position: "absolute",
																				top: "-2px",
																				right: "-2px",
																				width: "6px",
																				height: "6px",
																				backgroundColor: "#22c55e",
																				borderRadius: "50%",
																				border: "1px solid white",
																			}}
																		/>
																	)}
															</button>
														)}
													</div>
													{/* Archive Button Row */}
													<div style={{ position: "relative" }}>
														{updatingArchive[itemId] ? (
															<CircularProgress
																size={12}
																thickness={5}
																sx={{ color: "#cc0000" }}
															/>
														) : (
															<>
																{/* Archive Button */}{" "}
																<button
																	data-tour-target="vff-archive-button"
																	onClick={() => {
																		if (
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																		) {
																			setShowAccessDeniedModal(true);
																			return;
																		}
																		const dropdownId = `archive-dropdown-${itemId}`;
																		const dropdown =
																			document.getElementById(dropdownId);
																		if (dropdown) {
																			dropdown.style.display =
																				dropdown.style.display === "none"
																					? "block"
																					: "none";
																		}
																	}}
																	disabled={
																		!isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		)
																	}
																	style={{
																		width: "70px",
																		padding: "2px 4px",
																		fontSize: "9px",
																		border: "1px solid #d1d5db",
																		borderRadius: "3px",
																		backgroundColor:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? "#f3f4f6"
																				: "#f8f9fa",
																		cursor:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? "not-allowed"
																				: "pointer",
																		fontFamily: "MAN EUROPE_Regular",
																		color:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? "#9ca3af"
																				: "#374151",
																		fontWeight: "500",
																		textAlign: "center",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																		opacity:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? 0.6
																				: 1,
																	}}
																	title={
																		!isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		)
																			? "Access restricted - Only authorized users can modify archive status"
																			: `Archive: ${getArchiveDisplayValue(row.archive)}`
																	}
																>
																	{getArchiveDisplayValue(row.archive)}
																</button>
																{/* Archive Options Dropdown */}
																<div
																	id={`archive-dropdown-${itemId}`}
																	style={{
																		position: "absolute",
																		top: "100%",
																		left: "0",
																		backgroundColor: "white",
																		border: "1px solid #e5e7eb",
																		borderRadius: "4px",
																		boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
																		zIndex: 99999,
																		maxHeight: "200px",
																		overflowY: "auto",
																		display: "none",
																		minWidth: "150px",
																	}}
																>
																	{ARCHIVE_OPTIONS.map((option) => (
																		<div
																			key={option}
																			style={{
																				padding: "8px 12px",
																				fontSize: "11px",
																				cursor: "pointer",
																				backgroundColor:
																					getArchiveDisplayValue(
																						row.archive || "N",
																					) === option
																						? "#e6f3ff"
																						: "white",
																				borderBottom:
																					option ===
																					ARCHIVE_OPTIONS[
																						ARCHIVE_OPTIONS.length - 1
																					]
																						? "none"
																						: "1px solid #f1f5f9",
																				fontFamily: "MAN EUROPE_Regular",
																				color:
																					getArchiveDisplayValue(
																						row.archive || "N",
																					) === option
																						? "#1e40af"
																						: "#374151",
																				fontWeight:
																					getArchiveDisplayValue(
																						row.archive || "N",
																					) === option
																						? "600"
																						: "normal",
																			}}
																			onClick={async () => {
																				const backendValue =
																					getArchiveBackendValue(option);
																				await handleArchiveChange(
																					itemId,
																					backendValue,
																				);
																				// Close dropdown after selection
																				const dropdown =
																					document.getElementById(
																						`archive-dropdown-${itemId}`,
																					);
																				if (dropdown)
																					dropdown.style.display = "none";
																			}}
																			onMouseEnter={(e) => {
																				if (
																					getArchiveDisplayValue(
																						row.archive || "N",
																					) !== option
																				) {
																					e.target.style.backgroundColor =
																						"#f8f9fa";
																				}
																			}}
																			onMouseLeave={(e) => {
																				e.target.style.backgroundColor =
																					getArchiveDisplayValue(
																						row.archive || "N",
																					) === option
																						? "#e6f3ff"
																						: "white";
																			}}
																		>
																			{option}
																			{getArchiveDisplayValue(
																				row.archive || "N",
																			) === option && (
																				<span
																					style={{
																						marginLeft: "8px",
																						color: "#22c55e",
																						fontSize: "12px",
																					}}
																				>
																					✓
																				</span>
																			)}
																		</div>
																	))}
																</div>
															</>
														)}
													</div>
												</div>
											</td>
											<td
												style={{
													padding: "5px",
													verticalAlign: "top",
													lineHeight: "1.2",
													textAlign: "left",
													fontFamily: "MAN EUROPE_Regular",
												}}
											>
												{" "}
												<div
													style={{
														display: "flex",
														alignItems: "center",
														gap: "4px",
													}}
												>
													{/* First box - Color based on status_color */}
													<div
														style={{
															width: "14px",
															height: "14px",
															backgroundColor: getColorForStatusColor(
																row.status_color,
															),
															border: "1px solid #d1d5db",
															borderRadius: "2px",
															flexShrink: 0,
														}}
													></div>
													{/* Second box - Text from kategorie_teileanflug */}
													<div
														style={{
															width: "25px",
															height: "14px",
															backgroundColor: getKategorieStyle(
																row.kategorie_teileanflug,
															).backgroundColor,
															color: getKategorieStyle(
																row.kategorie_teileanflug,
															).color,
															border: "1px solid #d1d5db",
															borderRadius: "2px",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															fontSize: "9px",
															fontWeight: "bold",
															flexShrink: 0,
														}}
													>
														{row.kategorie_teileanflug &&
														row.kategorie_teileanflug !== "-"
															? row.kategorie_teileanflug
																	.toString()
																	.substring(0, 3)
															: ""}
													</div>
													{/* Third box - Ruckmelde Status */}
													<div
														style={{
															minWidth: "20px",
															height: "14px",
															backgroundColor:
																row.ruckmelde_stat && row.ruckmelde_stat !== "-"
																	? "#20B155"
																	: "#f8f9fa",
															border: "1px solid #d1d5db",
															borderRadius: "2px",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															fontSize: "8px",
															fontWeight: "bold",
															flexShrink: 0,
															padding: "0 2px",
															color:
																row.ruckmelde_stat && row.ruckmelde_stat !== "-"
																	? "white"
																	: "black",
														}}
													>
														{row.ruckmelde_stat && row.ruckmelde_stat !== "-"
															? row.ruckmelde_stat.toString()
															: ""}
													</div>
												</div>{" "}
												<div
													style={{
														fontSize: "10px",
														marginTop: "4px",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													<span style={{ fontSize: "10px" }}> </span>
													{formatDateToDDMMYYYY(row.kast_key_anlage)}
												</div>{" "}
												{/* More info button */}{" "}
												<button
													data-tour-target="more-info-button"
													onClick={(e) => handleOpenSidePanel(row, e)}
													style={{
														background:
															sidePanelOpen &&
															selectedRowForPanel &&
															selectedRowForPanel.kast_key === row.kast_key
																? "#cc0000"
																: "none",
														border:
															sidePanelOpen &&
															selectedRowForPanel &&
															selectedRowForPanel.kast_key === row.kast_key
																? "1px solid #cc0000"
																: "1px solid #ccc",
														padding: "2px 6px",
														cursor: "pointer",
														fontSize: "12px",
														color:
															sidePanelOpen &&
															selectedRowForPanel &&
															selectedRowForPanel.kast_key === row.kast_key
																? "white"
																: "#666",
														display: "flex",
														alignItems: "center",
														gap: "3px",
														marginTop: "3px",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													{sidePanelOpen &&
													selectedRowForPanel &&
													selectedRowForPanel.kast_key === row.kast_key
														? "Close info"
														: "More info"}
													<ChevronRight size={12} />
												</button>
											</td>
											<td
												style={{
													padding: "5px",
													verticalAlign: "top",
													lineHeight: "1.2",
													textAlign: "left",
													fontFamily: "MAN EUROPE_Regular",
												}}
											>
												<div
													style={{
														fontSize: "12px",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													{row.vehicle_no}
												</div>
												<div
													style={{
														fontSize: "11px",
														marginBottom: "2px",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													{row.anlo}
												</div>
											</td>
											<td
												style={{
													padding: "4px 6px",
													verticalAlign: "top",
													lineHeight: "1.3",
													textAlign: "left",
													fontFamily: "MAN EUROPE_Regular",
												}}
											>
												{" "}
												<div>
													{/* Always visible basic info */}
													<div
														style={{
															fontSize: "15px",
															marginBottom: "4px",
															fontFamily: "MAN EUROPE_Bold",
														}}
													>
														<span
															style={{
																fontSize: "15px",
																fontFamily: "MAN Europe_Bold",
															}}
														></span>
														{row.part_no}
													</div>{" "}
													<div
														style={{
															fontSize: "11px",
															marginBottom: "2px",
															fontFamily: "MAN EUROPE_Regular",
														}}
													>
														<span
															style={{
																fontSize: "12px",
																fontFamily: "MAN Europe_Bold",
															}}
														></span>
														{translateDescription(row.part_description)}
													</div>{" "}
													<div
														style={{
															fontSize: "11px",
															marginTop: "4px",
															fontFamily: "MAN EUROPE_Regular",
															color: "black",
														}}
													>
														<span style={{ fontFamily: "MAN EUROPE_Bold" }}>
															ALG :{" "}
														</span>
														{row.alg || "-"}
													</div>
													<div
														style={{
															fontSize: "11px",
															marginTop: "2px",
															fontFamily: "MAN EUROPE_Regular",
															color: "black",
														}}
													>
														<span style={{ fontFamily: "MAN EUROPE_Bold" }}>
															PET :{" "}
														</span>
														{row.pet || "-"}
													</div>
												</div>
											</td>
											<td
												style={{
													padding: "2px 6px",
													verticalAlign: "top",
													textAlign: "left",
													borderTop:
														"4px solid " + getStatusColor(row.supplier_status),
													borderTopWidth: "4px",
													borderRight: "2px solid transparent",
													borderLeft: "2px solid transparent",
													lineHeight: "1.2",
													position: "relative",
													overflow: "hidden",
													fontFamily: "MAN EUROPE_Regular",
													borderTopLeftRadius: "4px",
													borderTopRightRadius: "4px",
													width: "120px",
													maxWidth: "120px",
													minWidth: "120px",
												}}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														gap: "2px",
														padding: "4px 0",
													}}
												>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
															marginBottom: "2px",
														}}
													>
														{/* <span style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'black',
                          fontFamily: 'MAN EUROPE_Regular'
                        }}>Supplier:</span> */}
														<Tooltip
															title={row.supplier || "No supplier data"}
															placement="top"
														>
															<div
																style={{
																	flex: 1,
																	textAlign: "left",
																	color: "black",
																	fontSize: "12px",
																	fontWeight: "500",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																	whiteSpace: "nowrap",
																	fontFamily: "MAN EUROPE_Regular",
																}}
															>
																{row.supplier || "---"}
															</div>
														</Tooltip>
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															alignItems: "center",
															padding: "4px 0",
														}}
													>
														{/* <span style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: 'black',
                          fontFamily: 'MAN EUROPE_Regular'
                        }}>Purchase:</span> */}
														<Tooltip
															title={
																row.purchase_type || "No purchase type data"
															}
															placement="top"
														>
															<div
																style={{
																	flex: 1,
																	textAlign: "left",
																	color: "black",
																	fontSize: "12px",
																	fontWeight: "500",
																	fontFamily: "MAN EUROPE_Regular",
																}}
															>
																{row.purchase_type || "N/A"}
															</div>
														</Tooltip>
													</div>
												</div>
											</td>
											<td
												style={{
													padding: "4px 6px",
													textAlign: "left",
													borderTop:
														"4px solid " + getStatusColor(row.stock_status),
													borderTopWidth: "4px",
													borderRight: "2px solid transparent",
													borderLeft: "2px solid transparent",
													position: "relative",
													verticalAlign: "top",
													lineHeight: "1.2",
													borderTopLeftRadius: "4px",
													borderTopRightRadius: "4px",
												}}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														gap: "2px",
														width: "100%",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													{" "}
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																minWidth: "55px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															Req Date:
														</span>
														<span
															style={{
																textAlign: "right",
																minWidth: "75px",
																fontSize: "12px",
															}}
														>
															{formatDateToDDMMYYYY(row.required_date)}
														</span>
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "55px",
																fontSize: "12px",
															}}
														>
															MET:
														</span>
														<span
															style={{
																textAlign: "right",
																minWidth: "75px",
																fontSize: "12px",
															}}
														>
															{row.met}
														</span>
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "55px",
																fontSize: "12px",
															}}
														>
															LTB:
														</span>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "4px",
																minWidth: "75px",
																justifyContent: "flex-end",
															}}
														>
															<span
																style={{
																	fontSize: "12px",
																	minWidth: "60px",
																	textAlign: "right",
																}}
															>
																{updatingLtbItems[itemId] ? (
																	<CircularProgress
																		size={16}
																		thickness={5}
																		sx={{ color: "#cc0000" }}
																	/>
																) : (
																	(() => {
																		const dateValue =
																			ltbDates[itemId] || row.ltb;
																		if (dateValue) {
																			try {
																				const dateObj = new Date(dateValue);
																				if (!isNaN(dateObj.getTime())) {
																					return format(dateObj, "dd/MM/yyyy");
																				}
																			} catch (error) {
																				console.error(
																					"Error formatting date:",
																					error,
																				);
																			}
																		}
																		return "-";
																	})()
																)}
															</span>{" "}
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	gap: "4px",
																}}
															>
																{" "}
																<div
																	onClick={() => {
																		if (
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																		) {
																			setShowAccessDeniedModal(true);
																			return;
																		}
																		if (
																			!updatingLtbItems[itemId] &&
																			!isAnyUpdateInProgress()
																		) {
																			setOpenDatePicker(itemId);
																		}
																	}}
																	style={{
																		cursor:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			) ||
																			updatingLtbItems[itemId] ||
																			isAnyUpdateInProgress()
																				? "not-allowed"
																				: "pointer",
																		display: "flex",
																		alignItems: "center",
																		opacity:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			) ||
																			updatingLtbItems[itemId] ||
																			isAnyUpdateInProgress()
																				? 0.5
																				: 1,
																	}}
																	title={
																		!isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		)
																			? "Access restricted - Only authorized users can edit LTB dates"
																			: isAnyUpdateInProgress()
																				? `Please wait for ${getCurrentUpdateDescription()} to complete`
																				: "Select date"
																	}
																>
																	<Calendar size={16} />
																</div>{" "}
																{/* Clear LTB Date Button */}{" "}
																{(ltbDates[itemId] || row.ltb) && (
																	<div
																		onClick={() => {
																			if (
																				!isUserAuthorizedForResponsibleFields(
																					getUserEmail,
																				)
																			) {
																				setShowAccessDeniedModal(true);
																				return;
																			}
																			// Check for concurrent updates
																			if (
																				isAnyUpdateInProgress() &&
																				!updatingLtbItems[itemId]
																			) {
																				showConcurrentUpdateWarning(
																					"LTB date clear",
																				);
																				return;
																			}

																			if (!updatingLtbItems[itemId]) {
																				handleUpdateItem(itemId, "ltb", null);
																				console.log(
																					`Cleared LTB date for item ${itemId}`,
																				);
																			}
																		}}
																		style={{
																			cursor:
																				!isUserAuthorizedForResponsibleFields(
																					getUserEmail,
																				) ||
																				updatingLtbItems[itemId] ||
																				(isAnyUpdateInProgress() &&
																					!updatingLtbItems[itemId])
																					? "not-allowed"
																					: "pointer",
																			display: "flex",
																			alignItems: "center",
																			opacity:
																				!isUserAuthorizedForResponsibleFields(
																					getUserEmail,
																				) ||
																				updatingLtbItems[itemId] ||
																				(isAnyUpdateInProgress() &&
																					!updatingLtbItems[itemId])
																					? 0.5
																					: 1,
																			color: "#dc2626",
																		}}
																		title={
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? "Access restricted - Only authorized users can edit LTB dates"
																				: isAnyUpdateInProgress() &&
																						!updatingLtbItems[itemId]
																					? `Please wait for ${getCurrentUpdateDescription()} to complete`
																					: "Clear date"
																		}
																	>
																		<X size={14} />
																	</div>
																)}
															</div>
															{openDatePicker === itemId &&
																!updatingLtbItems[itemId] &&
																isUserAuthorizedForResponsibleFields(
																	getUserEmail,
																) && (
																	<div
																		style={{
																			position: "absolute",
																			zIndex: 10,
																			backgroundColor: "white",
																			padding: "8px",
																		}}
																	>
																		<LocalizationProvider
																			dateAdapter={AdapterDateFns}
																			adapterLocale={enGB}
																		>
																			<DatePicker
																				open={true}
																				onClose={() => setOpenDatePicker(null)}
																				slotProps={{
																					textField: {
																						size: "small",
																						sx: {
																							width: "140px",
																							"& .MuiInputBase-root": {
																								fontSize: "12px",
																								height: "32px",
																							},
																							"& .MuiInputBase-input": {
																								fontSize: "12px",
																								padding: "6px 8px",
																							},
																						},
																					},
																					popper: {
																						sx: {
																							"& .MuiPaper-root": {
																								fontSize: "14px",
																								"& .MuiPickersCalendarHeader-root":
																									{ fontSize: "14px" },
																								"& .MuiPickersArrowSwitcher-root":
																									{
																										"& .MuiIconButton-root": {
																											color: "#cc0000",
																										},
																									},
																								"& .MuiDayCalendar-weekDayLabel":
																									{
																										fontSize: "13px",
																										width: "36px",
																										height: "36px",
																									},
																								"& .MuiPickersDay-root": {
																									fontSize: "13px",
																									width: "36px",
																									height: "36px",
																									"&:hover": {
																										backgroundColor:
																											"#cc000020",
																									},
																									"&.Mui-selected": {
																										backgroundColor: "#cc0000",
																										color: "white",
																									},
																								},
																								"& .MuiPickersDay-today": {
																									border: "1px solid #cc0000",
																								},
																							},
																						},
																					},
																				}}
																				value={(() => {
																					try {
																						const dateValue =
																							ltbDates[itemId] || row.ltb;
																						return dateValue
																							? new Date(dateValue)
																							: null;
																					} catch (error) {
																						console.error(
																							"Error parsing date for picker:",
																							error,
																						);
																						return null;
																					}
																				})()}
																				onChange={(newDate) => {
																					// Format the date as YYYY-MM-DD format without timezone issues
																					const formattedDate = newDate
																						? formatDateForBackend(newDate)
																						: null;

																					// Trigger confirmation dialog via handleUpdateItem
																					// Use 'ltb' as the field name since that's where the date is stored in the database
																					handleUpdateItem(
																						itemId,
																						"ltb",
																						formattedDate,
																					);
																					// Close the date picker
																					setOpenDatePicker(null);
																				}}
																			/>
																		</LocalizationProvider>
																	</div>
																)}
														</div>
													</div>
												</div>
											</td>
											<td
												style={{
													padding: "2px 6px",
													textAlign: "left",
													borderTop: "4px solid transparent",
													borderTopWidth: "4px",
													borderLeft: "2px solid transparent",
													borderRight: "2px solid transparent",
													position: "relative",
													verticalAlign: "top",
													lineHeight: "1.2",
													borderTopLeftRadius: "4px",
													borderTopRightRadius: "4px",
												}}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														alignItems: "flex-start",
														gap: "2px",
														width: "100%",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													{" "}
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "60px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															Required:
														</span>
														<span
															style={{
																textAlign: "right",
																minWidth: "50px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															{row.required !== undefined ? row.required : "0"}
														</span>
													</div>{" "}
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "60px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															Total:
														</span>
														<span
															style={{
																textAlign: "right",
																minWidth: "50px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															{row.total_menge}
														</span>
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "60px",
																fontSize: "12px",
															}}
														>
															Assembly Date:
														</span>
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "4px",
																minWidth: "50px",
																justifyContent: "flex-end",
															}}
														>
															<span
																style={{
																	fontSize: "12px",
																	minWidth: "45px",
																	textAlign: "right",
																}}
															>
																{updatingBuildDateItems[itemId] ? (
																	<CircularProgress
																		size={16}
																		thickness={5}
																		sx={{ color: "#cc0000" }}
																	/>
																) : (
																	(() => {
																		const dateValue =
																			buildDateDates[itemId] || row.build_date;
																		if (dateValue) {
																			try {
																				const dateObj = new Date(dateValue);
																				if (!isNaN(dateObj.getTime())) {
																					return format(dateObj, "dd/MM/yyyy");
																				}
																			} catch (error) {
																				console.error(
																					"Error formatting build date:",
																					error,
																				);
																			}
																		}
																		return "-";
																	})()
																)}
															</span>{" "}
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	gap: "4px",
																}}
															>
																{" "}
																<div
																	onClick={() => {
																		if (
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																		) {
																			setShowAccessDeniedModal(true);
																			return;
																		}
																		if (
																			!updatingBuildDateItems[itemId] &&
																			!isAnyUpdateInProgress()
																		) {
																			setOpenBuildDatePicker(itemId);
																		}
																	}}
																	style={{
																		cursor:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			) ||
																			updatingBuildDateItems[itemId] ||
																			isAnyUpdateInProgress()
																				? "not-allowed"
																				: "pointer",
																		display: "flex",
																		alignItems: "center",
																		opacity:
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			) ||
																			updatingBuildDateItems[itemId] ||
																			isAnyUpdateInProgress()
																				? 0.5
																				: 1,
																	}}
																	title={
																		!isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		)
																			? "Access restricted - Only authorized users can edit assembly dates"
																			: isAnyUpdateInProgress()
																				? `Please wait for ${getCurrentUpdateDescription()} to complete`
																				: "Select build date"
																	}
																>
																	<Calendar size={16} />
																</div>{" "}
																{/* Clear Build Date Button */}{" "}
																{(buildDateDates[itemId] || row.build_date) && (
																	<div
																		onClick={() => {
																			if (
																				!isUserAuthorizedForResponsibleFields(
																					getUserEmail,
																				)
																			) {
																				setShowAccessDeniedModal(true);
																				return;
																			}
																			// Check for concurrent updates
																			if (
																				isAnyUpdateInProgress() &&
																				!updatingBuildDateItems[itemId]
																			) {
																				showConcurrentUpdateWarning(
																					"Build date clear",
																				);
																				return;
																			}

																			if (!updatingBuildDateItems[itemId]) {
																				handleUpdateItem(
																					itemId,
																					"build_date",
																					null,
																				);
																				console.log(
																					`Cleared Build Date for item ${itemId}`,
																				);
																			}
																		}}
																		style={{
																			cursor:
																				!isUserAuthorizedForResponsibleFields(
																					getUserEmail,
																				) ||
																				updatingBuildDateItems[itemId] ||
																				(isAnyUpdateInProgress() &&
																					!updatingBuildDateItems[itemId])
																					? "not-allowed"
																					: "pointer",
																			display: "flex",
																			alignItems: "center",
																			opacity:
																				!isUserAuthorizedForResponsibleFields(
																					getUserEmail,
																				) ||
																				updatingBuildDateItems[itemId] ||
																				(isAnyUpdateInProgress() &&
																					!updatingBuildDateItems[itemId])
																					? 0.5
																					: 1,
																			color: "#dc2626",
																		}}
																		title={
																			!isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? "Access restricted - Only authorized users can edit assembly dates"
																				: isAnyUpdateInProgress() &&
																						!updatingBuildDateItems[itemId]
																					? `Please wait for ${getCurrentUpdateDescription()} to complete`
																					: "Clear build date"
																		}
																	>
																		<X size={14} />
																	</div>
																)}
															</div>{" "}
															{openBuildDatePicker === itemId &&
																!updatingBuildDateItems[itemId] &&
																isUserAuthorizedForResponsibleFields(
																	getUserEmail,
																) && (
																	<div
																		style={{
																			position: "absolute",
																			zIndex: 10,
																			backgroundColor: "white",
																			padding: "8px",
																		}}
																	>
																		<LocalizationProvider
																			dateAdapter={AdapterDateFns}
																			adapterLocale={enGB}
																		>
																			<DatePicker
																				open={true}
																				onClose={() =>
																					setOpenBuildDatePicker(null)
																				}
																				slotProps={{
																					textField: {
																						size: "small",
																						sx: {
																							width: "140px",
																							"& .MuiInputBase-root": {
																								fontSize: "12px",
																								height: "32px",
																							},
																							"& .MuiInputBase-input": {
																								fontSize: "12px",
																								padding: "6px 8px",
																							},
																						},
																					},
																					popper: {
																						sx: {
																							"& .MuiPaper-root": {
																								fontSize: "14px",
																								"& .MuiPickersCalendarHeader-root":
																									{ fontSize: "14px" },
																								"& .MuiPickersArrowSwitcher-root":
																									{
																										"& .MuiIconButton-root": {
																											color: "#cc0000",
																										},
																									},
																								"& .MuiDayCalendar-weekDayLabel":
																									{
																										fontSize: "13px",
																										width: "36px",
																										height: "36px",
																									},
																								"& .MuiPickersDay-root": {
																									fontSize: "13px",
																									width: "36px",
																									height: "36px",
																									"&:hover": {
																										backgroundColor:
																											"#cc000020",
																									},
																									"&.Mui-selected": {
																										backgroundColor: "#cc0000",
																										color: "white",
																									},
																								},
																								"& .MuiPickersDay-today": {
																									border: "1px solid #cc0000",
																								},
																							},
																						},
																					},
																				}}
																				value={(() => {
																					try {
																						const dateValue =
																							buildDateDates[itemId] ||
																							row.build_date;
																						return dateValue
																							? new Date(dateValue)
																							: null;
																					} catch (error) {
																						console.error(
																							"Error parsing build date for picker:",
																							error,
																						);
																						return null;
																					}
																				})()}
																				onChange={(newDate) => {
																					// Format the date as YYYY-MM-DD format without timezone issues
																					const formattedDate = newDate
																						? formatDateForBackend(newDate)
																						: null;

																					// Trigger confirmation dialog via handleUpdateItem
																					handleUpdateItem(
																						itemId,
																						"build_date",
																						formattedDate,
																					);
																					// Close the date picker
																					setOpenBuildDatePicker(null);
																				}}
																			/>
																		</LocalizationProvider>
																	</div>
																)}{" "}
														</div>
													</div>
												</div>
											</td>
											<td
												style={{
													padding: "2px 6px",
													textAlign: "left",
													borderLeft: "2px solid transparent",
													borderRight: "2px solid transparent",
													position: "relative",
													verticalAlign: "top",
													lineHeight: "1.2",
													borderTopLeftRadius: "4px",
													borderTopRightRadius: "4px",
												}}
											>
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														alignItems: "flex-start",
														gap: "2px",
														width: "100%",
														fontFamily: "MAN EUROPE_Regular",
													}}
												>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "60px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															Munich:
														</span>
														<span
															style={{
																textAlign: "right",
																minWidth: "50px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
																position: "relative",
															}}
														>
															{(parseFloat(row.munich_dypp) || 0) >= 1 ? (
																<>
																	<span
																		onMouseEnter={(e) => {
																			const rect =
																				e.currentTarget.getBoundingClientRect();
																			setMunichPopover({
																				key: row.kast_key,
																				top: rect.top,
																				left: rect.right,
																			});
																		}}
																		onMouseLeave={() => setMunichPopover(null)}
																		style={{
																			color: "#1e6ee6",
																			cursor: "pointer",
																			fontWeight: 600,
																		}}
																	>
																		{row.munich}
																	</span>
																	{munichPopover &&
																		munichPopover.key === row.kast_key && (
																			<>
																				<div
																					style={{
																						position: "fixed",
																						top: munichPopover.top - 6,
																						left: munichPopover.left,
																						transform:
																							"translate(-100%, -100%)",
																						backgroundColor: "#fff",
																						border: "1px solid #1e2832",
																						borderRadius: "4px",
																						boxShadow:
																							"0 4px 12px rgba(0,0,0,0.2)",
																						padding: "8px 10px",
																						zIndex: 99999,
																						minWidth: "200px",
																						fontFamily: "MAN EUROPE_Regular",
																						textAlign: "left",
																						pointerEvents: "none",
																					}}
																				>
																					<div
																						style={{
																							fontFamily: "MAN EUROPE_Bold",
																							fontSize: "11px",
																							color: "#1e2832",
																							marginBottom: "6px",
																							textTransform: "uppercase",
																							letterSpacing: "0.5px",
																						}}
																					>
																						Munich Stock
																					</div>
																					<div
																						style={{
																							display: "flex",
																							justifyContent: "space-between",
																							fontSize: "12px",
																							marginBottom: "3px",
																							gap: "12px",
																						}}
																					>
																						<span>
																							Lager (Stock in warehouse):
																						</span>
																						<span style={{ fontWeight: 600 }}>
																							{row.munich_lager}
																						</span>
																					</div>
																					<div
																						style={{
																							display: "flex",
																							justifyContent: "space-between",
																							fontSize: "12px",
																							marginBottom: "3px",
																							gap: "12px",
																						}}
																					>
																						<span>Stock on line:</span>
																						<span style={{ fontWeight: 600 }}>
																							{row.munich_dypp}
																						</span>
																					</div>
																					<div
																						style={{
																							display: "flex",
																							justifyContent: "space-between",
																							fontSize: "12px",
																							borderTop: "1px solid #e0e0e0",
																							paddingTop: "3px",
																							marginTop: "3px",
																							gap: "12px",
																						}}
																					>
																						<span style={{ fontWeight: 600 }}>
																							Total:
																						</span>
																						<span style={{ fontWeight: 600 }}>
																							{(
																								(parseFloat(row.munich_dypp) ||
																									0) +
																								(parseFloat(row.munich_lager) ||
																									0)
																							).toString()}
																						</span>
																					</div>
																				</div>
																			</>
																		)}
																</>
															) : (
																row.munich
															)}
														</span>
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "60px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															Cracow:
														</span>
														<span
															style={{
																textAlign: "right",
																minWidth: "50px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															{row.cracow}
														</span>
													</div>
													<div
														style={{
															display: "flex",
															justifyContent: "space-between",
															width: "100%",
														}}
													>
														<span
															style={{
																textAlign: "left",
																minWidth: "60px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															MTB:
														</span>
														<span
															style={{
																textAlign: "right",
																minWidth: "50px",
																fontFamily: "MAN EUROPE_Regular",
																fontSize: "12px",
															}}
														>
															{row.total_stock_mtb}
														</span>{" "}
													</div>
												</div>
											</td>
											<td
												style={{
													padding: "4px 6px",
													verticalAlign: "top",
													textAlign: "center",
													fontFamily: "MAN EUROPE_Regular",
													width: "140px",
													minWidth: "140px",
													maxWidth: "140px",
													position: "relative",
													overflow: "visible",
												}}
											>
												{updatingStatusBTT[itemId] ? (
													<Box
														display="flex"
														justifyContent="center"
														alignItems="center"
														height="28px"
													>
														<CircularProgress
															size={16}
															thickness={5}
															sx={{ color: "#cc0000" }}
														/>
													</Box>
												) : (
													<div
														style={{
															position: "relative",
															display: "flex",
															flexDirection: "column",
															gap: "2px",
														}}
													>
														{/* Display Area - Show selected values as chips */}
														<div
															style={{
																minHeight: "32px",
																maxHeight: "60px",
																padding: "4px",
																border: "1px solid #e5e7eb",
																borderRadius: "4px",
																backgroundColor:
																	!isUserAuthorizedForResponsibleFields(
																		getUserEmail,
																	)
																		? "#f3f4f6"
																		: "#fff",
																display: "flex",
																flexWrap: "wrap",
																gap: "4px",
																alignItems: "flex-start",
																cursor: !isUserAuthorizedForResponsibleFields(
																	getUserEmail,
																)
																	? "not-allowed"
																	: "pointer",
																fontSize: "12px",
																overflowY: "auto",
																scrollbarWidth: "thin",
																scrollbarColor: "#d1d5db transparent",
																position: "relative",
																opacity: !isUserAuthorizedForResponsibleFields(
																	getUserEmail,
																)
																	? 0.6
																	: 1,
															}}
															className="btt-status-container"
															onClick={() => {
																if (
																	!isUserAuthorizedForResponsibleFields(
																		getUserEmail,
																	)
																) {
																	setShowAccessDeniedModal(true);
																	return;
																}
																// Toggle dropdown visibility
																const dropdownId = `status-btt-dropdown-${itemId}`;
																const dropdown =
																	document.getElementById(dropdownId);
																if (dropdown) {
																	const isCurrentlyVisible =
																		dropdown.style.display !== "none";

																	if (isCurrentlyVisible) {
																		dropdown.style.display = "none";
																	} else {
																		// Calculate if dropdown should open upwards or downwards
																		const container = dropdown.parentElement;
																		if (container) {
																			const rect =
																				container.getBoundingClientRect();
																			const tableContainer =
																				document.querySelector(
																					".table-container",
																				);
																			const tableRect = tableContainer
																				? tableContainer.getBoundingClientRect()
																				: null;
																			const dropdownHeight = 220; // maxHeight of dropdown

																			// Calculate space below relative to table container
																			const spaceBelow = tableRect
																				? tableRect.bottom - rect.bottom
																				: window.innerHeight - rect.bottom;

																			console.log(
																				"BTT Dropdown position check:",
																				{
																					spaceBelow,
																					dropdownHeight,
																					rectBottom: rect.bottom,
																					rectTop: rect.top,
																					tableBottom: tableRect?.bottom,
																				},
																			);

																			// If not enough space below, open upwards
																			if (spaceBelow < dropdownHeight + 20) {
																				dropdown.style.top = "auto";
																				dropdown.style.bottom = "100%";
																				dropdown.style.marginBottom = "2px";
																				console.log("Opening dropdown upwards");
																			} else {
																				dropdown.style.top = "100%";
																				dropdown.style.bottom = "auto";
																				dropdown.style.marginTop = "2px";
																				console.log(
																					"Opening dropdown downwards",
																				);
																			}
																		}
																		dropdown.style.display = "block";
																	}
																}
															}}
														>
															{(() => {
																const currentValue = row.status_btt;
																if (!currentValue || currentValue === "") {
																	return (
																		<span
																			style={{
																				color: "#9ca3af",
																				fontSize: "14px",
																			}}
																		>
																			Click to select the Status BTT...
																		</span>
																	);
																}

																const values = Array.isArray(currentValue)
																	? currentValue
																	: currentValue
																			.split(",")
																			.map((v) => v.trim())
																			.filter((v) => v !== "");
																return values.map((value, index) => (
																	<div
																		key={index}
																		style={{
																			backgroundColor: "black",
																			fontFamily: "MAN EUROPE_Bold",
																			color: "white",
																			fontSize: "14px",
																			padding: "2px 4px",
																			borderRadius: "0px",
																			display: "flex",
																			alignItems: "center",
																			gap: "3px",
																			maxWidth: "100%",
																			minHeight: "22px",
																		}}
																		title={value} // Show full value on hover
																	>
																		<span
																			style={{
																				overflow: "hidden",
																				textOverflow: "ellipsis",
																				whiteSpace: "nowrap",
																				maxWidth: "50px",
																				fontWeight: "500",
																			}}
																		>
																			{value}
																		</span>
																		<button
																			onClick={(e) => {
																				e.stopPropagation();
																				const newValues = values.filter(
																					(v) => v !== value,
																				);
																				handleStatusBTTUpdate(
																					itemId,
																					newValues,
																				);
																			}}
																			style={{
																				background: "none",
																				border: "none",
																				color: "white",
																				cursor: "pointer",
																				fontSize: "12px",
																				padding: "0",
																				lineHeight: "1",
																				width: "14px",
																				height: "14px",
																				display: "flex",
																				alignItems: "center",
																				justifyContent: "center",
																			}}
																			title="Remove"
																		>
																			×
																		</button>
																	</div>
																));
															})()}
														</div>
														{/* Dropdown Options */}
														<div
															id={`status-btt-dropdown-${itemId}`}
															style={{
																position: "absolute",
																top: "100%",
																left: "0",
																right: "0",
																backgroundColor: "white",
																border: "1px solid #e5e7eb",
																borderRadius: "0px",
																boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
																zIndex: 99999,
																maxHeight: "220px",
																overflowY: "auto",
																display: "none",
															}}
														>
															{/* Options List */}
															<div
																style={{
																	maxHeight: "140px",
																	overflowY: "auto",
																}}
															>
																{STATUS_BTT_OPTIONS.map((option) => {
																	// Use temporary selections if available, otherwise use current values
																	const currentValue = row.status_btt;
																	const currentValues = currentValue
																		? Array.isArray(currentValue)
																			? currentValue
																			: currentValue
																					.split(",")
																					.map((v) => v.trim())
																					.filter((v) => v !== "")
																		: [];
																	const tempValues =
																		tempStatusBTTSelections[itemId] ||
																		currentValues;
																	const isSelected =
																		tempValues.includes(option);

																	return (
																		<div
																			key={option}
																			style={{
																				padding: "8px 10px",
																				fontSize: "13px",
																				cursor: "pointer",
																				backgroundColor: isSelected
																					? "#f3f4f6"
																					: "white",
																				borderBottom: "1px solid #f1f5f9",
																				display: "flex",
																				alignItems: "center",
																				gap: "8px",
																				minHeight: "32px",
																			}}
																			onClick={() => {
																				// Update temporary selections
																				const currentTempValues =
																					tempStatusBTTSelections[itemId] ||
																					currentValues;
																				let newTempValues;

																				if (isSelected) {
																					// Remove from selection
																					newTempValues =
																						currentTempValues.filter(
																							(v) => v !== option,
																						);
																				} else {
																					// Add to selection
																					newTempValues = [
																						...currentTempValues,
																						option,
																					];
																				}

																				setTempStatusBTTSelections((prev) => ({
																					...prev,
																					[itemId]: newTempValues,
																				}));
																			}}
																			onMouseEnter={(e) => {
																				e.target.style.backgroundColor =
																					"#f8f9fa";
																			}}
																			onMouseLeave={(e) => {
																				e.target.style.backgroundColor =
																					isSelected ? "#f3f4f6" : "white";
																			}}
																		>
																			<input
																				type="checkbox"
																				checked={isSelected}
																				readOnly
																				style={{
																					width: "14px",
																					height: "14px",
																					accentColor: "#cc0000",
																				}}
																			/>
																			<span style={{ fontWeight: "500" }}>
																				{option}
																			</span>
																		</div>
																	);
																})}
															</div>

															{/* Action Buttons */}
															<div
																style={{
																	borderTop: "1px solid #f1f5f9",
																	padding: "8px",
																	display: "flex",
																	gap: "6px",
																	justifyContent: "flex-end",
																	backgroundColor: "#f8f9fa",
																}}
															>
																<button
																	onClick={() => {
																		// Cancel - reset temp selections and close dropdown
																		setTempStatusBTTSelections((prev) => {
																			const newState = { ...prev };
																			delete newState[itemId];
																			return newState;
																		});
																		const dropdown = document.getElementById(
																			`status-btt-dropdown-${itemId}`,
																		);
																		if (dropdown)
																			dropdown.style.display = "none";
																	}}
																	style={{
																		padding: "6px 12px",
																		fontSize: "12px",
																		border: "1px solid #d1d5db",
																		borderRadius: "0px",
																		backgroundColor: "white",
																		color: "#666",
																		cursor: "pointer",
																		fontFamily: "MAN EUROPE_Regular",
																		minHeight: "28px",
																	}}
																	onMouseEnter={(e) => {
																		e.target.style.backgroundColor = "#f5f5f5";
																	}}
																	onMouseLeave={(e) => {
																		e.target.style.backgroundColor = "white";
																	}}
																>
																	Cancel
																</button>
																<button
																	onClick={async () => {
																		// Save selections
																		const currentValue = row.status_btt;
																		const currentValues = currentValue
																			? Array.isArray(currentValue)
																				? currentValue
																				: currentValue
																						.split(",")
																						.map((v) => v.trim())
																						.filter((v) => v !== "")
																			: [];
																		const newValues =
																			tempStatusBTTSelections[itemId] ||
																			currentValues;

																		await handleStatusBTTUpdate(
																			itemId,
																			newValues,
																		);

																		// Clear temp selections and close dropdown
																		setTempStatusBTTSelections((prev) => {
																			const newState = { ...prev };
																			delete newState[itemId];
																			return newState;
																		});
																		const dropdown = document.getElementById(
																			`status-btt-dropdown-${itemId}`,
																		);
																		if (dropdown)
																			dropdown.style.display = "none";
																	}}
																	disabled={updatingStatusBTT[itemId]}
																	style={{
																		padding: "6px 12px",
																		fontSize: "12px",
																		border: "none",
																		borderRadius: "0px",
																		backgroundColor: updatingStatusBTT[itemId]
																			? "#ccc"
																			: "#cc0000",
																		color: "white",
																		cursor: updatingStatusBTT[itemId]
																			? "not-allowed"
																			: "pointer",
																		fontFamily: "MAN EUROPE_Bold",
																		display: "flex",
																		alignItems: "center",
																		gap: "4px",
																		minHeight: "28px",
																	}}
																	onMouseEnter={(e) => {
																		if (!updatingStatusBTT[itemId]) {
																			e.target.style.backgroundColor =
																				"#b30000";
																		}
																	}}
																	onMouseLeave={(e) => {
																		if (!updatingStatusBTT[itemId]) {
																			e.target.style.backgroundColor =
																				"#cc0000";
																		}
																	}}
																>
																	{updatingStatusBTT[itemId] ? (
																		<>
																			{" "}
																			<div
																				style={{
																					width: "12px",
																					height: "12px",
																					border: "1px solid white",
																					borderTop: "1px solid transparent",
																					borderRadius: "0px",
																					animation: "spin 1s linear infinite",
																				}}
																			/>
																			Saving...
																		</>
																	) : (
																		"Save"
																	)}
																</button>
															</div>
														</div>
													</div>
												)}
											</td>
											<td
												style={{
													padding: "2px 4px",
													verticalAlign: "top",
													lineHeight: "1.1",
												}}
											>
												<div
													style={{
														display: "flex",
														gap: "4px",
														height: "100%",
													}}
												>
													{/* Parts Column */}
													<div
														style={{
															flex: 1,
															display: "flex",
															flexDirection: "column",
															gap: "1px",
															padding: "2px",
														}}
													>
														{/* Responsible field */}{" "}
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "2px",
																marginBottom: "1px",
															}}
														>
															<span
																style={{
																	fontSize: "12px",
																	fontFamily: "MAN EUROPE_Regular",
																	minWidth: "35px",
																	color: "black",
																}}
															>
																Resp:
															</span>
															<TextField
																variant="outlined"
																size="small"
																disabled={
																	!isEditMode ||
																	!canEditProtected ||
																	!isUserAuthorizedForResponsibleFields(
																		getUserEmail,
																	)
																}
																value={
																	responsibleValues[itemId]
																		?.part_responsible !== undefined
																		? responsibleValues[itemId].part_responsible
																		: row.part_responsible !== "-"
																			? row.part_responsible
																			: ""
																}
																onChange={(e) => {
																	// Check for invalid characters and show warning
																	const originalValue = e.target.value;
																	// Allow letters, numbers, spaces, dots, hyphens, underscores, apostrophes, and forward slashes
																	const newValue = originalValue.replace(
																		/[^a-zA-Z0-9\s.\-_'/]/g,
																		"",
																	);

																	if (
																		originalValue !== newValue &&
																		originalValue.length > newValue.length
																	) {
																		// Show warning for invalid characters
																		alert(
																			"Warning: Only letters, numbers, spaces, dots, hyphens, underscores, apostrophes, and forward slashes are allowed in responsible fields.",
																		);
																	}

																	setResponsibleValues((prev) => ({
																		...prev,
																		[itemId]: {
																			...prev[itemId],
																			part_responsible: newValue,
																			vehicle_responsible:
																				prev[itemId]?.vehicle_responsible !==
																				undefined
																					? prev[itemId].vehicle_responsible
																					: row.vehicle_responsible !== "-"
																						? row.vehicle_responsible
																						: "",
																		},
																	}));

																	// Track changes in bulk edit mode
																	if (isEditMode) {
																		const originalValue =
																			row.part_responsible !== "-"
																				? row.part_responsible
																				: "";
																		if (newValue !== originalValue) {
																			onEditedItemsChange?.((prev) => ({
																				...prev,
																				[itemId]: {
																					...prev[itemId],
																					part_responsible: newValue,
																				},
																			}));
																		} else {
																			// Remove from edited items if value reverted to original
																			onEditedItemsChange?.((prev) => {
																				const newState = { ...prev };
																				if (newState[itemId]) {
																					delete newState[itemId]
																						.part_responsible;
																					if (
																						Object.keys(newState[itemId])
																							.length === 0
																					) {
																						delete newState[itemId];
																					}
																				}
																				return newState;
																			});
																		}
																	}
																}}
																onKeyPress={(e) => {
																	// Only allow Enter key save when NOT in bulk edit mode
																	if (e.key === "Enter" && !isEditMode) {
																		e.preventDefault();
																		handleSaveResponsibleField(
																			itemId,
																			"part_responsible",
																		);
																	}
																}}
																onFocus={() => {
																	// Initialize values when focused if not already set
																	if (!responsibleValues[itemId]) {
																		setResponsibleValues((prev) => ({
																			...prev,
																			[itemId]: {
																				part_responsible:
																					row.part_responsible !== "-"
																						? row.part_responsible
																						: "",
																				vehicle_responsible:
																					row.vehicle_responsible !== "-"
																						? row.vehicle_responsible
																						: "",
																			},
																		}));
																	}

																	// Store original value for unsaved changes detection (only when not in edit mode)
																	if (!isEditMode) {
																		const currentOriginalValue =
																			row.part_responsible !== "-"
																				? row.part_responsible
																				: "";
																		setOriginalResponsibleValues((prev) => ({
																			...prev,
																			[itemId]: {
																				...prev[itemId],
																				part_responsible: currentOriginalValue,
																			},
																		}));
																	}
																}}
																onBlur={() => {
																	// Check for unsaved changes when field loses focus (only when not in edit mode)
																	if (!isEditMode) {
																		const currentValue =
																			responsibleValues[itemId]
																				?.part_responsible !== undefined
																				? responsibleValues[itemId]
																						.part_responsible
																				: row.part_responsible !== "-"
																					? row.part_responsible
																					: "";
																		const originalValue =
																			originalResponsibleValues[itemId]
																				?.part_responsible || "";

																		// If values are different, show unsaved changes warning
																		if (currentValue !== originalValue) {
																			setUnsavedChangesDialog({
																				open: true,
																				itemId: itemId,
																				fieldType: "part_responsible",
																				originalValue: originalValue,
																				currentValue: currentValue,
																			});
																		}
																	}
																}}
																sx={{
																	flex: 1,
																	"& .MuiOutlinedInput-root": {
																		fontSize: "10px",
																		height: "16px",
																		backgroundColor: isEditMode
																			? "#fff"
																			: "white",
																		"& input": {
																			padding: "2px 4px",
																			fontSize: "13px",
																			fontWeight: "bold",
																			color: "black !important",
																		},
																		"&:hover": {
																			backgroundColor: isEditMode
																				? "#f8f9fa"
																				: "#f8f9fa",
																		},
																		"&.Mui-focused": {
																			backgroundColor: "#fff",
																		},
																		"&.Mui-disabled": {
																			backgroundColor: "#f5f5f5",
																			"& input": {
																				color: "black !important",
																				WebkitTextFillColor: "black !important",
																			},
																			"& .MuiOutlinedInput-input": {
																				color: "black !important",
																				WebkitTextFillColor: "black !important",
																			},
																		},
																	},
																}}
																placeholder="Enter name"
																title={
																	!isUserAuthorizedForResponsibleFields(
																		getUserEmail,
																	)
																		? "Access restricted - Only authorized users can edit responsible person"
																		: isEditMode
																			? "Edit mode active - changes will be saved when you click Save"
																			: "Please press Enter to save changes"
																}
															/>
														</div>
														{/* Followup date field */}
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "2px",
																marginBottom: "1px",
															}}
														>
															<span
																style={{
																	fontSize: "12px",
																	fontFamily: "MAN EUROPE_Regular",
																	minWidth: "35px",
																	color: "#666",
																}}
															>
																Date:
															</span>
															{isEditMode ? (
																<div
																	style={{
																		flex: 1,
																		padding: "2px 3px",
																		border: "1px solid #d1d5db",
																		borderRadius: "2px",
																		fontSize: "12px",
																		backgroundColor: "white",
																		height: "16px",
																		display: "flex",
																		justifyContent: "space-between",
																		alignItems: "center",
																		position: "relative",
																		cursor:
																			isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? "pointer"
																				: "not-allowed",
																		opacity:
																			isUserAuthorizedForResponsibleFields(
																				getUserEmail,
																			)
																				? 1
																				: 0.6,
																	}}
																	onClick={() =>
																		!updatingPartFollowupItems[itemId] &&
																		isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		) &&
																		setOpenPartDatePicker(itemId)
																	}
																	title={
																		isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		)
																			? "Click to edit date"
																			: "Access restricted - Only authorized users can edit dates"
																	}
																>
																	<span
																		style={{
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																			flex: "1",
																		}}
																	>
																		{(() => {
																			// Show edited value if available, otherwise original value
																			const editedValue =
																				editedItems[itemId]?.part_followup_date;
																			const originalValue =
																				partFollowupDates[itemId] ||
																				row.part_followup_date;
																			const dateValue =
																				editedValue !== undefined
																					? editedValue
																					: originalValue;

																			if (
																				dateValue &&
																				dateValue !== "-" &&
																				dateValue !== "N/A" &&
																				dateValue !== ""
																			) {
																				try {
																					const dateObj = new Date(dateValue);
																					if (!isNaN(dateObj.getTime())) {
																						return format(
																							dateObj,
																							"dd/MM/yyyy",
																						);
																					}
																				} catch (error) {
																					console.error(
																						"Error formatting part followup date:",
																						error,
																					);
																				}
																			}
																			return "Select date";
																		})()}
																	</span>{" "}
																	<div
																		style={{
																			display: "flex",
																			alignItems: "center",
																			marginLeft: "4px",
																			gap: "4px",
																		}}
																	>
																		<Calendar size={12} />
																		{/* Clear Part Followup Date Button - Edit Mode */}
																		{(() => {
																			const editedValue =
																				editedItems[itemId]?.part_followup_date;
																			const originalValue =
																				partFollowupDates[itemId] ||
																				row.part_followup_date;
																			const dateValue =
																				editedValue !== undefined
																					? editedValue
																					: originalValue;
																			return (
																				dateValue &&
																				dateValue !== "-" &&
																				dateValue !== "N/A" &&
																				dateValue !== ""
																			);
																		})() && (
																			<div
																				onClick={(e) => {
																					e.stopPropagation();
																					if (
																						!updatingPartFollowupItems[
																							itemId
																						] &&
																						isUserAuthorizedForResponsibleFields(
																							getUserEmail,
																						)
																					) {
																						// Clear the date in bulk edit mode
																						onEditedItemsChange?.((prev) => ({
																							...prev,
																							[itemId]: {
																								...prev[itemId],
																								part_followup_date: "",
																							},
																						}));
																						console.log(
																							`Cleared Part followup date for bulk edit ${itemId}`,
																						);
																					}
																				}}
																				style={{
																					cursor:
																						updatingPartFollowupItems[itemId] ||
																						!isUserAuthorizedForResponsibleFields(
																							getUserEmail,
																						)
																							? "not-allowed"
																							: "pointer",
																					display: "flex",
																					alignItems: "center",
																					opacity:
																						updatingPartFollowupItems[itemId] ||
																						!isUserAuthorizedForResponsibleFields(
																							getUserEmail,
																						)
																							? 0.5
																							: 1,
																					color: "#dc2626",
																				}}
																				title={
																					isUserAuthorizedForResponsibleFields(
																						getUserEmail,
																					)
																						? "Clear date"
																						: "Access restricted - Only authorized users can edit dates"
																				}
																			>
																				<X size={10} />
																			</div>
																		)}
																	</div>{" "}
																	{openPartDatePicker === itemId &&
																		!updatingPartFollowupItems[itemId] &&
																		isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		) && (
																			<div
																				style={{
																					position: "absolute",
																					zIndex: 10,
																					backgroundColor: "white",
																					padding: "8px",
																					top: "100%",
																					right: 0,
																				}}
																			>
																				<LocalizationProvider
																					dateAdapter={AdapterDateFns}
																					adapterLocale={enGB}
																				>
																					<DatePicker
																						open={true}
																						onClose={() =>
																							setOpenPartDatePicker(null)
																						}
																						slotProps={{
																							textField: {
																								size: "small",
																								sx: {
																									width: "140px",
																									"& .MuiInputBase-root": {
																										fontSize: "12px",
																										height: "32px",
																									},
																									"& .MuiInputBase-input": {
																										fontSize: "12px",
																										padding: "6px 8px",
																									},
																								},
																							},
																							popper: {
																								sx: {
																									"& .MuiPaper-root": {
																										fontSize: "14px",
																										"& .MuiPickersCalendarHeader-root":
																											{
																												fontSize: "14px",
																												color: "black",
																											},
																										"& .MuiPickersArrowSwitcher-root":
																											{
																												"& .MuiIconButton-root":
																													{
																														color: "#cc0000",
																														"&:hover": {
																															backgroundColor:
																																"#cc000020",
																														},
																													},
																											},
																										"& .MuiDayCalendar-weekDayLabel":
																											{
																												fontSize: "13px",
																												width: "36px",
																												height: "36px",
																											},
																										"& .MuiPickersDay-root": {
																											fontSize: "13px",
																											width: "36px",
																											height: "36px",
																											"&:hover": {
																												backgroundColor:
																													"#cc000020",
																											},
																											"&.Mui-selected": {
																												backgroundColor:
																													"#cc0000",
																												color: "white",
																												"&:hover": {
																													backgroundColor:
																														"#aa0000",
																												},
																											},
																										},
																										"& .MuiPickersDay-today": {
																											border:
																												"1px solid #cc0000",
																											"&:not(.Mui-selected)": {
																												color: "#cc0000",
																												fontWeight: "bold",
																											},
																										},
																									},
																								},
																							},
																						}}
																						value={(() => {
																							try {
																								const editedValue =
																									editedItems[itemId]
																										?.part_followup_date;
																								const originalValue =
																									partFollowupDates[itemId] ||
																									row.part_followup_date;
																								const dateValue =
																									editedValue !== undefined
																										? editedValue
																										: originalValue;

																								if (
																									dateValue &&
																									dateValue !== "-" &&
																									dateValue !== "N/A" &&
																									dateValue !== ""
																								) {
																									return new Date(dateValue);
																								}
																								return null;
																							} catch (error) {
																								console.error(
																									"Error parsing date for picker:",
																									error,
																								);
																								return null;
																							}
																						})()}
																						onChange={(newDate) => {
																							// Format the date as YYYY-MM-DD format without timezone issues
																							const formattedDate = newDate
																								? formatDateForBackend(newDate)
																								: "";

																							// Update bulk edit state instead of calling handleUpdateItem
																							const originalValue = (() => {
																								const origValue =
																									partFollowupDates[itemId] ||
																									row.part_followup_date;
																								if (
																									origValue &&
																									origValue !== "-" &&
																									origValue !== "N/A"
																								) {
																									try {
																										const dateObj = new Date(
																											origValue,
																										);
																										if (
																											!isNaN(dateObj.getTime())
																										) {
																											return formatDateForBackend(
																												dateObj,
																											);
																										}
																									} catch (error) {
																										console.error(
																											"Error formatting original date:",
																											error,
																										);
																									}
																								}
																								return "";
																							})();

																							if (
																								formattedDate !== originalValue
																							) {
																								onEditedItemsChange?.(
																									(prev) => ({
																										...prev,
																										[itemId]: {
																											...prev[itemId],
																											part_followup_date:
																												formattedDate,
																										},
																									}),
																								);
																							} else {
																								// Remove from edited items if value reverted to original
																								onEditedItemsChange?.(
																									(prev) => {
																										const newState = {
																											...prev,
																										};
																										if (newState[itemId]) {
																											delete newState[itemId]
																												.part_followup_date;
																											if (
																												Object.keys(
																													newState[itemId],
																												).length === 0
																											) {
																												delete newState[itemId];
																											}
																										}
																										return newState;
																									},
																								);
																							}

																							// Close the date picker
																							setOpenPartDatePicker(null);

																							console.log(
																								`Selected Part followup date for bulk edit ${itemId}: ${formattedDate}`,
																							);
																						}}
																					/>
																				</LocalizationProvider>
																			</div>
																		)}
																</div>
															) : (
																<div
																	style={{
																		flex: 1,
																		padding: "2px 3px",
																		border: "1px solid #d1d5db",
																		borderRadius: "2px",
																		fontSize: "12px",
																		backgroundColor: "white",
																		height: "16px",
																		display: "flex",
																		justifyContent: "space-between",
																		alignItems: "center",
																		position: "relative",
																	}}
																>
																	<span
																		style={{
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																			flex: "1",
																		}}
																	>
																		{(() => {
																			const dateValue =
																				partFollowupDates[itemId] ||
																				row.part_followup_date;
																			if (
																				dateValue &&
																				dateValue !== "-" &&
																				dateValue !== "N/A"
																			) {
																				try {
																					const dateObj = new Date(dateValue);
																					if (!isNaN(dateObj.getTime())) {
																						return format(
																							dateObj,
																							"dd/MM/yyyy",
																						);
																					}
																				} catch (error) {
																					console.error(
																						"Error formatting part followup date:",
																						error,
																					);
																				}
																			}
																			return "N/A";
																		})()}
																	</span>
																	{updatingPartFollowupItems[itemId] ? (
																		<div
																			style={{
																				display: "flex",
																				alignItems: "center",
																				marginLeft: "8px",
																			}}
																		>
																			<CircularProgress
																				size={16}
																				thickness={5}
																				sx={{ color: "#cc0000" }}
																			/>
																		</div>
																	) : !isEditMode ? (
																		<div
																			style={{
																				display: "flex",
																				alignItems: "center",
																				marginLeft: "8px",
																				opacity: 0.5,
																			}}
																		>
																			<Calendar size={16} color="#ccc" />
																		</div>
																	) : (
																		<div
																			style={{
																				display: "flex",
																				alignItems: "center",
																				marginLeft: "8px",
																				gap: "4px",
																			}}
																		>
																			{" "}
																			<div
																				onClick={() =>
																					!updatingPartFollowupItems[itemId] &&
																					isUserAuthorizedForResponsibleFields(
																						getUserEmail,
																					) &&
																					setOpenPartDatePicker(itemId)
																				}
																				style={{
																					cursor:
																						updatingPartFollowupItems[itemId] ||
																						!isUserAuthorizedForResponsibleFields(
																							getUserEmail,
																						)
																							? "not-allowed"
																							: "pointer",
																					display: "flex",
																					alignItems: "center",
																					opacity:
																						updatingPartFollowupItems[itemId] ||
																						!isUserAuthorizedForResponsibleFields(
																							getUserEmail,
																						)
																							? 0.5
																							: 1,
																				}}
																				title={
																					isUserAuthorizedForResponsibleFields(
																						getUserEmail,
																					)
																						? "Select date"
																						: "Access restricted - Only authorized users can edit dates"
																				}
																			>
																				<Calendar size={16} />
																			</div>
																			{/* Clear Part Followup Date Button - Non-Edit Mode */}
																			{(() => {
																				const dateValue =
																					partFollowupDates[itemId] ||
																					row.part_followup_date;
																				return (
																					dateValue &&
																					dateValue !== "-" &&
																					dateValue !== "N/A"
																				);
																			})() && (
																				<div
																					onClick={() => {
																						if (
																							!updatingPartFollowupItems[
																								itemId
																							] &&
																							isUserAuthorizedForResponsibleFields(
																								getUserEmail,
																							)
																						) {
																							handleUpdateItem(
																								itemId,
																								"part_followup_date",
																								null,
																							);
																							console.log(
																								`Cleared Part followup date for item ${itemId}`,
																							);
																						}
																					}}
																					style={{
																						cursor:
																							updatingPartFollowupItems[
																								itemId
																							] ||
																							!isUserAuthorizedForResponsibleFields(
																								getUserEmail,
																							)
																								? "not-allowed"
																								: "pointer",
																						display: "flex",
																						alignItems: "center",
																						opacity:
																							updatingPartFollowupItems[
																								itemId
																							] ||
																							!isUserAuthorizedForResponsibleFields(
																								getUserEmail,
																							)
																								? 0.5
																								: 1,
																						color: "#dc2626",
																					}}
																					title={
																						isUserAuthorizedForResponsibleFields(
																							getUserEmail,
																						)
																							? "Clear date"
																							: "Access restricted - Only authorized users can edit dates"
																					}
																				>
																					<X size={12} />
																				</div>
																			)}
																		</div>
																	)}{" "}
																	{openPartDatePicker === itemId &&
																		!updatingPartFollowupItems[itemId] &&
																		isEditMode &&
																		isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		) && (
																			<div
																				style={{
																					position: "absolute",
																					zIndex: 10,
																					backgroundColor: "white",
																					padding: "8px",
																					top: "100%",
																					right: 0,
																				}}
																			>
																				<LocalizationProvider
																					dateAdapter={AdapterDateFns}
																					adapterLocale={enGB}
																				>
																					<DatePicker
																						open={true}
																						onClose={() =>
																							setOpenPartDatePicker(null)
																						}
																						slotProps={{
																							textField: {
																								size: "small",
																								sx: {
																									width: "140px",
																									"& .MuiInputBase-root": {
																										fontSize: "12px",
																										height: "32px",
																									},
																									"& .MuiInputBase-input": {
																										fontSize: "12px",
																										padding: "6px 8px",
																									},
																								},
																							},
																							popper: {
																								sx: {
																									"& .MuiPaper-root": {
																										fontSize: "14px",
																										"& .MuiPickersCalendarHeader-root":
																											{
																												fontSize: "14px",
																												color: "black",
																											},
																										"& .MuiPickersArrowSwitcher-root":
																											{
																												"& .MuiIconButton-root":
																													{
																														color: "#cc0000",
																														"&:hover": {
																															backgroundColor:
																																"#cc000020",
																														},
																													},
																											},
																										"& .MuiDayCalendar-weekDayLabel":
																											{
																												fontSize: "13px",
																												width: "36px",
																												height: "36px",
																											},
																										"& .MuiPickersDay-root": {
																											fontSize: "13px",
																											width: "36px",
																											height: "36px",
																											"&:hover": {
																												backgroundColor:
																													"#cc000020",
																											},
																											"&.Mui-selected": {
																												backgroundColor:
																													"#cc0000",
																												color: "white",
																												"&:hover": {
																													backgroundColor:
																														"#aa0000",
																												},
																											},
																										},
																										"& .MuiPickersDay-today": {
																											border:
																												"1px solid #cc0000",
																											"&:not(.Mui-selected)": {
																												color: "#cc0000",
																												fontWeight: "bold",
																											},
																										},
																									},
																								},
																							},
																						}}
																						value={(() => {
																							try {
																								const dateValue =
																									partFollowupDates[itemId] ||
																									row.part_followup_date;
																								if (
																									dateValue &&
																									dateValue !== "-" &&
																									dateValue !== "N/A"
																								) {
																									return new Date(dateValue);
																								}
																								return null;
																							} catch (error) {
																								console.error(
																									"Error parsing date for picker:",
																									error,
																								);
																								return null;
																							}
																						})()}
																						onChange={(newDate) => {
																							// Format the date as YYYY-MM-DD format without timezone issues
																							const formattedDate = newDate
																								? formatDateForBackend(newDate)
																								: null;

																							// Trigger update (this will also update local state in executeUpdate)
																							handleUpdateItem(
																								itemId,
																								"part_followup_date",
																								formattedDate,
																							);

																							// Close the date picker
																							setOpenPartDatePicker(null);

																							console.log(
																								`Selected Part followup date for item ${itemId}: ${formattedDate}`,
																							);
																						}}
																					/>
																				</LocalizationProvider>{" "}
																			</div>
																		)}
																</div>
															)}
														</div>
														{/* Comment and Status - compact horizontal layout */}
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "3px",
																marginTop: "1px",
																padding: "0 0 0 5px",
															}}
														>
															<CommentTooltip
																commentText={
																	commentsData[row.kast_key]
																		?.latest_part_comment
																}
																user={
																	commentsData[row.kast_key]
																		?.latest_part_comment_user
																}
																title="Parts Comments"
															>
																<button
																	onClick={() => handleOpenCommentModal(row)}
																	style={{
																		background: commentsData[row.kast_key]
																			?.latest_part_comment
																			? "#1e2832"
																			: "#ddd",
																		color: "white",
																		border: "none",
																		borderRadius: "2px",
																		padding: "2px 4px",
																		cursor: "pointer",
																		display: "flex",
																		alignItems: "center",
																		fontSize: "15px",
																		height: "18px",
																		minWidth: "25px",
																		justifyContent: "center",
																	}}
																	data-tour-target="parts-comment-button"
																>
																	<MessageSquare size={12} />
																</button>
															</CommentTooltip>
															<span
																style={{
																	fontSize: "10px",
																	fontFamily: "MAN EUROPE_Regular",
																	color: "#666",
																	padding: "0 0 0 30px",
																}}
															>
																Status:
															</span>
															{updatingPartStatus[itemId] ? (
																<div
																	style={{
																		display: "flex",
																		alignItems: "center",
																		gap: "4px",
																	}}
																>
																	<CircularProgress
																		size={12}
																		thickness={5}
																		sx={{ color: "#cc0000" }}
																	/>
																	{(() => {
																		// Check if this item is in the queue
																		const queuePosition =
																			statusUpdateQueue.current.findIndex(
																				(item) =>
																					item.id === itemId &&
																					item.field === "snr_ma_nahme_status",
																			);
																		if (queuePosition >= 0) {
																			return (
																				<span
																					style={{
																						fontSize: "8px",
																						color:
																							queuePosition === 0
																								? "#cc0000"
																								: "#666",
																						fontFamily: "MAN EUROPE_Bold",
																						backgroundColor:
																							queuePosition === 0
																								? "rgba(204, 0, 0, 0.1)"
																								: "rgba(102, 102, 102, 0.1)",
																						padding: "1px 3px",
																						borderRadius: "2px",
																						whiteSpace: "nowrap",
																					}}
																				>
																					{queuePosition === 0
																						? "Processing..."
																						: `Queued #${queuePosition + 1}`}
																				</span>
																			);
																		}
																		return null;
																	})()}
																</div>
															) : (
																<ToggleSwitch
																	value={row.snr_ma_nahme_status}
																	onChange={() =>
																		handleStatusToggle(
																			itemId,
																			"snr_ma_nahme_status",
																			row.snr_ma_nahme_status,
																		)
																	}
																	disabled={
																		updatingPartStatus[itemId] ||
																		!canWrite ||
																		!isUserAuthorizedForResponsibleFields(
																			getUserEmail,
																		)
																	}
																	size="small"
																	data-tour-target="status-toggle-parts"
																	title={
																		!canWrite
																			? "Access denied - You have read-only access"
																			: !isUserAuthorizedForResponsibleFields(
																						getUserEmail,
																					)
																				? "Access restricted - Only authorized users can modify status toggles"
																				: undefined
																	}
																/>
															)}
														</div>
													</div>
												</div>
											</td>
										</tr>
									);
								})}
						</tbody>
					</table>
					{/* Loading more indicator */}
					{isLoadingMore && (
						<div
							style={{
								textAlign: "center",
								padding: "20px",
								borderTop: "1px solid #eee",
								backgroundColor: "#f8f9fa",
							}}
						>
							<CircularProgress
								size={30}
								thickness={4}
								sx={{ color: "#cc0000" }}
							/>
							<Typography
								variant="body2"
								style={{ marginTop: "10px", color: "#666" }}
							>
								Loading more records...
							</Typography>
						</div>
					)}
					{/* Load More button (fallback for manual loading) */}
					{!isLoading &&
						!isLoadingMore &&
						hasMoreData &&
						displayData.length > 0 && (
							<div
								style={{
									textAlign: "center",
									padding: "20px",
									borderTop: "1px solid #eee",
									backgroundColor: "#f8f9fa",
								}}
							>
								<button
									onClick={loadMoreData}
									style={{
										padding: "8px 16px",
										backgroundColor: "#cc0000",
										color: "white",
										border: "none",
										borderRadius: "4px",
										cursor: "pointer",
										fontSize: "14px",
										fontFamily: "MAN EUROPE_Regular",
										display: "flex",
										alignItems: "center",
										gap: "8px",
										margin: "0 auto",
									}}
									onMouseOver={(e) =>
										(e.target.style.backgroundColor = "#b30000")
									}
									onMouseOut={(e) =>
										(e.target.style.backgroundColor = "#cc0000")
									}
								>
									Load More Records
								</button>
								<Typography
									variant="body2"
									style={{ marginTop: "8px", color: "#666", fontSize: "12px" }}
								>
									Or scroll down to auto-load
								</Typography>
							</div>
						)}
					{/* No more data indicator */}
					{!isLoading &&
						!isLoadingMore &&
						!hasMoreData &&
						displayData.length > 0 && (
							<div
								style={{
									textAlign: "center",
									padding: "15px",
									borderTop: "1px solid #eee",
									backgroundColor: "#f8f9fa",
									color: "#666",
									fontSize: "14px",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								All records loaded - {displayData.length} total records
							</div>
						)}{" "}
					{/* Status Update Queue Indicator */}
					{queueLength > 0 && (
						<div
							style={{
								textAlign: "center",
								padding: "10px",
								borderTop: "1px solid #eee",
								backgroundColor: "#fff8dc",
								color: "#666",
								fontSize: "12px",
								fontFamily: "MAN Europe_Bold",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "8px",
							}}
						>
							<CircularProgress
								size={14}
								thickness={5}
								sx={{ color: "#cc0000" }}
							/>
							<span>
								Processing status updates: {queueLength} item
								{queueLength !== 1 ? "s" : ""} in queue
							</span>
						</div>
					)}
				</div>{" "}
				<div
					style={{
						backgroundColor: "#f5f5f5",
						padding: "1px 1px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						borderTop: "1px solid #ddd",
						position: "sticky",
						bottom: 0,
						width: "100%",
						boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
						minHeight: "25px",
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							width: "100%",
							padding: "0 20px",
							position: "relative",
						}}
					>
						{/* Left spacer for balance */}
						<div style={{ flex: 1 }}></div>

						{/* Center content */}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								position: "absolute",
								left: "50%",
								transform: "translateX(-50%)",
							}}
						>
							<span
								style={{
									fontSize: "12px",
									color: "#666",
									fontFamily: "MAN Europe_Bold",
								}}
							>
								Showing {displayData.length} of {totalRecords} records
							</span>
							{!hasMoreData && displayData.length > 0 && (
								<span
									style={{
										fontSize: "11px",
										color: "#059669",
										marginTop: "2px",
										fontFamily: "MAN Europe_Bold",
									}}
								>
									✅ All records loaded
								</span>
							)}
						</div>

						{/* Right content */}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-end",
								flex: 1,
							}}
						>
							<span
								style={{
									fontSize: "11px",
									color: "#666",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								Last DB Update (IST)
							</span>
							<span
								style={{
									fontSize: "12px",
									color: "#333",
									fontFamily: "MAN EUROPE_Bold",
									marginTop: "2px",
								}}
							>
								{lastDbUpdate
									? (() => {
											try {
												// Parse the timestamp and add 5:30 hours for IST
												const date = new Date(lastDbUpdate);
												date.setHours(date.getHours() + 5);
												date.setMinutes(date.getMinutes() + 30);

												// Format as DD-MM-YYYY HH:MM:SS
												const year = date.getFullYear();
												const month = String(date.getMonth() + 1).padStart(
													2,
													"0",
												);
												const day = String(date.getDate()).padStart(2, "0");
												const hours = String(date.getHours()).padStart(2, "0");
												const minutes = String(date.getMinutes()).padStart(
													2,
													"0",
												);
												const seconds = String(date.getSeconds()).padStart(
													2,
													"0",
												);

												return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
											} catch (e) {
												return lastDbUpdate;
											}
										})()
									: "Loading..."}
							</span>
						</div>
					</div>
				</div>{" "}
				{/* Comment Modal */}{" "}
				<CommentModal
					open={commentModalOpen}
					onClose={() => setCommentModalOpen(false)}
					kastKey={currentKastKey}
					onAddComment={() => handleCommentAdded(currentKastKey)}
					newComment={newComment}
					setNewComment={setNewComment}
					editingCommentId={editingCommentId}
					setEditingCommentId={setEditingCommentId}
					editText={editText}
					setEditText={setEditText}
					getUserDisplayName={getUserDisplayName || (() => "Current User")} // Use prop or fallback
					getUserEmail={getUserEmail}
					canWrite={canWrite}
					preloadedCommentData={
						currentKastKey && commentsData[currentKastKey]
							? {
									latest_comment:
										commentsData[currentKastKey].latest_part_comment,
									latest_comment_date:
										commentsData[currentKastKey].latest_part_comment_date,
									latest_comment_user:
										commentsData[currentKastKey].latest_part_comment_user,
								}
							: null
					}
				/>
				{/* Vehicle Comment Modal */}{" "}
				<VehicleCommentModal
					open={vehicleCommentModalOpen}
					onClose={handleCloseVehicleCommentModal}
					kastKey={currentVehicleKastKey}
					onAddComment={() => handleVehicleCommentAdded(currentVehicleKastKey)}
					newComment={newVehicleComment}
					setNewComment={setNewVehicleComment}
					editingCommentId={editingVehicleCommentId}
					setEditingVehicleCommentId={setEditingVehicleCommentId}
					editText={editVehicleText}
					setEditText={setEditVehicleText}
					getUserDisplayName={getUserDisplayName || (() => "Current User")} // Use prop or fallback
					preloadedCommentData={
						currentVehicleKastKey && commentsData[currentVehicleKastKey]
							? {
									latest_comment:
										commentsData[currentVehicleKastKey].latest_vehicle_comment,
									latest_comment_date:
										commentsData[currentVehicleKastKey]
											.latest_vehicle_comment_date,
									latest_comment_user:
										commentsData[currentVehicleKastKey]
											.latest_vehicle_comment_user,
								}
							: null
					}
				/>
				{/* Unsaved Changes Warning Dialog */}
				<Dialog
					open={unsavedChangesDialog.open}
					onClose={() =>
						setUnsavedChangesDialog((prev) => ({ ...prev, open: false }))
					}
					maxWidth="sm"
					fullWidth
					PaperProps={{
						style: {
							borderRadius: "12px",
							overflow: "hidden",
						},
					}}
				>
					<DialogTitle
						sx={{
							backgroundColor: "#1e2832",
							color: "white",
							fontFamily: "MAN EUROPE_Bold",
							fontSize: "16px",
							textAlign: "center",
							py: 2,
						}}
					>
						⚠️ Unsaved Changes
					</DialogTitle>

					<DialogContent sx={{ py: 3, px: 3 }}>
						<DialogContentText
							sx={{
								color: "#333",
								fontSize: "14px",
								textAlign: "center",
								mb: 2,
								mt: 2,
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							You have unsaved changes in the responsible field. Your changes
							will be lost if you leave without pressing Enter to save.
						</DialogContentText>

						<Box
							sx={{
								backgroundColor: "#f8f9fa",
								padding: "12px",
								borderRadius: "6px",
								border: "1px solid #e9ecef",
								mb: 2,
							}}
						>
							<Typography
								variant="body2"
								sx={{
									fontSize: "12px",
									color: "#666",
									mb: 1,
									fontFamily: "MAN EUROPE_Bold",
								}}
							>
								Field: Resp (
								{unsavedChangesDialog.fieldType === "part_responsible"
									? "Parts"
									: "Vehicle"}
								)
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontSize: "12px",
									color: "#666",
									mb: 0.5,
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								<strong>Original:</strong> "{unsavedChangesDialog.originalValue}
								"
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontSize: "12px",
									color: "#666",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								<strong>Current:</strong> "{unsavedChangesDialog.currentValue}"
							</Typography>
						</Box>
					</DialogContent>

					<DialogActions
						sx={{
							p: 3,
							gap: 2,
							justifyContent: "center",
							borderTop: "1px solid #e9ecef",
						}}
					>
						<Button
							onClick={() => {
								// Restore original value and close dialog
								const { itemId, fieldType, originalValue } =
									unsavedChangesDialog;
								setResponsibleValues((prev) => ({
									...prev,
									[itemId]: {
										...prev[itemId],
										[fieldType]: originalValue,
									},
								}));
								setUnsavedChangesDialog((prev) => ({ ...prev, open: false }));
							}}
							variant="outlined"
							sx={{
								textTransform: "none",
								color: "black",
								borderColor: "#d1d5db",
								fontSize: "13px",
								minWidth: "120px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									borderColor: "#1e2832",
									backgroundColor: "rgba(30, 40, 50, 0.04)",
								},
							}}
						>
							Discard Changes
						</Button>
						<Button
							onClick={async () => {
								// Save the changes and close dialog
								const { itemId, fieldType } = unsavedChangesDialog;
								await handleSaveResponsibleField(itemId, fieldType);
								setUnsavedChangesDialog((prev) => ({ ...prev, open: false }));
							}}
							variant="contained"
							sx={{
								textTransform: "none",
								backgroundColor: "#cc0000",
								fontSize: "13px",
								minWidth: "120px",
								fontWeight: 600,
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": { backgroundColor: "#b30000" },
							}}
						>
							Save Changes
						</Button>{" "}
					</DialogActions>
				</Dialog>
				{/* Concurrent Update Warning Dialog */}
				<Dialog
					open={concurrentUpdateDialog.open}
					onClose={() =>
						setConcurrentUpdateDialog((prev) => ({ ...prev, open: false }))
					}
					maxWidth="sm"
					fullWidth
					PaperProps={{
						style: {
							borderRadius: "12px",
							overflow: "hidden",
						},
					}}
				>
					<DialogTitle
						sx={{
							backgroundColor: "#1e2832",
							color: "white",
							fontFamily: "MAN EUROPE_Bold",
							fontSize: "16px",
							textAlign: "center",
							py: 2,
						}}
					>
						⚠️ Update In Progress
					</DialogTitle>

					<DialogContent sx={{ py: 3, px: 3 }}>
						<DialogContentText
							sx={{
								color: "#333",
								fontSize: "14px",
								textAlign: "center",
								mb: 2,
								mt: 2,
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							{concurrentUpdateDialog.message}
						</DialogContentText>

						<Box
							sx={{
								backgroundColor: "#fff3cd",
								padding: "12px",
								borderRadius: "6px",
								border: "1px solid #ffeaa7",
								mb: 2,
								textAlign: "center",
							}}
						>
							<Typography
								variant="body2"
								sx={{
									fontSize: "12px",
									color: "#856404",
									fontFamily: "MAN EUROPE_Bold",
									mb: 1,
								}}
							>
								Current Update: {concurrentUpdateDialog.currentField}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontSize: "12px",
									color: "#856404",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								Attempted: {concurrentUpdateDialog.attemptedField}
							</Typography>
						</Box>

						<Box
							sx={{
								padding: "12px",
								borderRadius: "6px",
								border: "1px solid #bbdefb",
								textAlign: "center",
							}}
						>
							<Typography
								variant="body2"
								sx={{
									fontSize: "12px",
									color: "#1565c0",
									fontFamily: "MAN EUROPE_Regular",
								}}
							>
								💡 To prevent data conflicts and ensure reliability, only one
								update can be processed at a time. Please wait for the current
								operation to complete.
							</Typography>
						</Box>
					</DialogContent>
					<DialogActions
						sx={{
							p: 3,
							justifyContent: "center",
							borderTop: "1px solid #e9ecef",
						}}
					>
						<Button
							onClick={() =>
								setConcurrentUpdateDialog((prev) => ({ ...prev, open: false }))
							}
							variant="contained"
							sx={{
								textTransform: "none",
								backgroundColor: "#cc0000",
								fontSize: "13px",
								minWidth: "120px",
								fontWeight: 600,
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": { backgroundColor: "#b30000" },
							}}
						>
							Understood
						</Button>
					</DialogActions>
				</Dialog>{" "}
				{/* Access Denied Modal — shown when a write action is rejected (HTTP 403 / read-only user) */}
				<Dialog
					open={showAccessDeniedModal}
					onClose={() => setShowAccessDeniedModal(false)}
					maxWidth="sm"
					fullWidth
					PaperProps={{ style: { borderRadius: "5px", padding: "8px" } }}
				>
					<DialogContent
						style={{ textAlign: "center", padding: "32px 24px 24px 24px" }}
					>
						<div
							style={{
								width: "64px",
								height: "64px",
								backgroundColor: "#1e2832",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								margin: "0 auto 24px auto",
								border: "3px solid #1e2832",
							}}
						>
							<div style={{ fontSize: "32px", color: "#cc0000" }}>🔒</div>
						</div>

						<Typography
							variant="h6"
							style={{
								marginBottom: "16px",
								fontFamily: "MAN EUROPE_Bold",
								color: "#333",
								fontSize: "20px",
							}}
						>
							Access Denied
						</Typography>

						<Typography
							variant="body1"
							style={{
								color: "#666",
								lineHeight: "1.6",
								fontSize: "16px",
								marginBottom: "8px",
							}}
						>
							You have <strong>read-only</strong> access and cannot make
							changes.
						</Typography>

						<Typography
							variant="body2"
							style={{ color: "#888", fontSize: "14px" }}
						>
							Please contact your administrator if you need write access.
						</Typography>
					</DialogContent>

					<DialogActions
						style={{ padding: "0 24px 24px 24px", justifyContent: "center" }}
					>
						<Button
							onClick={() => setShowAccessDeniedModal(false)}
							variant="contained"
							style={{
								backgroundColor: "#cc0000",
								color: "white",
								borderRadius: "5px",
								padding: "10px 32px",
								fontFamily: "MAN EUROPE_Bold",
								textTransform: "none",
								fontSize: "14px",
							}}
							onMouseOver={(e) => (e.target.style.backgroundColor = "#b30000")}
							onMouseOut={(e) => (e.target.style.backgroundColor = "#cc0000")}
						>
							OK
						</Button>
					</DialogActions>
				</Dialog>
				{/* Flag Update Prevention Modal */}
				<Dialog
					open={flagUpdateModal.open}
					onClose={() =>
						setFlagUpdateModal((prev) => ({ ...prev, open: false }))
					}
					maxWidth="sm"
					fullWidth
					PaperProps={{
						style: {
							borderRadius: "12px",
							overflow: "hidden",
						},
					}}
				>
					<DialogTitle
						sx={{
							backgroundColor:
								flagUpdateModal.type === "simultaneous" ? "#1e2832" : "#1e2832",
							color: "white",
							fontFamily: "MAN EUROPE_Bold",
							fontSize: "16px",
							textAlign: "center",
							py: 2,
						}}
					>
						{flagUpdateModal.type === "simultaneous"
							? "⚠️ Multiple Operations Detected"
							: "💡 Efficiency Tip"}
					</DialogTitle>
					<DialogContent sx={{ py: 3, px: 3 }}>
						<DialogContentText
							sx={{
								color: "#333",
								fontSize: "14px",
								textAlign: "center",
								mb: 2,
								mt: 2,
								fontFamily: "MAN EUROPE_Regular",
							}}
						>
							{flagUpdateModal.message}
						</DialogContentText>

						{flagUpdateModal.type === "simultaneous" && (
							<Box
								sx={{
									backgroundColor: "#fff3cd",
									padding: "12px",
									borderRadius: "6px",
									border: "1px solid #ffeaa7",
									mb: 2,
									textAlign: "center",
								}}
							>
								<Typography
									variant="body2"
									sx={{
										fontSize: "12px",
										color: "#856404",
										fontFamily: "MAN EUROPE_Bold",
										mb: 1,
									}}
								>
									Currently updating: {Object.keys(updatingFlagging).length}{" "}
									item(s)
								</Typography>
								<Typography
									variant="body2"
									sx={{
										fontSize: "11px",
										color: "#666",
										fontFamily: "MAN EUROPE_Regular",
									}}
								>
									This dialog will auto-dismiss when all operations complete
								</Typography>
							</Box>
						)}

						{flagUpdateModal.type === "guide-to-edit" && (
							<Box
								sx={{
									backgroundColor: "#f0f8ff",
									padding: "12px",
									borderRadius: "6px",
									border: "1px solid #b3d9ff",
									mb: 2,
									textAlign: "center",
								}}
							>
								<Typography
									variant="body2"
									sx={{
										fontSize: "12px",
										color: "#0066cc",
										fontFamily: "MAN EUROPE_Bold",
										mb: 1,
									}}
								>
									Benefits of Edit Mode:
								</Typography>
								<Typography
									variant="body2"
									sx={{
										fontSize: "11px",
										color: "#333",
										fontFamily: "MAN EUROPE_Regular",
									}}
								>
									• Make multiple changes at once
									<br />• Reduce server load and improve performance
									<br />• Review changes before saving
									<br />• Undo changes easily
								</Typography>
							</Box>
						)}
					</DialogContent>

					<DialogActions
						sx={{
							p: 3,
							gap: 2,
							justifyContent: "center",
							borderTop: "1px solid #e9ecef",
						}}
					>
						<Button
							onClick={() =>
								setFlagUpdateModal((prev) => ({ ...prev, open: false }))
							}
							variant="outlined"
							sx={{
								textTransform: "none",
								color: "black",
								borderColor: "#d1d5db",
								fontSize: "13px",
								minWidth: "120px",
								fontFamily: "MAN EUROPE_Bold",
								"&:hover": {
									borderColor: "#1e2832",
									backgroundColor: "rgba(30, 40, 50, 0.04)",
								},
							}}
						>
							{flagUpdateModal.type === "simultaneous"
								? "Wait for Completion"
								: "Continue as Normal"}
						</Button>
						{flagUpdateModal.type === "guide-to-edit" && onEditModeToggle && (
							<Button
								onClick={() => {
									// Switch to edit mode
									onEditModeToggle();
									setFlagUpdateModal((prev) => ({ ...prev, open: false }));
								}}
								variant="contained"
								sx={{
									textTransform: "none",
									backgroundColor: "#cc0000",
									fontSize: "13px",
									minWidth: "120px",
									fontWeight: 600,
									fontFamily: "MAN EUROPE_Bold",
									"&:hover": { backgroundColor: "#b30000" },
								}}
							>
								Switch to Edit Mode
							</Button>
						)}
					</DialogActions>
				</Dialog>{" "}
				{/* Side Panel for More Info */}
				<VFFDataTableSidePanel
					isOpen={sidePanelOpen}
					onClose={handleCloseSidePanel}
					selectedRow={selectedRowForPanel}
					onUpdate={(kastKey, field, value) => {
						// Update the data in the table when side panel updates a field
						setTableData((prevData) =>
							prevData.map((row) =>
								row.kast_key === kastKey ? { ...row, [field]: value } : row,
							),
						);

						// Also update the selectedRowForPanel so side panel shows updated data immediately
						if (
							selectedRowForPanel &&
							selectedRowForPanel.kast_key === kastKey
						) {
							setSelectedRowForPanel((prev) => ({
								...prev,
								[field]: value,
							}));
						}
					}}
				/>
			</div>
		);
	},
);

export default VFFDataTable;
